'use client'

import { ClientSelector } from '@/components/client/ClientSelector'
import { getClient } from '@/lib/api/clients/client-api'
import { FormField, FormSection } from '@/components/form-section'
import { HealthPlanSelector } from '@/components/health-plan/HealthPlanSelector'
import { PartnerSelector } from '@/components/partner/PartnerSelector'
import PipelineSelector from '@/components/pipeline-selector/PipelineSelector'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import { HospitalSearchSelector } from '@/components/HospitalSearchSelector'
import { CatalogQuickCreate } from '@/components/catalog/catalog-quick-create'
import {
    CATALOG_QUICK_CREATE_SETTING,
    DEFAULT_CATALOG_QUICK_CREATE_ENABLED,
    normalizeCatalogQuickCreate,
} from '@/lib/catalog/quick-create-setting'
import {
    Catalog,
    CatalogCategory,
    CatalogPartner,
    CatalogProfessional,
    CatalogVariation,
    getCatalog,
    getCatalogMedicalSupplies,
    getCatalogExams,
    getCatalogVariations,
    getCatalogsByCompany,
    catalogBasePrice,
    catalogCompanyPrice,
} from '@/lib/api/catalog/catalog-api'
import {
    NO_DISCOUNT_ALLOWED,
    cascadeMaxDiscount,
    combineMaxDiscounts,
    variationMaxDiscount,
} from '@/lib/api/catalog/catalog-max-discount'
import {
    createConsultation,
    CreateConsultationData,
    getConsultation,
    updateConsultation,
    rescheduleConsultation,
} from '@/lib/api/consultations/consultation-api'
import {
    createExam,
    CreateExamData,
    getExam,
    importExamListFromDocument,
    updateExam,
    rescheduleExam,
} from '@/lib/api/exams/exam-api'
import {
    createOrder,
    CreateOrderData,
    getOrder,
    updateOrder,
    rescheduleOrder,
    enableOrderFlexPayment,
} from '@/lib/api/orders/order-api'
import { getRoomsByCompany, Room } from '@/lib/api/rooms/rooms-api'
import {
    createTeleconsultation,
    getTeleconsultation,
    updateTeleconsultation,
} from '@/lib/api/teleconsultation/teleconsultation-api'
import { getSettingByName } from '@/lib/api/utils/settings-api'
import { mask, unMask } from '@/lib/form-state/util'
import { useClientLabel } from '@/lib/hooks/use-client-label'
import { useReturnConsultationBillingPolicy } from '@/lib/hooks/use-return-consultation-billing-policy'
import {
    useCatalogCategoryLabels,
    useServiceLabel,
} from '@/lib/hooks/use-service-label'
import { useCatalogAccess } from '@/lib/hooks/use-catalog-access'
import { useAuth } from '@/store/hooks'
import { formatInTimeZone, toZonedTime } from 'date-fns-tz'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    MdAdd,
    MdAutoAwesome,
    MdClose,
    MdInventory2,
    MdLocationOn,
    MdInfoOutline,
    MdNotes,
    MdPayments,
    MdPerson,
    MdTune,
} from 'react-icons/md'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { getPartner } from '@/lib/api/partners/partners-api'
import { getProfessional } from '@/lib/api/professionals/professionals-api'
import ConsultationFields from './components/consultation-fields'
import MedicalSuppliesFields from './components/medical-supplies-fields'
import {
    OrderProductItem,
    OrderProductsFields,
} from './components/order-products-fields'
import PricingSection, {
    getPartnerTotalForItems,
    PriceComposition,
} from './components/pricing-section'
import {
    AI_SCHEDULING_SERVICE_TYPES,
    type SchedulingAiLine,
    useSchedulingAiBridge,
} from './scheduling-ai-bridge'
import { AdjustmentPreviewBilling } from '@/lib/billing/appointment-adjustment-preview'
import {
    hasNoEffectiveBilling,
    isClosedBillingStatus,
} from '@/lib/billing/appointment-billing-state'
import {
    getAttachableBillings,
    PaymentQuoteOption,
} from '@/lib/api/financial/financial-billing-api'
import {
    previewPayoutRedirect,
    type PayoutRecipientChange,
    type PayoutRedirectPreview,
} from '@/lib/api/financial/payout-preview-api'
import ServiceAddressSection, {
    ServiceAddress,
} from '@/components/service-address/service-address-section'
import PartnerComparisonModal from './components/partner-comparison-modal'
import OrderExamsFields from './components/order-exams-fields'
import ConsultationVariationsPicker from './components/consultation-variations-picker'
import PayoutRedirectNotice from './components/payout-redirect-notice'
import OrderFields from './components/order-fields'
import ReplaceOrAppendDialog from './components/replace-or-append-dialog'
import RescheduleReasonField from './components/reschedule-reason-field'
import ScheduleWhenFields from './components/schedule-when-fields'
import SchedulingActions from './components/scheduling-actions'
import SelectedItemsTable from './components/selected-items-table'
import UnmatchedImportsPanel from './components/unmatched-imports-panel'
import VariationRowControls from './components/variation-row-controls'
import SchedulingHeader from './components/scheduling-header'
import SchedulingSectionNav from './components/scheduling-section-nav'
import SchedulingSummaryBar from './components/scheduling-summary-bar'
import CouponField, { type AppliedCoupon } from './components/coupon-field'
import { quoteCoupon } from '@/lib/api/commerce/coupons'
import { useResourceVisibility } from '@/lib/hooks/use-entity-access'
import TeleconsultationFields from './components/teleconsultation-fields'
import {
    ConsultationVariationItem,
    ExamSelectedItem,
    MedicalSupplyItem,
    OrderExamItem,
    OrderSelectedItem,
    SERVICE_TYPE_LABELS,
    ServiceType,
} from './types'
import {
    convertLocalToUTC,
    currencyFormat,
    formatCentsLabel,
    parseCurrencyToCents,
} from './scheduling-format'

/**
 * O que falta no vínculo do parceiro com o item: custo, venda, ou os dois.
 *
 * `price` é o custo (o que vira repasse na baixa) e `sellingPrice` é a venda (o que o
 * paciente paga). Zero conta como faltando, não como "de graça": venda zerada com custo
 * lançado é prejuízo, e custo zerado é repasse que nunca nasce.
 *
 * Vínculo inexistente conta como faltando os dois: parceiro que executa sem preço
 * cadastrado recebe repasse zero exatamente como quem tem o campo em branco.
 */
function missingLinkPrices(link?: {
    price?: number | null
    sellingPrice?: number | null
}): string | null {
    if (!link) return 'custo e valor de venda'
    const semCusto = !link.price
    const semVenda = !link.sellingPrice
    if (semCusto && semVenda) return 'custo e valor de venda'
    if (semCusto) return 'custo'
    if (semVenda) return 'valor de venda'
    return null
}

/**
 * O que falta no vínculo do PROFISSIONAL — régua mais frouxa que a do parceiro, e cada
 * folga vem de um número medido em produção:
 *
 * - **venda em branco não conta**: é assim em 2.233 dos 2.238 vínculos de exame, nas 835
 *   variações de exame e em 89 dos 142 de consulta. A venda do profissional não é
 *   preenchida por prática, e o backend resolve caindo no preço do item
 *   (`sellingPrice ?? price`). Exigi-la bloquearia quase toda a agenda. Venda ZERO, essa
 *   sim, é cobrar zero do paciente — e bloqueia;
 * - **custo zero não conta**: são 286 vínculos, e significam profissional da casa que não
 *   recebe repasse por aquele item;
 * - **sem vínculo nenhum não conta**: mesma coisa — item que o profissional executa sem
 *   repasse próprio.
 *
 * Sobra o que é erro de verdade: o custo em branco (12 vínculos hoje), que gera repasse
 * zero para quem deveria receber.
 */
function missingProfessionalPrices(link?: {
    price?: number | null
    sellingPrice?: number | null
}): string | null {
    if (!link) return null
    if (link.price == null) return 'custo'
    if (link.sellingPrice === 0) return 'valor de venda'
    return null
}

export default function SchedulingFormPage() {
    const { user, isPartner, isEmployee } = useAuth()
    const hidePartnerSelector = isPartner || (isEmployee && !!user?.partnerId)
    // Empresas de intermediação funilam o paciente para UM único parceiro: ao
    // escolher um parceiro, a busca do catálogo passa a mostrar só itens dele.
    const isIntermediation =
        user?.company?.secondarySegment === 'INTERMEDIATION'
    // Campos que só existem na saúde (convênio, hospital, anestesista). O módulo
    // é agnóstico de segmento — um salão agenda o mesmo `Order` que a clínica —,
    // mas esses três não têm equivalente fora dela e só poluiriam o formulário.
    // Nada quebra se ficarem vazios: são opcionais no backend.
    const isHealth = user?.company?.segment === 'HEALTH'
    // Paciente/Cliente/Aluno/Tutor conforme o segmento.
    const { clientLabel, ClientLabel } = useClientLabel()
    // "Cobrar consulta de retorno" (Configurações Financeiras > Cobrança).
    // Desligada por padrão: o retorno é a continuação da consulta já paga.
    const { chargeReturn: chargeReturnConsultation } =
        useReturnConsultationBillingPolicy()
    // `SERVICE_TYPE_LABELS` é a versão da saúde; fora dela, Procedimento vira
    // Pedido e Consulta vira Avaliação.
    const serviceLabels = useServiceLabel()
    const categoryLabels = useCatalogCategoryLabels()
    const serviceTypeLabels: Record<ServiceType, string> = useMemo(
        () => ({
            CONSULTATION: serviceLabels.Consultation,
            EXAM: serviceLabels.Exam,
            PROCEDURE: serviceLabels.Order,
            PACKAGE: SERVICE_TYPE_LABELS.PACKAGE,
        }),
        [serviceLabels]
    )
    // Tipos que o usuário vê no menu (mesma regra do sidebar).
    const { isCategoryAllowed, canSeeProfessionals } = useCatalogAccess()
    const availableServiceTypes = (
        Object.keys(SERVICE_TYPE_LABELS) as ServiceType[]
    ).filter(type => isCategoryAllowed(type))
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const [searchParams] = useSearchParams()
    const isEditing = !!id

    // Aceita `type` (novo, padronizado em ingles) e `tipo` (legado) pra
    // sobreviver a links antigos salvos em bookmarks/handoffs. Valores
    // desconhecidos (ex.: PACKAGE, que é agendado via EXAM/PROCEDURE conforme a
    // composição) caem no default para não quebrar a tela.
    const rawType = searchParams.get('type') || searchParams.get('tipo')
    const validServiceTypes: ServiceType[] = [
        'CONSULTATION',
        'EXAM',
        'PROCEDURE',
        'PACKAGE',
    ]
    const initialType: ServiceType = validServiceTypes.includes(
        rawType as ServiceType
    )
        ? (rawType as ServiceType)
        : 'CONSULTATION'
    const urlPresetBudget = searchParams.get('status') === 'BUDGET'
    const [isBudgetMode, setIsBudgetMode] = useState(urlPresetBudget)
    // Status do registro carregado (ao editar). Um orçamento aprovado sai do status
    // BUDGET e vira agendamento normal — a partir daí `budgetOnly` não deve mais forçar
    // o modo orçamento.
    const [loadedStatus, setLoadedStatus] = useState<string | null>(null)
    /**
     * Atendimento nascido de VENDA DE PACOTE.
     *
     * Os componentes dele já têm executante no cadastro do pacote (`CatalogPackageItem`),
     * e é de lá que sai o repasse. Exigir destinatário na linha aqui faria o mesmo
     * trabalho ser pago duas vezes.
     */
    const [fromPackage, setFromPackage] = useState(false)
    /**
     * Edição de uma VENDA DE PACOTE — o pacote não-agendável, que se vende em vez de
     * agendar.
     *
     * Muda a cara da tela, não o que ela salva: a venda continua sendo o `Order`, e é
     * ele que a edição atualiza (com o ajuste de cobrança que já existe — complementar
     * para mais, estorno para menos). O modelo do pacote fica no catálogo e não é tocado
     * daqui; editá-lo mudaria o pacote de todos os próximos pacientes.
     *
     * ⚠️ `schedulable` mora no CATÁLOGO: marcar o pacote como agendável depois faz as
     * vendas antigas voltarem para a tela genérica. Congelar a marca na venda resolveria,
     * e é uma coluna — não foi feito.
     */
    const [packageSaleMode, setPackageSaleMode] = useState(false)
    // Produtos entregues no pacote. Só a venda de pacote os tem; enviar a lista
    // reescreve a do atendimento, e por isso ela só vai ao backend em modo pacote.
    const [orderProducts, setOrderProducts] = useState<
        OrderProductItem[]
    >([])
    const [showPartnerComparison, setShowPartnerComparison] = useState(false)

    // === Estado compartilhado ===
    const [serviceType, setServiceType] = useState<ServiceType>(initialType)
    const [loading, setLoading] = useState(false)
    const [loadingData, setLoadingData] = useState(isEditing)
    const [clientId, setClientId] = useState<string>('')
    const [professionalId, setProfessionalId] = useState<string>('')
    const [partnerId, setPartnerId] = useState<number | null>(
        hidePartnerSelector ? user?.partnerId || null : null
    )
    /**
     * Local de atendimento confirmado pelo operador. Fica `null` até a confirmação
     * — e o submit permanece bloqueado enquanto isso, porque o endereço cadastrado
     * do profissional costuma ser o residencial, não o do consultório onde atende.
     */
    const [serviceAddress, setServiceAddress] = useState<ServiceAddress | null>(
        null
    )

    /**
     * Confirmação do local só é exigida ao AGENDAR de fato. Em edição o local já
     * está gravado, e no modo orçamento nada foi agendado ainda — o endereço é
     * confirmado depois, na aprovação do orçamento.
     */
    const addressConfirmed = isEditing || isBudgetMode || !!serviceAddress
    /**
     * Trocar quem atende (parceiro ou profissional) devolve o local à confirmação.
     *
     * O endereço escolhido era o do executante anterior, e a seção só pré-seleciona com o
     * valor vazio: sem isto, trocar de parceiro mantinha o endereço do primeiro, e o
     * atendimento e a guia saíam com o local errado. Em edição o local já está gravado.
     */
    const executantKeyRef = useRef<string | null>(null)
    useEffect(() => {
        const key = `${partnerId ?? ''}|${professionalId}`
        const previous = executantKeyRef.current
        executantKeyRef.current = key
        if (previous === null || previous === key || isEditing) return
        setServiceAddress(null)
    }, [partnerId, professionalId, isEditing])
    const [healthPlanId, setHealthPlanId] = useState<number | null>(null)
    const [hospitalId, setHospitalId] = useState<number | null>(null)
    const [hospitalIdTouched, setHospitalIdTouched] = useState(false)
    /**
     * Data e hora são campos SEPARADOS no formulário — alguns parceiros atendem por ordem
     * de chegada, e exigir hora ali era mentira (o operador digitava um horário qualquer).
     *
     * No banco continua existindo um único `scheduledAt`: é ele que sustenta ordenação,
     * filtros de período e relatórios. `scheduledAt` abaixo é DERIVADO dos dois campos, o
     * que mantém intacto todo o resto do submit.
     */
    const [scheduledDate, setScheduledDate] = useState('')
    const [scheduledTime, setScheduledTime] = useState('')
    const [arrivalOrder, setArrivalOrder] = useState(false)

    /** Data original — usada para saber se a edição é uma REMARCAÇÃO (já tinha data). */
    const [originalScheduledAt, setOriginalScheduledAt] = useState('')
    const [rescheduleReason, setRescheduleReason] = useState('')

    /**
     * Quem executava o atendimento quando a tela abriu.
     *
     * Trocar o executante de um atendimento já cobrado move a conta a pagar para quem
     * passou a executar — e é recusado quando o repasse já foi pago. Sem guardar o
     * estado inicial não há como perguntar ao backend o que a troca vai causar, e o
     * operador só descobriria ao salvar (ou não descobriria, no caso em que dá certo).
     */
    const [originalExecutants, setOriginalExecutants] = useState<{
        professionalId: number | null
        partnerId: number | null
    } | null>(null)
    const [payoutRedirect, setPayoutRedirect] =
        useState<PayoutRedirectPreview | null>(null)

    /**
     * CPF do parceiro escolhido. Médico que atende pela própria clínica tem ficha de
     * parceiro e de profissional, sem ligação entre elas; com as duas no atendimento o
     * repasse do profissional nasce sem valor e o relatório perde o custo. O backend
     * recusa a combinação; aqui o profissional com o mesmo CPF some da busca.
     */
    const [partnerDocument, setPartnerDocument] = useState<string | null>(null)

    /**
     * `yyyy-MM-ddTHH:mm` — o mesmo formato que o `datetime-local` produzia, então os
     * consumidores no submit não mudam. Sem hora (ordem de chegada), 00:00.
     */
    const scheduledAt = scheduledDate
        ? `${scheduledDate}T${arrivalOrder ? '00:00' : scheduledTime || '00:00'}`
        : ''

    /** Preenche os dois campos a partir do `yyyy-MM-ddTHH:mm` vindo do backend. */
    const setScheduledAt = (value: string) => {
        if (!value) {
            setScheduledDate('')
            setScheduledTime('')
            return
        }
        const [d, t] = value.split('T')
        setScheduledDate(d || '')
        setScheduledTime(t || '')
    }
    const [notes, setNotes] = useState('')
    const [discount, setDiscount] = useState('')
    const [paymentMethod, setPaymentMethod] = useState('')
    /**
     * Parcelas combinadas com o pagador, quando o operador fecha no cartão.
     *
     * Vai junto na criação: a cobrança nasce com a taxa da faixa certa e o checkout
     * não pergunta ao paciente — o combinado na mesa é o que ele encontra no link.
     */
    const [installmentCount, setInstallmentCount] = useState<number | null>(null)
    /**
     * COBRAR DEPOIS: agenda sem emitir cobrança nenhuma.
     *
     * A maioria dos pacientes marca hoje e paga no dia, na recepção. Emitir cobrança em
     * todo agendamento enchia o financeiro de pendências que ninguém esperava receber
     * ainda, e mandava lembrete de vencimento a quem ia pagar presencialmente.
     *
     * O valor continua gravado no atendimento — o que espera é só a emissão, feita
     * depois pelo "Gerar cobrança" da tela de detalhe (que já existe e usa esse valor).
     *
     * ⚠️ Não é o "decidir na tela de pagamento": aquele EMITE a cobrança e só deixa a
     * forma em aberto. Aqui não nasce cobrança alguma.
     */
    const [deferBilling, setDeferBilling] = useState(false)
    // Cobrança já existente do atendimento (só em edição). A PricingSection usa para avisar,
    // antes de salvar, que mexer no valor de uma cobrança paga cria cobrança complementar ou
    // estorno — em vez de simplesmente corrigir o preço.
    const [existingBilling, setExistingBilling] =
        useState<AdjustmentPreviewBilling | null>(null)
    // === Vários atendimentos, uma cobrança ===
    // O paciente que agenda exame no laboratório A, exame no B e uma consulta em
    // outro lugar recebia TRÊS cobranças e três links. Com "Adicionar outro", o
    // atendimento seguinte é somado na cobrança que nasceu do primeiro.
    //
    // A fonte do agrupamento é o SERVIDOR, não a sessão do navegador. Ao escolher
    // o paciente a tela pergunta se ele já tem cobrança em aberto de hoje e, tendo,
    // continua nela. Assim o operador que saiu para almoçar, o colega em outra
    // máquina e a página recarregada retomam o mesmo grupo — nada disso funcionaria
    // com o estado preso na URL ou na memória desta aba.
    const [billingTargetId, setBillingTargetId] = useState<number | null>(null)
    /** Quantos atendimentos a cobrança já cobre (vem do backend). */
    const [groupedCount, setGroupedCount] = useState(0)
    /** Total já acumulado na cobrança, em centavos (vem do backend). */
    const [groupedAmount, setGroupedAmount] = useState(0)
    /**
     * O operador dispensou o agrupamento para este paciente.
     *
     * Existe porque a detecção é automática: o paciente que voltou à tarde para
     * algo que ele quer pagar à parte não pode ficar preso à cobrança da manhã.
     * Vale até trocar de paciente.
     */
    const [agrupamentoDispensado, setAgrupamentoDispensado] = useState(false)
    // Em edição, `billingTargetId` é a cobrança do próprio atendimento e só existe
    // quando ela ainda aceita soma (ver `aplicarCobrancaDoRegistro`) — por isso não
    // há mais `!isEditing` aqui.
    const agrupando =
        !isBudgetMode && !agrupamentoDispensado && billingTargetId != null
    /**
     * A cobrança que este atendimento deve engordar. `undefined` quando não há
     * agrupamento — aí o backend cria a cobrança dele, como sempre. Passa por
     * `agrupando` para que "Gerar cobrança separada" realmente separe.
     */
    const destinoDaSoma = agrupando ? (billingTargetId ?? undefined) : undefined
    const [pipelineId, setPipelineId] = useState<number | undefined>()
    // Duração fica nula quando ninguém informa — não há campo na tela e não existe
    // tempo padrão. Só é enviada quando o registro carregado já tinha uma duração.
    const [duration, setDuration] = useState<number | null>(null)

    // === Catálogo ===
    const [catalogs, setCatalogs] = useState<Catalog[]>([])
    const [catalogsById, setCatalogsById] = useState<Record<number, Catalog>>(
        {}
    )
    const [loadingCatalogs, setLoadingCatalogs] = useState(false)
    const [selectedCatalog, setSelectedCatalog] = useState<Catalog | null>(null)
    const [catalogSearch, setCatalogSearch] = useState('')
    const [showCatalogDropdown, setShowCatalogDropdown] = useState(false)
    const catalogDropdownRef = useRef<HTMLDivElement>(null)
    const catalogSearchDebounceRef = useRef<ReturnType<
        typeof setTimeout
    > | null>(null)
    const [catalogPricingMode, setCatalogPricingMode] =
        useState<string>('priority')
    const [searchAllCatalog, setSearchAllCatalog] = useState(false)
    // Cadastro rápido de item do catálogo a partir da busca sem resultado.
    // Habilitado por empresa em Configurações Médicas; padrão desligado.
    /**
     * Item do catálogo escolhido na edição enquanto a pilha tem um só: espera o
     * operador dizer se é para SUBSTITUIR ou ACRESCENTAR (ver `handleCatalogSelect`).
     */
    const [pendingCatalogChoice, setPendingCatalogChoice] =
        useState<Catalog | null>(null)
    const [showQuickCreate, setShowQuickCreate] = useState(false)
    const [quickCreateAllowed, setQuickCreateAllowed] = useState(
        DEFAULT_CATALOG_QUICK_CREATE_ENABLED
    )

    const mergeCatalogs = (list: Catalog[]) => {
        if (!list.length) return
        setCatalogsById(prev => {
            const next = { ...prev }
            list.forEach(c => {
                next[c.id] = c
            })
            return next
        })
    }

    // === Exame: múltiplos itens ===
    const [examItems, setExamItems] = useState<ExamSelectedItem[]>([])
    // === Procedimento: múltiplos itens (pilha) ===
    const [orderItems, setOrderItems] = useState<
        OrderSelectedItem[]
    >([])
    const [importingExamDoc, setImportingExamDoc] = useState(false)
    const examImportInputRef = useRef<HTMLInputElement>(null)
    const [unmatchedImports, setUnmatchedImports] = useState<string[]>([])

    // === Variações do catálogo ===
    // Variações ativas carregadas sob demanda, indexadas por catalogId.
    const [variationsByCatalog, setVariationsByCatalog] = useState<
        Record<number, CatalogVariation[]>
    >({})
    // Variações escolhidas para a CONSULTA (item único, multi-seleção). O valor
    // somado substitui o preço base da consulta.
    const [consultationVariations, setConsultationVariations] = useState<
        ConsultationVariationItem[]
    >([])

    // === Consulta ===
    const [consultTitle, setConsultTitle] = useState('')
    const [isReturn, setIsReturn] = useState(false)
    const [triageId, setTriageId] = useState('')
    const [diagnosis, setDiagnosis] = useState('')
    const [treatmentPlan, setTreatmentPlan] = useState('')

    // === Procedimento ===
    const [roomId, setRoomId] = useState<number | null>(null)
    const [rooms, setRooms] = useState<Room[]>([])
    const [preInstructions, setPreInstructions] = useState('')
    const [postInstructions, setPostInstructions] = useState('')
    const [validUntil, setValidUntil] = useState('')

    // === Procedimento: exames atrelados ===
    const [orderExams, setOrderExams] = useState<OrderExamItem[]>(
        []
    )
    const [orderExamTemplates, setOrderExamTemplates] = useState<
        OrderExamItem[]
    >([])

    // === Procedimento: consultas atreladas ===
    const [orderConsultations, setOrderConsultations] = useState<
        OrderExamItem[]
    >([])

    // === Exame/Procedimento: materiais e anestesista ===
    const [medicalSupplies, setMedicalSupplies] = useState<MedicalSupplyItem[]>(
        []
    )
    const [anesthetistFee, setAnesthetistFee] = useState('')
    // Quem RECEBE a taxa do anestesista. Obrigatório quando há taxa: é ela que vira conta a
    // pagar na baixa, e repasse sem credor não tem para quem transferir.
    // Ver docs/billing-system.md §7.
    const [anesthetistRecipientKind, setAnesthetistRecipientKind] = useState<
        'HOSPITAL' | 'PROFESSIONAL' | 'PARTNER' | null
    >(null)
    const [anesthetistRecipientId, setAnesthetistRecipientId] = useState<
        number | null
    >(null)
    // PROCEDIMENTO: override editável do valor de repasse ao profissional (string de moeda).
    // Vazio = usa o valor do catálogo. Salvo em Order.professionalAmount.
    const [professionalAmountOverride, setProfessionalAmountOverride] =
        useState('')
    const [hospitalFee, setHospitalFee] = useState('')
    const [hospitalFeeNotes, setHospitalFeeNotes] = useState('')

    // === Teleconsulta ===
    const [teleTitle, setTeleTitle] = useState('')
    const [teleIsReturn, setTeleIsReturn] = useState(false)
    const [maxTeleDuration, setMaxTeleDuration] = useState('3600')
    const [recordingEnabled, setRecordingEnabled] = useState(false)
    const [enableChat, setEnableChat] = useState(true)
    const [enableScreenShare, setEnableScreenShare] = useState(true)
    const [specialtyId, setSpecialtyId] = useState<number | null>(null)
    const [telePrice, setTelePrice] = useState('')
    const [teleSellingPrice, setTeleSellingPrice] = useState('')
    const [teleMarkup, setTeleMarkup] = useState('')
    const [priceComposition, setPriceComposition] =
        useState<PriceComposition>('item')
    const [overridePrice, setOverridePrice] = useState<number | null>(null)
    // Cupom: abate depois do desconto manual (crédito do cliente na empresa). Só para
    // quem lê cupons — a mesma regra do menu.
    const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(null)
    const [applyingCoupon, setApplyingCoupon] = useState(false)
    const isResourceVisible = useResourceVisibility()
    const canUseCoupons = isResourceVisible('commerce', 'coupons')
    // Procedimento: custo (calculado) e markup% reportados pela PricingSection para
    // gravar em Order.costPrice/markupPercentage. finalPrice continua sendo a venda.
    const [orderCostPrice, setOrderCostPrice] = useState<number | null>(
        null
    )
    const [orderMarkup, setOrderMarkup] = useState<number | null>(null)
    const handleCostMarkupChange = useCallback(
        (cost: number, markup: number | null) => {
            setOrderCostPrice(cost > 0 ? cost : null)
            setOrderMarkup(markup)
        },
        []
    )
    /**
     * Custo e markup COMO ESTÃO GRAVADOS no registro em edição.
     *
     * A tela mostra estes valores enquanto o operador não mexe em nada, em vez do
     * par recalculado ao vivo: reabrir um procedimento tem que contar a mesma
     * história que foi acertada, mesmo que um preço de vínculo tenha mudado no
     * cadastro depois. Assim que ele toca em qualquer campo, o cálculo assume.
     */
    const [savedCostPrice, setSavedCostPrice] = useState<number | null>(null)
    const [savedMarkup, setSavedMarkup] = useState<number | null>(null)
    /**
     * Procedimento aprovado em pagamento FLEX: o paciente quita em parcelas avulsas
     * pelo portal, escolhendo forma e valor a cada pagamento. Ver `Order.flexPayment`.
     */
    const [flexPayment, setFlexPayment] = useState(false)

    /**
     * O operador escolheu "Pagamento flexível" entre as formas, nesta edição.
     *
     * É o mesmo gesto do "Ativar pagamento Flex" do menu de detalhes, oferecido no
     * lugar onde a conversa acontece: quem acabou de ouvir "não consigo fechar tudo
     * agora" está nesta tela. Vira a chamada de `enableOrderFlexPayment` DEPOIS
     * de salvar a edição — ligar antes faria a correção de valor desta mesma
     * submissão entrar pela regra do flex (sem complemento nem estorno), quando a
     * cobrança que existe ainda é a normal.
     */
    const [flexRequested, setFlexRequested] = useState(false)

    /**
     * O valor no campo do profissional veio do CATÁLOGO, não do operador.
     *
     * Materializado ao abrir a edição, ele é o CUSTO do vínculo — o que o profissional
     * recebe. O preço ao paciente continua saindo do honorário de VENDA, que diverge
     * do custo em parte dos vínculos: deixar o número materializado entrar no preço
     * derrubaria o valor do atendimento só por abrir a tela.
     */
    const [professionalAmountFromCatalog, setProfessionalAmountFromCatalog] =
        useState(false)
    /**
     * O operador mexeu em algo depois que a tela carregou.
     *
     * Enquanto for `false` numa EDIÇÃO, nada recalculado pode alterar o valor final
     * que veio do banco — é a segunda trava contra o valor de venda subir sozinho
     * quando um dado do catálogo chega atrasado (ver `catalogsHydrating`). Numa
     * criação nasce `true`: não há valor acertado a proteger.
     *
     * Marcado na captura de ponteiro/tecla/change do formulário inteiro porque o
     * preço se compõe de muitos pontos (itens, taxas, profissional, parceiro,
     * desconto); listar handler por handler deixaria buracos silenciosos.
     */
    const [pricingTouched, setPricingTouched] = useState(!isEditing)
    // `setState` com o mesmo valor não re-renderiza, então repetir a cada evento é barato.
    const markPricingTouched = useCallback(() => setPricingTouched(true), [])
    const [floatingBarVisible, setFloatingBarVisible] = useState(false)

    // Resumo fixo do topo. Sem ele o operador rola meia tela só para conferir
    // para quem é e quanto vai sair — a conferência mais repetida do dia.
    const [clientName, setClientName] = useState('')
    const [summaryPrice, setSummaryPrice] = useState(0)
    /**
     * Cotação da forma de pagamento escolhida (taxa já embutida), vinda do backend.
     *
     * O resumo mostra o valor do serviço enquanto ninguém escolheu uma forma; a partir
     * daí passa a mostrar o que o pagador desembolsa — que é o número que o operador
     * fala ao paciente. Só a exibição muda: o valor gravado no atendimento continua
     * sendo o do serviço.
     */
    const [paymentQuote, setPaymentQuote] =
        useState<PaymentQuoteOption | null>(null)
    /**
     * A cotação chega por requisição, então ela atrasa em relação ao valor em tela.
     * Comparar com `baseAmount` evita o instante em que o resumo mostraria o total da
     * forma calculado sobre o preço ANTERIOR. Sem taxa (total igual ao serviço), não
     * há o que anunciar.
     */
    // O resumo mostra o que será cobrado: o preço manual menos o cupom.
    const chargedSummaryPrice = Math.max(
        0,
        summaryPrice - Math.min(appliedCoupon?.amount ?? 0, summaryPrice)
    )
    /**
     * "Aplicar" do cupom: prévia no backend com os itens em tela e o preço manual. O
     * valor definitivo é recalculado ao salvar (validade e usos podem mudar até lá).
     */
    const handleApplyCoupon = async (code: string) => {
        if (!clientId) {
            toast.error(`Selecione o ${clientLabel} antes de aplicar o cupom.`)
            return
        }
        const items =
            serviceType === 'EXAM'
                ? examItems.map(i => ({ catalogId: i.catalogId, quantity: i.quantity || 1, price: i.unitPrice || 0 }))
                : serviceType === 'PROCEDURE'
                  ? orderItems.map(i => ({ catalogId: i.catalogId, quantity: i.quantity || 1, price: i.unitPrice || 0 }))
                  : selectedCatalog
                    ? [{ catalogId: selectedCatalog.id, quantity: 1, price: summaryPrice }]
                    : []
        if (!items.length || summaryPrice <= 0) {
            toast.error('Escolha o item e o valor antes de aplicar o cupom.')
            return
        }
        const itemsTotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0)
        setApplyingCoupon(true)
        try {
            const response = await quoteCoupon({
                code,
                clientId: parseInt(clientId, 10),
                items,
                manualTotal: itemsTotal > 0 ? itemsTotal : summaryPrice,
                manualFinal: summaryPrice,
            })
            if (response.success && response.result) {
                setAppliedCoupon({ code: response.result.code, amount: response.result.amount, persisted: false })
                toast.success(`Cupom ${response.result.code} aplicado.`)
            } else {
                toast.error(response.error || 'Cupom inválido.')
            }
        } finally {
            setApplyingCoupon(false)
        }
    }

    const summaryQuote =
        paymentQuote &&
        paymentQuote.baseAmount === chargedSummaryPrice &&
        paymentQuote.total !== chargedSummaryPrice
            ? paymentQuote
            : null
    const summaryQuoteLabel = summaryQuote
        ? summaryQuote.installments > 1
            ? `${summaryQuote.installments}x no cartão`
            : summaryQuote.method === 'PIX'
              ? 'Pix'
              : summaryQuote.method === 'DEBIT_CARD'
                ? 'débito'
                : summaryQuote.method === 'BOLETO'
                  ? 'boleto'
                  : 'cartão'
        : ''
    const [activeSection, setActiveSection] = useState<string>('')

    // O selector devolve só o id; na edição a tela abre com um paciente que
    // ninguém selecionou nesta sessão, então o nome vem de uma busca própria.
    useEffect(() => {
        if (!clientId) {
            setClientName('')
            return
        }
        let cancelled = false
        void getClient(parseInt(clientId, 10))
            .then(response => {
                if (!cancelled && response.success && response.result) {
                    setClientName(response.result.name)
                }
            })
            .catch(() => {})
        return () => {
            cancelled = true
        }
    }, [clientId])

    /**
     * Procura, no servidor, uma cobrança de HOJE deste paciente que ainda aceite
     * somar atendimento — e continua nela.
     *
     * É o que dá continuidade entre operadores, máquinas e recargas de página: o
     * vínculo não está na sessão de ninguém, está no banco. Quem decide a
     * elegibilidade é o backend (em aberto, sem pagamento gerado, fora de
     * parcelamento); aqui só se restringe a HOJE — cobrança de ontem pode já ter
     * tido o link enviado, e somar nela mudaria um valor que o paciente já viu.
     */
    const detectarAgrupamento = useCallback(
        async (paciente: string, preferidoId?: number) => {
            // Em EDIÇÃO o alvo não se procura: é a cobrança DESTE atendimento, aplicada
            // por `aplicarCobrancaDoRegistro` ao carregar. Sair sem mexer no estado —
            // zerar aqui apagaria o alvo que o carregamento acabou de definir, porque
            // este efeito roda logo depois, disparado pelo `clientId` do registro.
            if (isEditing) return null

            if (!paciente || isBudgetMode) {
                setBillingTargetId(null)
                setGroupedCount(0)
                setGroupedAmount(0)
                return null
            }

            const response = await getAttachableBillings(
                parseInt(paciente, 10)
            ).catch(() => null)
            const lista =
                response?.success && response.result ? response.result : []

            const hoje = new Date().toDateString()
            const doDia =
                // O paciente pode ter mais de uma cobrança de hoje. Quando se chega
                // aqui vindo do "Adicionar outro" de uma edição, o alvo certo é a
                // cobrança daquele atendimento — não a mais recente da lista.
                (preferidoId != null
                    ? lista.find(b => b.id === preferidoId)
                    : undefined) ??
                lista.find(b => new Date(b.createdAt).toDateString() === hoje)

            setBillingTargetId(doDia?.id ?? null)
            setGroupedCount(doDia?.appointmentCount ?? 0)
            setGroupedAmount(doDia?.amount ?? 0)
            return doDia ?? null
        },
        [isEditing, isBudgetMode]
    )

    /**
     * "Adicionar outro" saindo da EDIÇÃO: a cobrança que pode receber o próximo
     * atendimento é a **deste** atendimento, não a "cobrança de hoje do paciente"
     * que a criação procura em `getAttachableBillings`.
     *
     * As condições espelham `attachAppointmentToBilling` (backend) — se a regra de
     * lá mudar, esta função muda junto, senão a tela oferece um botão que o salvar
     * vai recusar. A janela de HOJE é a mesma da criação: o agrupamento é da visita
     * do dia, e cobrança de outro dia já pode ter tido o link enviado ao paciente.
     * Valor que sobe em cobrança já paga não é caso deste botão — para isso existe a
     * cobrança complementar (`handleBillingAdjustment`).
     */
    const aplicarCobrancaDoRegistro = (billing: any) => {
        setExistingBilling(billing ?? null)

        /**
         * A forma de pagamento é herdada da cobrança do atendimento — MENOS quando ela
         * está encerrada (cancelada, estornada, baixada).
         *
         * Herdar de cobrança morta é pior do que não herdar: a tela abre com "Pix" já
         * marcado, o operador entende que está tudo escolhido e clica em salvar — e nada
         * é cobrado, porque quem autoriza a edição a gerar a cobrança é a ESCOLHA feita
         * nesta edição (`paymentChoiceTouchedRef`), não o valor que apareceu na tela.
         * Sem cobrança viva o atendimento está, na prática, sem cobrança: a forma nasce
         * em branco, e escolhê-la é o gesto que gera a nova cobrança ao salvar.
         */
        if (billing && !isClosedBillingStatus(billing.status)) {
            if (billing.paymentMethod) setPaymentMethod(billing.paymentMethod)
            setInstallmentCount(billing.installmentCount ?? null)
        } else {
            setPaymentMethod('')
            setInstallmentCount(null)
        }

        const emAberto =
            billing?.status === 'PENDING' || billing?.status === 'OVERDUE'
        const doDia =
            !!billing?.createdAt &&
            new Date(billing.createdAt).toDateString() ===
                new Date().toDateString()
        const elegivel =
            !!billing?.id &&
            emAberto &&
            doDia &&
            !billing.paymentMethod &&
            !billing.providerPaymentId &&
            !billing.parentBillingId &&
            !billing.installmentGroupId

        if (!elegivel) {
            setBillingTargetId(null)
            setGroupedCount(0)
            setGroupedAmount(0)
            return
        }

        setBillingTargetId(billing.id)
        // Sem fatia, a cobrança cobre um atendimento só — o que está sendo editado.
        setGroupedCount(Math.max(1, billing.payoutShares?.length ?? 0))
        setGroupedAmount(billing.amount ?? 0)
    }

    useEffect(() => {
        if (!partnerId) {
            setPartnerDocument(null)
            return
        }
        let cancelled = false
        getPartner(partnerId)
            .then(response => {
                if (!cancelled) setPartnerDocument(response.result?.cpf ?? null)
            })
            .catch(() => {
                if (!cancelled) setPartnerDocument(null)
            })
        return () => {
            cancelled = true
        }
    }, [partnerId])

    // Parceiro escolhido DEPOIS do profissional (ou aplicado pela comparação de
    // parceiros): o profissional já está no estado e o filtro da busca não o alcança.
    // O par que o atendimento já tinha ao abrir fica como está — só o backend decide
    // sobre registro antigo, e só se o executante mudar.
    useEffect(() => {
        const cpf = (partnerDocument ?? '').replace(/\D/g, '')
        if (cpf.length !== 11 || !professionalId) return
        const professional = Number.parseInt(professionalId, 10)
        if (
            originalExecutants &&
            originalExecutants.partnerId === partnerId &&
            originalExecutants.professionalId === professional
        ) {
            return
        }
        let cancelled = false
        getProfessional(professional)
            .then(response => {
                if (cancelled) return
                const document = (response.result?.document ?? '').replace(/\D/g, '')
                if (document !== cpf) return
                setProfessionalId('')
                toast.warning(
                    `${response.result?.name} já está no atendimento como parceiro. O profissional foi removido: o repasse sai pelo cadastro de parceiro.`
                )
            })
            .catch(() => {})
        return () => {
            cancelled = true
        }
    }, [partnerDocument, professionalId, partnerId, originalExecutants])

    /**
     * Pergunta ao backend o que a troca de executante fará com o repasse.
     *
     * Só quando há cobrança e o executante mudou de fato — é a única situação em que
     * existe conta a pagar para mover. A resposta vem do MESMO caminho que a gravação
     * usa, então o que a tela promete é literalmente o que vai acontecer (e, quando é
     * recusa, é a mesma recusa, palavra por palavra).
     */
    useEffect(() => {
        const billingId = existingBilling?.id
        if (!isEditing || !billingId || !originalExecutants) {
            setPayoutRedirect(null)
            return
        }

        const proximoProfissional = professionalId
            ? Number.parseInt(professionalId, 10)
            : null
        const changes: PayoutRecipientChange[] = []
        if (originalExecutants.partnerId !== (partnerId ?? null)) {
            changes.push({
                kind: 'PARTNER',
                previousEntityId: originalExecutants.partnerId,
                nextEntityId: partnerId ?? null,
            })
        }
        if (originalExecutants.professionalId !== proximoProfissional) {
            changes.push({
                kind: 'PROFESSIONAL',
                previousEntityId: originalExecutants.professionalId,
                nextEntityId: proximoProfissional,
            })
        }

        if (changes.length === 0) {
            setPayoutRedirect(null)
            return
        }

        // A resposta de uma troca anterior não pode sobreviver a esta: sem o
        // descarte, trocar de parceiro duas vezes rápido deixaria em tela o aviso da
        // primeira — inclusive um bloqueio que já não vale.
        let descartado = false
        // PACKAGE não é origem de repasse: sem o atendimento, o servidor só recusa a
        // troca quando a cobrança tiver mais de uma conta do mesmo destinatário.
        const editedSource =
            serviceType === 'EXAM' ||
            serviceType === 'CONSULTATION' ||
            serviceType === 'PROCEDURE'
                ? { sourceType: serviceType, sourceId: Number.parseInt(id!, 10) }
                : undefined
        void previewPayoutRedirect(billingId, changes, editedSource).then(res => {
            if (descartado) return
            setPayoutRedirect(res.success && res.result ? res.result : null)
        })
        return () => {
            descartado = true
        }
    }, [
        isEditing,
        existingBilling?.id,
        originalExecutants,
        partnerId,
        professionalId,
        serviceType,
        id,
    ])

    /**
     * Sementes da URL: paciente e cobrança-alvo com que a tela abre.
     *
     * Quem chega assim veio do "Adicionar outro" de uma EDIÇÃO (`finishUpdate`) — o
     * modo edição não pode continuar na mesma montagem, então a continuidade viaja na
     * query. Só semeia; quem confirma o alvo é a detecção no servidor logo abaixo,
     * que segue sendo a fonte de verdade do agrupamento.
     */
    const urlSeedClientId = searchParams.get('clientId')
    const urlSeedBillingTarget = searchParams.get('billingTarget')
    useEffect(() => {
        if (isEditing) return
        if (urlSeedClientId && !clientId) setClientId(urlSeedClientId)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditing, urlSeedClientId])

    // Ao escolher (ou trocar) o paciente, pergunta ao servidor se há grupo em
    // andamento. Trocar de paciente também zera a dispensa: ela era do anterior.
    useEffect(() => {
        setAgrupamentoDispensado(false)
        const seed = urlSeedBillingTarget
            ? Number.parseInt(urlSeedBillingTarget, 10)
            : NaN
        void detectarAgrupamento(
            clientId,
            Number.isFinite(seed) ? seed : undefined
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId, detectarAgrupamento, urlSeedBillingTarget])

    // Catalogo "somente orçamento": força o modo orçamento e bloqueia o toggle.
    // `budgetOnly` só vale enquanto o registro ainda é orçamento — depois de aprovado
    // (saiu do status BUDGET) ele vira agendamento normal e não pode mais ser forçado
    // ao modo orçamento.
    const forceBudgetMode = useMemo(() => {
        if (loadedStatus && loadedStatus !== 'BUDGET') return false
        if (serviceType === 'EXAM') {
            return examItems.some(item => {
                const cat = catalogsById[item.catalogId]
                return cat?.budgetOnly === true
            })
        }
        if (serviceType === 'PROCEDURE' && orderItems.length > 0) {
            return orderItems.some(item => {
                const cat = catalogsById[item.catalogId]
                return cat?.budgetOnly === true
            })
        }
        return selectedCatalog?.budgetOnly === true
    }, [
        serviceType,
        selectedCatalog,
        examItems,
        orderItems,
        catalogsById,
        loadedStatus,
    ])

    useEffect(() => {
        if (forceBudgetMode && !isBudgetMode) {
            setIsBudgetMode(true)
            // A DATA JÁ DIGITADA PRECISA SAIR JUNTO COM O CAMPO.
            //
            // O modo orçamento esconde "Data e hora do atendimento" (ver
            // `ScheduleWhenFields`), mas esconder o campo não apagava o estado: quem
            // preenchia a data e SÓ DEPOIS escolhia um item `budgetOnly` salvava um
            // orçamento com data invisível na tela. O orçamento não gera evento de
            // agenda de propósito, então o atendimento nascia marcado para o paciente
            // e ausente da agenda da clínica — sem erro e sem aviso.
            //
            // Ao editar não se mexe: ali a data é a que está gravada no registro, e
            // limpá-la sozinha seria apagar informação que o operador não pediu para
            // mudar.
            if (!isEditing) {
                setScheduledDate('')
                setScheduledTime('')
                setArrivalOrder(false)
            }
        }
    }, [forceBudgetMode])

    /**
     * Consulta não é orçável: o toggle de orçamento não aparece em consulta e, se o
     * operador tinha ligado o modo orçamento em outro tipo e trocou para consulta,
     * o modo é desligado. Ao editar, o status gravado manda — aqui não se mexe.
     */
    const canToggleBudgetMode = serviceType !== 'CONSULTATION'

    useEffect(() => {
        if (!isEditing && !canToggleBudgetMode && isBudgetMode) {
            setIsBudgetMode(false)
        }
    }, [canToggleBudgetMode, isEditing, isBudgetMode])

    // === Carregar dados ===
    useEffect(() => {
        if (user?.companyId) {
            loadCatalogs()
            getRoomsByCompany(user.companyId).then(res => {
                if (res.success)
                    setRooms((res.result || []).filter(r => r.active))
            })
            getSettingByName('catalog_pricing_mode', user.companyId).then(
                res => {
                    if (res.success && res.result)
                        setCatalogPricingMode(res.result.value)
                }
            )
            getSettingByName(CATALOG_QUICK_CREATE_SETTING, user.companyId).then(
                res => {
                    if (res.success && res.result)
                        setQuickCreateAllowed(
                            normalizeCatalogQuickCreate(res.result.value)
                        )
                }
            )
            if (isEditing && id) {
                loadExistingData(parseInt(id, 10))
            }
        }
    }, [user?.companyId, serviceType])

    // Auto-selecionar catálogo via URL (?catalogId=...)
    useEffect(() => {
        if (isEditing) return
        const catalogIdParam = searchParams.get('catalogId')
        if (!catalogIdParam) return
        const catalogId = parseInt(catalogIdParam, 10)
        if (!Number.isFinite(catalogId)) return

        // Para EXAM/PROCEDURE (multi-item), evitar re-adicionar se já está nos items
        if (
            serviceType === 'EXAM' &&
            examItems.some(i => i.catalogId === catalogId)
        ) {
            return
        }
        if (
            serviceType === 'PROCEDURE' &&
            orderItems.some(i => i.catalogId === catalogId)
        ) {
            return
        }
        // Para outros tipos, evitar re-aplicar se já selecionado
        if (
            serviceType !== 'EXAM' &&
            serviceType !== 'PROCEDURE' &&
            selectedCatalog?.id === catalogId
        ) {
            return
        }

        getCatalog(catalogId, { scheduling: true }).then(res => {
            if (res.success && res.result) {
                handleCatalogSelect(res.result)
            }
        })
    }, [searchParams, serviceType, isEditing])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                catalogDropdownRef.current &&
                !catalogDropdownRef.current.contains(event.target as Node)
            ) {
                setShowCatalogDropdown(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () =>
            document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    /** Catálogos que a tela precisa ter em mãos para calcular preço e custo. */
    const referencedCatalogIds = useMemo(() => {
        const ids = new Set<number>()
        if (selectedCatalog) ids.add(selectedCatalog.id)
        examItems.forEach(item => ids.add(item.catalogId))
        orderItems.forEach(item => ids.add(item.catalogId))
        return Array.from(ids)
    }, [selectedCatalog, examItems, orderItems])

    /**
     * Ids já buscados um a um, tenham sido encontrados ou não.
     *
     * Sem isso, um catálogo que não volta (removido, sem permissão) deixaria a tela
     * "eternamente hidratando" — e o preço nunca destravaria.
     */
    const [attemptedCatalogIds, setAttemptedCatalogIds] = useState<Set<number>>(
        () => new Set()
    )

    /**
     * Ainda falta buscar algum catálogo referenciado.
     *
     * ⚠️ **É o que segura o preço na edição.** A lista do mount (`loadCatalogs`) traz
     * só as 30 primeiras entradas sem busca: um item lá no meio de um catálogo grande
     * só chega por este `getCatalog`, e ele responde DEPOIS de `loadingData`/
     * `loadingCatalogs` já serem false. Nessa janela o vínculo do profissional não
     * existe para o cálculo, o valor composto sai menor, e quando a resposta chega o
     * salto é lido como "o operador acrescentou algo" — somando ao valor final salvo.
     * Foi assim que um procedimento de R$ 19.500 abriu valendo R$ 27.700 (a diferença
     * era exatamente o honorário do profissional).
     */
    const catalogsHydrating = useMemo(
        () =>
            referencedCatalogIds.some(
                id => !catalogsById[id] && !attemptedCatalogIds.has(id)
            ),
        [referencedCatalogIds, catalogsById, attemptedCatalogIds]
    )

    useEffect(() => {
        const missing = referencedCatalogIds.filter(
            id => !catalogsById[id] && !attemptedCatalogIds.has(id)
        )
        if (!missing.length) return
        Promise.all(missing.map(id => getCatalog(id, { scheduling: true }))).then(responses => {
            const found = responses
                .filter(r => r.success && r.result)
                .map(r => r.result as Catalog)
            mergeCatalogs(found)
            setAttemptedCatalogIds(prev => {
                const next = new Set(prev)
                missing.forEach(id => next.add(id))
                return next
            })
        })
    }, [referencedCatalogIds, catalogsById, attemptedCatalogIds])

    /**
     * INTERMEDIAÇÃO: o profissional só é escolhido em CIRURGIA.
     *
     * Numa intermediadora quem executa o serviço é o PARCEIRO — é com ele que o
     * repasse é combinado, e o valor do catálogo já é o dele. Escolher também um
     * profissional abre uma SEGUNDA fatia de repasse na mesma cobrança: a consulta
     * 3951 cobrou R$ 190,00 do paciente e comprometeu R$ 330,00 (R$ 150,00 ao
     * parceiro "CLÍNICA SÃO LUCAS" e R$ 180,00 à profissional "POLIANA - CLÍNICA
     * SÃO LUCAS", a mesma clínica cadastrada duas vezes).
     *
     * O procedimento CIRÚRGICO é a exceção legítima: ali o cirurgião é uma pessoa
     * distinta do local, tem honorário próprio combinado caso a caso, e o campo
     * "Valor do profissional" existe justamente para isso.
     *
     * Fora da intermediação nada muda — o seletor continua sempre visível.
     */
    const hideProfessionalSelector = useMemo(() => {
        // Sem "Profissionais" em Gestão de entidades a busca responde 403.
        if (!canSeeProfessionals) return true
        if (!isIntermediation) return false
        if (serviceType !== 'PROCEDURE') return true
        if (orderItems.length > 0)
            return !orderItems.some(
                item => catalogsById[item.catalogId]?.isSurgical === true
            )
        return selectedCatalog?.isSurgical !== true
    }, [
        canSeeProfessionals,
        isIntermediation,
        serviceType,
        orderItems,
        selectedCatalog,
        catalogsById,
    ])

    /**
     * Escondeu o campo, o valor sai junto — senão um profissional escolhido antes de
     * trocar o tipo de serviço continuaria gravado, invisível, gerando o repasse
     * duplicado que a regra acima existe para evitar.
     *
     * Só na CRIAÇÃO e com os catálogos já carregados: numa edição o profissional
     * gravado é parte do registro (e do repasse que já existe), e limpá-lo sozinho
     * apagaria informação que o operador não pediu para mudar.
     */
    useEffect(() => {
        if (isEditing || !hideProfessionalSelector || !professionalId) return
        if (loadingData || loadingCatalogs || catalogsHydrating) return
        setProfessionalId('')
    }, [
        hideProfessionalSelector,
        professionalId,
        isEditing,
        loadingData,
        loadingCatalogs,
        catalogsHydrating,
    ])

    // === Variações: preço de exibição e carregamento sob demanda ===
    /**
     * Preço de exibição da variação, na MESMA cascata que o backend cobra
     * (`resolveVariationsForPricing`): parceiro > profissional > empresa. A variação é
     * um Catalog filho com parceiros/profissionais próprios; ignorar isso e ler só o
     * preço da empresa fazia a tela mostrar um valor e o cliente ser cobrado outro.
     */
    const variationDisplayPrice = (
        variation: CatalogVariation,
        forPartnerId: number | null,
        forProfessionalId: number | null
    ): number => {
        const partner = forPartnerId
            ? (variation.partners || []).find(p => p.partnerId === forPartnerId)
            : undefined
        if (partner) return partner.sellingPrice || partner.price || 0

        const professional = forProfessionalId
            ? (variation.professionals || []).find(
                  p => p.professionalId === forProfessionalId
              )
            : undefined
        if (professional)
            return professional.sellingPrice || professional.price || 0

        const cp = (variation.companyPrices || []).find(
            p => p.companyId === user?.companyId
        )
        return cp?.sellingPrice ?? cp?.price ?? 0
    }

    /**
     * Menor preço que a variação tem em ALGUM vínculo — usado só para dizer ao
     * operador de onde o valor virá quando ele ainda não escolheu o executante.
     *
     * A variação costuma não ter preço próprio da empresa: quem tem preço é o vínculo
     * com o parceiro (ver `docs/catalog-variation-as-item-redesign.md`). Sem isto a
     * tela mostrava **R$ 0,00** antes de escolher o parceiro, e o operador concluía que
     * a variação estava quebrada ou era de graça — quando o preço só ainda não existe.
     *
     * `null` quando nem os vínculos têm preço: aí é cadastro faltando, e o zero é a
     * verdade.
     */
    const variationPriceFrom = (variation: CatalogVariation): number | null => {
        const valores = [
            ...(variation.partners || []).map(
                p => p.sellingPrice || p.price || 0
            ),
            ...(variation.professionals || []).map(
                p => p.sellingPrice || p.price || 0
            ),
        ].filter(v => v > 0)
        return valores.length ? Math.min(...valores) : null
    }

    // Preço base do item (sem variação) para a empresa atual.
    const catalogBasePriceFor = (catalogId: number): number => {
        const cat = catalogsById[catalogId]
        if (!cat) return 0
        const cp = catalogCompanyPrice(cat, user?.companyId)
        return cp.sellingPrice || cp.price || 0
    }

    const itemKey = (catalogId: number, variationId?: number | null) =>
        `${catalogId}:${variationId ?? ''}`

    /**
     * Itens cujo vínculo com o executante escolhido está sem preço.
     *
     * Sem custo no vínculo o repasse sai ZERO e a conta a pagar nasce a menor, em
     * silêncio: o LABORATÓRIO POLICLÍNICA VIDA cobrou R$ 40,08 num exame em que a
     * plataforma lançou R$ 25,38, porque Hemograma, Pcr e Glicose estavam com o preço em
     * branco no cadastro do parceiro. Somando as contas em aberto, R$ 5.247,12.
     *
     * Parceiro e profissional são conferidos os dois, cada um pela sua régua
     * (`missingLinkPrices` e `missingProfessionalPrices`) — cada um gera a sua conta a
     * pagar. Escolhida uma variação, quem responde é ELA: a variação é um Catalog filho
     * com vínculos próprios, e é o preço dela que o backend cobra e repassa.
     */
    const itemsMissingExecutantPrice = useMemo(() => {
        const forProfessionalId = professionalId ? Number(professionalId) : null
        if (!partnerId && !forProfessionalId) return []

        // Orçamento de PROCEDIMENTO fica livre: ali o valor de quem executa é digitado na
        // hora, item a item, e não vem do vínculo do catálogo. Orçamento de exame e de
        // consulta continua valendo a regra — neles o preço sai do cadastro.
        if (serviceType === 'PROCEDURE' && (isBudgetMode || loadedStatus === 'BUDGET'))
            return []

        // Vínculo do executante no item (ou na variação, que tem vínculo próprio).
        // `undefined` = catálogo ainda não carregou: não é falta de preço, é falta de
        // dado na tela, e bloquear aí travaria o formulário sem motivo.
        const issuesFor = (
            catalogId: number,
            catalogVariationId: number | null | undefined,
            label: string
        ): {
            key: string
            baseKey: string
            name: string
            missing: string
            executant: string
        }[] => {
            const key = itemKey(catalogId, catalogVariationId)

            // CIRURGIA fica de fora. O procedimento cirúrgico é orçado: o valor de quem
            // executa é combinado caso a caso e digitado na hora, e o catálogo nem tenta
            // ter preço por parceiro — 1.880 dos 2.630 procedimentos são assim. Exigir
            // cadastro aqui travava a edição de orçamento já aprovado.
            if (catalogsById[catalogId]?.isSurgical) return []

            // Escolhida uma variação, quem tem preço é ELA: a variação é um Catalog
            // filho com parceiros e profissionais próprios, e é o preço dela que o
            // backend cobra e repassa (`resolveVariationsForPricing`). Conferir o pai
            // aqui deixaria passar variação zerada dentro de item bem cadastrado.
            let source: { partners?: CatalogPartner[]; professionals?: CatalogProfessional[] } | undefined
            if (catalogVariationId != null) {
                const carregadas = variationsByCatalog[catalogId]
                // Lista ainda não carregada: é falta de dado na tela, não de cadastro.
                if (!carregadas) return []
                source = carregadas.find(v => v.id === catalogVariationId)
                // Carregou e a variação não está lá (desativada, removida): não há preço
                // de onde sair, e o repasse iria a zero do mesmo jeito.
                if (!source)
                    return [
                        {
                            key: `${key}:variacao`,
                            baseKey: key,
                            name: label,
                            missing: 'custo e valor de venda',
                            executant:
                                'esta variação, que não está mais ativa no catálogo',
                        },
                    ]
            } else {
                source = catalogsById[catalogId]
                if (!source) return []
            }

            // Parceiro e profissional são conferidos os DOIS quando os dois estão
            // escolhidos: cada um gera a sua conta a pagar, com o seu próprio preço.
            // Vínculo inativo não conta — o backend só soma vínculo ativo, então
            // deixá-lo passar aqui devolveria repasse zero na baixa.
            const issues: {
                key: string
                baseKey: string
                name: string
                missing: string
                executant: string
            }[] = []

            if (partnerId) {
                const link = (source.partners || []).find(
                    p => p.partnerId === partnerId && p.active !== false
                )
                const missing = missingLinkPrices(link)
                if (missing)
                    issues.push({
                        key: `${key}:parceiro`,
                        baseKey: key,
                        name: label,
                        missing,
                        executant: link?.partner?.name ?? 'o parceiro selecionado',
                    })
            }

            if (forProfessionalId) {
                const link = (source.professionals || []).find(
                    p =>
                        p.professionalId === forProfessionalId &&
                        p.active !== false
                )
                const missing = missingProfessionalPrices(link)
                if (missing)
                    issues.push({
                        key: `${key}:profissional`,
                        baseKey: key,
                        name: label,
                        missing,
                        executant:
                            link?.professional?.name ??
                            'o profissional selecionado',
                    })
            }

            return issues
        }

        const nomeDaLinha = (item: {
            name: string
            variationName?: string
        }): string =>
            item.variationName ? `${item.name} — ${item.variationName}` : item.name

        const issues: ReturnType<typeof issuesFor> = []
        const push = (novas: ReturnType<typeof issuesFor>) => {
            novas.forEach(issue => {
                if (!issues.some(i => i.key === issue.key)) issues.push(issue)
            })
        }

        if (serviceType === 'EXAM') {
            examItems.forEach(item =>
                push(
                    issuesFor(
                        item.catalogId,
                        item.catalogVariationId,
                        nomeDaLinha(item)
                    )
                )
            )
        }
        if (serviceType === 'PROCEDURE') {
            orderItems.forEach(item =>
                push(
                    issuesFor(
                        item.catalogId,
                        item.catalogVariationId,
                        nomeDaLinha(item)
                    )
                )
            )
        }
        if (serviceType === 'CONSULTATION' && selectedCatalog) {
            if (consultationVariations.length > 0) {
                consultationVariations.forEach(cv =>
                    push(
                        issuesFor(
                            selectedCatalog.id,
                            cv.catalogVariationId,
                            `${selectedCatalog.name} — ${cv.name}`
                        )
                    )
                )
            } else {
                push(issuesFor(selectedCatalog.id, null, selectedCatalog.name))
            }
        }
        return issues
    }, [
        partnerId,
        professionalId,
        serviceType,
        isBudgetMode,
        loadedStatus,
        examItems,
        orderItems,
        selectedCatalog,
        consultationVariations,
        catalogsById,
        variationsByCatalog,
    ])

    const loadVariationsFor = async (catalogId: number) => {
        if (variationsByCatalog[catalogId])
            return variationsByCatalog[catalogId]
        const res = await getCatalogVariations(catalogId, { scheduling: true })
        const active = (res.success ? res.result || [] : []).filter(
            v => v.active
        )
        setVariationsByCatalog(prev => ({ ...prev, [catalogId]: active }))
        return active
    }

    // Carrega variações para todos os catálogos referenciados (inclui hidratação
    // da edição). Sob demanda: só busca o que ainda não estiver no cache.
    useEffect(() => {
        const ids = new Set<number>()
        examItems.forEach(i => ids.add(i.catalogId))
        orderItems.forEach(i => ids.add(i.catalogId))
        if (serviceType === 'CONSULTATION' && selectedCatalog)
            ids.add(selectedCatalog.id)
        ids.forEach(catalogId => {
            if (!(catalogId in variationsByCatalog)) {
                void loadVariationsFor(catalogId)
            }
        })
    }, [examItems, orderItems, selectedCatalog, serviceType])

    // CONSULTA: o preço de cada variação escolhida depende do parceiro/profissional
    // global (cascata parceiro > profissional > empresa). Ao trocar o parceiro/
    // profissional, recalcula os unitPrice das variações para o basePrice refletir.
    useEffect(() => {
        if (serviceType !== 'CONSULTATION' || !selectedCatalog) return
        const vars = variationsByCatalog[selectedCatalog.id] || []
        if (vars.length === 0) return
        setConsultationVariations(prev => {
            if (prev.length === 0) return prev
            let changed = false
            const next = prev.map(cv => {
                const v = vars.find(x => x.id === cv.catalogVariationId)
                if (!v) return cv
                const price = variationDisplayPrice(
                    v,
                    partnerId,
                    professionalId ? Number(professionalId) : null
                )
                if (price !== cv.unitPrice) {
                    changed = true
                    return { ...cv, unitPrice: price }
                }
                return cv
            })
            return changed ? next : prev
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        partnerId,
        professionalId,
        serviceType,
        selectedCatalog,
        variationsByCatalog,
    ])

    /**
     * EXAME/PROCEDIMENTO: o preço da variação também segue o parceiro/profissional.
     *
     * A linha da variação nasce com o preço do executante escolhido **naquele
     * momento** — e ficava congelada ali. Escolhendo a variação ANTES do parceiro (o
     * caminho natural: o operador escolhe o que vai fazer, depois onde), o preço
     * gravado era o de "sem parceiro", que quase sempre é zero, e o Valor Final
     * continuava R$ 0,00 mesmo depois de escolher o parceiro.
     *
     * A consulta já tinha esse recálculo (efeito acima); exame e procedimento não —
     * e é neles que a variação vira linha própria.
     *
     * Só toca em linha COM variação: o preço do item sem variação é resolvido pela
     * composição de preço (`PricingSection`), e sobrescrevê-lo aqui desfaria um valor
     * digitado à mão.
     */
    useEffect(() => {
        const forProfessionalId = professionalId ? Number(professionalId) : null
        const recalcular = <
            T extends { catalogId: number; catalogVariationId?: number | null; unitPrice: number },
        >(
            prev: T[]
        ): T[] => {
            let changed = false
            const next = prev.map(item => {
                if (item.catalogVariationId == null) return item
                const v = (variationsByCatalog[item.catalogId] || []).find(
                    x => x.id === item.catalogVariationId
                )
                if (!v) return item
                const price = variationDisplayPrice(
                    v,
                    partnerId,
                    forProfessionalId
                )
                if (price === item.unitPrice) return item
                changed = true
                return { ...item, unitPrice: price }
            })
            return changed ? next : prev
        }
        setExamItems(prev => recalcular(prev))
        setOrderItems(prev => recalcular(prev))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [partnerId, professionalId, variationsByCatalog])

    // Controles de seleção de variação para uma linha empilhável (EXAME/
    // PROCEDIMENTO), delegados a `VariationRowControls`. A cascata de preço
    // (parceiro > profissional > empresa) é resolvida aqui porque depende do
    // parceiro/profissional escolhidos no agendamento.
    const renderVariationControls = <
        T extends ExamSelectedItem | OrderSelectedItem,
    >(
        item: T,
        index: number,
        items: T[],
        setItems: React.Dispatch<React.SetStateAction<T[]>>
    ): React.ReactNode => (
        <VariationRowControls
            item={item}
            index={index}
            items={items}
            setItems={setItems}
            variations={variationsByCatalog[item.catalogId] || []}
            priceOf={variation =>
                variationDisplayPrice(
                    variation,
                    partnerId,
                    professionalId ? Number(professionalId) : null
                )
            }
            priceFromOf={variationPriceFrom}
            basePriceOf={catalogBasePriceFor}
        />
    )

    useEffect(() => {
        if (!showCatalogDropdown) return
        if (catalogSearchDebounceRef.current) {
            clearTimeout(catalogSearchDebounceRef.current)
        }
        catalogSearchDebounceRef.current = setTimeout(() => {
            loadCatalogs(catalogSearch)
        }, 300)
        return () => {
            if (catalogSearchDebounceRef.current) {
                clearTimeout(catalogSearchDebounceRef.current)
            }
        }
        // partnerId entra nas deps para recarregar a lista filtrada pelo
        // parceiro escolhido (intermediação) sem fechar/reabrir a busca.
    }, [catalogSearch, showCatalogDropdown, searchAllCatalog, partnerId])

    const loadCatalogs = async (search?: string): Promise<Catalog[]> => {
        if (!user?.companyId /*|| serviceType === 'TELECONSULTATION'*/) return []
        setLoadingCatalogs(true)
        try {
            const category =
                serviceType === 'CONSULTATION'
                    ? 'CONSULTATION'
                    : serviceType === 'EXAM'
                      ? 'EXAM'
                      : serviceType === 'PACKAGE'
                        ? 'PACKAGE'
                        : 'PROCEDURE'
            const response = await getCatalogsByCompany(user.companyId, {
                category: searchAllCatalog ? undefined : category,
                activeOnly: true,
                // Pacote novo é VENDIDO no próprio cadastro, com data por componente —
                // aparecer aqui levaria o operador ao formulário errado. Os antigos
                // continuam agendáveis. Ver `Catalog.schedulable`.
                schedulableOnly: true,
                page: 1,
                limit: 30,
                search: search?.trim() || undefined,
                // Intermediação: depois de escolher o parceiro, a busca só
                // retorna itens que ESSE parceiro atende (paciente vai a um
                // único local). Sem parceiro escolhido, mostra todos.
                // PROCEDURE NÃO funila por parceiro — é feito internamente.
                partnerId:
                    isIntermediation && partnerId && serviceType !== 'PROCEDURE'
                        ? partnerId
                        : undefined,
            })
            if (response.success) {
                const list = response.result || []
                setCatalogs(list)
                mergeCatalogs(list)
                return list
            }
        } catch {
            // silently fail
        } finally {
            setLoadingCatalogs(false)
        }
        return []
    }

    const loadExistingData = async (recordId: number) => {
        setLoadingData(true)
        try {
            const formatDate = (d: string) =>
                formatInTimeZone(
                    new Date(d),
                    'America/Sao_Paulo',
                    "yyyy-MM-dd'T'HH:mm"
                )

            if (serviceType === 'CONSULTATION') {
                const res = await getConsultation(recordId)
                if (!res.success || !res.result) {
                    toast.error('Consulta não encontrada')
                    navigate('/bookings/consultations')
                    return
                }
                const c = res.result
                if ((c as any).couponId) {
                    setAppliedCoupon({
                        code: (c as any).couponCode ?? '',
                        amount: (c as any).couponDiscount ?? 0,
                        persisted: true,
                    })
                }
                setClientId(c.clientId?.toString() || '')
                setProfessionalId(c.professionalId?.toString() || '')
                setPartnerId(c.partnerId || null)
                setOriginalExecutants({
                    professionalId: c.professionalId ?? null,
                    partnerId: c.partnerId ?? null,
                })
                setHealthPlanId(c.healthPlanId || null)
                if ((c as any).hospitalId) {
                    setHospitalId((c as any).hospitalId)
                    setHospitalIdTouched(true)
                }
                setScheduledAt(c.scheduledAt ? formatDate(c.scheduledAt) : '')
                setArrivalOrder(!!(c as any).arrivalOrder)
                // Guardado para detectar REMARCAÇÃO no submit: mudar a data de um atendimento
                // que já tinha data é remarcar, não editar.
                setOriginalScheduledAt(
                    c.scheduledAt ? formatDate(c.scheduledAt) : ''
                )
                setNotes(c.notes || '')
                setLoadedStatus((c as any).status ?? null)
                setDiscount(
                    c.discount != null ? String(Number(c.discount)) : ''
                )
                setDuration(c.duration ?? null)
                if (c.catalog) {
                    setSelectedCatalog(c.catalog as Catalog)
                    setCatalogSearch(c.catalog.name)
                }
                if ((c as any).variations?.length) {
                    setConsultationVariations(
                        (c as any).variations.map((v: any) => ({
                            catalogVariationId:
                                v.variationId ?? v.catalogVariationId,
                            quantity: v.quantity || 1,
                            name:
                                v.variationName ??
                                v.variation?.name ??
                                'Variação',
                            unitPrice: Number(v.unitPrice ?? 0),
                        }))
                    )
                }
                // Sala
                if ((c as any).roomId) setRoomId((c as any).roomId)
                else if ((c as any).agendaEvents?.[0]?.roomId)
                    setRoomId((c as any).agendaEvents[0].roomId)
                setConsultTitle(c.title || '')
                setIsReturn(c.isReturn || false)
                setTriageId(c.triageId?.toString() || '')
                setDiagnosis(c.diagnosis || '')
                setTreatmentPlan(c.treatmentPlan || '')
                aplicarCobrancaDoRegistro((c as any).billing ?? null)
            } else if (serviceType === 'EXAM') {
                const res = await getExam(recordId)
                if (!res.success || !res.result) {
                    toast.error('Exame não encontrado')
                    navigate('/bookings/exams')
                    return
                }
                const e = res.result as any
                if (e.couponId) {
                    setAppliedCoupon({ code: e.couponCode ?? '', amount: e.couponDiscount ?? 0, persisted: true })
                }
                setClientId(e.clientId?.toString() || '')
                setProfessionalId(e.professionalId?.toString() || '')
                setPartnerId(e.partnerId || null)
                setOriginalExecutants({
                    professionalId: e.professionalId ?? null,
                    partnerId: e.partnerId ?? null,
                })
                setHealthPlanId(e.healthPlanId || null)
                if (e.hospitalId) {
                    setHospitalId(e.hospitalId)
                    setHospitalIdTouched(true)
                }
                setScheduledAt(e.scheduledAt ? formatDate(e.scheduledAt) : '')
                setArrivalOrder(!!(e as any).arrivalOrder)
                // Guardado para detectar REMARCAÇÃO no submit: mudar a data de um atendimento
                // que já tinha data é remarcar, não editar.
                setOriginalScheduledAt(
                    e.scheduledAt ? formatDate(e.scheduledAt) : ''
                )
                setNotes(e.notes || '')
                setDiscount(
                    e.discount != null ? String(Number(e.discount)) : ''
                )
                setDuration(e.duration ?? null)
                if (e.items?.length) {
                    setExamItems(
                        e.items.map((item: any) => ({
                            catalogId: item.catalogId,
                            name:
                                item.catalog?.name || `Item #${item.catalogId}`,
                            catalogTypeName:
                                item.catalog?.catalogType?.name || '',
                            unitPrice: Number(
                                item.unitPrice ||
                                    item.catalog?.sellingPrice ||
                                    item.catalog?.price ||
                                    0
                            ),
                            quantity: item.quantity || 1,
                            catalogVariationId:
                                item.catalogVariationId ?? undefined,
                            variationName:
                                item.variationName ??
                                item.catalogVariation?.name ??
                                undefined,
                        }))
                    )
                }
                setLoadedStatus(e.status ?? null)
                if (e.status === 'BUDGET') setIsBudgetMode(true)
                // Valor customizado: comparar totalPrice com a soma dos itens
                if (e.totalPrice != null && e.items?.length) {
                    const itemsTotal = e.items.reduce(
                        (sum: number, item: any) => {
                            const price = Number(
                                item.unitPrice ||
                                    item.catalog?.sellingPrice ||
                                    item.catalog?.price ||
                                    0
                            )
                            return sum + price * (item.quantity || 1)
                        },
                        0
                    )
                    if (e.totalPrice !== itemsTotal) {
                        setOverridePrice(e.totalPrice)
                    }
                }
                if (e.validUntil) setValidUntil(e.validUntil.substring(0, 10))
                // Sala
                if (e.roomId) setRoomId(e.roomId)
                else if (e.agendaEvents?.[0]?.roomId)
                    setRoomId(e.agendaEvents[0].roomId)
                // Materiais médicos
                if (e.medicalSupplies?.length) {
                    setMedicalSupplies(
                        e.medicalSupplies.map((ms: any) => ({
                            medicalSupplyId: ms.medicalSupplyId,
                            name:
                                ms.medicalSupply?.name ||
                                `Material #${ms.medicalSupplyId}`,
                            type: ms.medicalSupply?.type || '',
                            quantity: ms.quantity || 1,
                            costPriceSnapshot:
                                ms.costPriceSnapshot ?? undefined,
                            recipientKind: ms.recipientKind ?? undefined,
                            recipientId: ms.recipientId ?? null,
                            unitPrice: ms.unitPrice || 0,
                            defaultSupplierId:
                                ms.medicalSupply?.supplierId ?? null,
                        }))
                    )
                }
                aplicarCobrancaDoRegistro((e as any).billing ?? null)
            } else if (serviceType === 'PROCEDURE') {
                const res = await getOrder(recordId)
                if (!res.success || !res.result) {
                    toast.error(`${serviceLabels.Order} não encontrado`)
                    navigate('/bookings/orders')
                    return
                }
                const p = res.result as any
                // Cupom do procedimento (agendamento ou loja): mesmo campo `discountAmount`.
                const orderCouponAmount = p.couponId ? (p.discountAmount ?? 0) : 0
                if (p.couponId) {
                    setAppliedCoupon({ code: p.couponCode ?? '', amount: orderCouponAmount, persisted: true })
                }
                setClientId(p.clientId?.toString() || '')
                setProfessionalId(p.professionalId?.toString() || '')
                setPartnerId(p.partnerId || null)
                setOriginalExecutants({
                    professionalId: p.professionalId ?? null,
                    partnerId: p.partnerId ?? null,
                })
                setHealthPlanId(p.healthPlanId || null)
                if (p.hospitalId) {
                    setHospitalId(p.hospitalId)
                    setHospitalIdTouched(true)
                }
                setScheduledAt(p.scheduledAt ? formatDate(p.scheduledAt) : '')
                setArrivalOrder(!!(p as any).arrivalOrder)
                // Guardado para detectar REMARCAÇÃO no submit: mudar a data de um atendimento
                // que já tinha data é remarcar, não editar.
                setOriginalScheduledAt(
                    p.scheduledAt ? formatDate(p.scheduledAt) : ''
                )
                setNotes(p.notes || '')
                setDiscount(
                    p.discount != null ? String(Number(p.discount)) : ''
                )
                setDuration(p.duration ?? null)
                if (p.catalog) {
                    setSelectedCatalog(p.catalog as Catalog)
                    setCatalogSearch(p.catalog.name)
                }
                // Itens empilhados do procedimento (multi-item). Registros
                // antigos sem `items` derivam 1 item do catálogo principal.
                if (p.items?.length) {
                    setOrderItems(
                        p.items.map((item: any) => ({
                            catalogId: item.catalogId,
                            name:
                                item.catalog?.name || `Item #${item.catalogId}`,
                            catalogTypeName:
                                item.catalog?.catalogType?.name || '',
                            unitPrice: Number(
                                item.unitPrice ||
                                    item.catalog?.sellingPrice ||
                                    item.catalog?.price ||
                                    0
                            ),
                            quantity: item.quantity || 1,
                            maxDiscount:
                                item.catalog?.maxDiscount != null
                                    ? Number(item.catalog.maxDiscount)
                                    : undefined,
                            catalogVariationId:
                                item.catalogVariationId ?? undefined,
                            variationName:
                                item.variationName ??
                                item.catalogVariation?.name ??
                                undefined,
                        }))
                    )
                } else if (p.catalog) {
                    setOrderItems([
                        {
                            catalogId: p.catalog.id,
                            name: p.catalog.name,
                            catalogTypeName: p.catalog.catalogType?.name || '',
                            unitPrice: Number(
                                p.catalog.sellingPrice || p.catalog.price || 0
                            ),
                            quantity: 1,
                            maxDiscount:
                                p.catalog.maxDiscount != null
                                    ? Number(p.catalog.maxDiscount)
                                    : undefined,
                        },
                    ])
                }
                // Sala
                if (p.roomId) setRoomId(p.roomId)
                else if (p.events?.[0]?.roomId) setRoomId(p.events[0].roomId)
                setPreInstructions(p.preInstructions || '')
                setPostInstructions(p.postInstructions || '')
                if (p.anesthetistFee)
                    setAnesthetistFee(
                        currencyFormat(p.anesthetistFee.toString(), true)
                    )
                if (p.anesthetistRecipientKind)
                    setAnesthetistRecipientKind(p.anesthetistRecipientKind)
                if (p.anesthetistRecipientId)
                    setAnesthetistRecipientId(p.anesthetistRecipientId)
                if (p.hospitalFee)
                    setHospitalFee(
                        currencyFormat(p.hospitalFee.toString(), true)
                    )
                if (p.hospitalFeeNotes) setHospitalFeeNotes(p.hospitalFeeNotes)
                if ((p as any).professionalAmount != null)
                    setProfessionalAmountOverride(
                        currencyFormat(
                            (p as any).professionalAmount.toString(),
                            true
                        )
                    )
                // Custo e markup acertados na criação — a tela abre com eles até que
                // o operador mexa em alguma coisa (ver `pricingTouched`).
                setSavedCostPrice((p as any).costPrice ?? null)
                setSavedMarkup(
                    (p as any).markupPercentage != null
                        ? Number((p as any).markupPercentage)
                        : null
                )
                // Aprovado em pagamento flex: quem decide forma e parcela é o
                // paciente, no portal. Oferecer a escolha aqui promete um acerto
                // que esta tela não faz.
                setFlexPayment(!!(p as any).flexPayment)
                setLoadedStatus(p.status ?? null)
                if (p.status === 'BUDGET') setIsBudgetMode(true)
                // Valor customizado: o campo editável é o VALOR FINAL, então abre com
                // `finalPrice` — o que foi acordado com o paciente.
                //
                // ⚠️ Abrir com `totalPrice` (o valor ANTES do desconto) fazia um salvar sem
                // alterações regravar o preço cheio: `resolvePricing` trata o override como
                // valor final e zera o desconto. O orçamento subia sozinho a cada edição.
                //
                // Com cupom, o `finalPrice` gravado já vem SEM ele: o campo editável é o
                // preço manual, então o cupom volta a ser somado aqui — senão o save
                // descontaria o cupom de novo.
                if (p.finalPrice != null && (p.finalPrice > 0 || orderCouponAmount > 0)) {
                    setOverridePrice(p.finalPrice + orderCouponAmount)
                } else if (p.totalPrice != null && p.totalPrice > 0) {
                    setOverridePrice(p.totalPrice)
                }
                if (p.validUntil) setValidUntil(p.validUntil.substring(0, 10))
                // Materiais médicos
                if (p.medicalSupplies?.length) {
                    setMedicalSupplies(
                        p.medicalSupplies.map((ms: any) => ({
                            medicalSupplyId: ms.medicalSupplyId,
                            name:
                                ms.medicalSupply?.name ||
                                `Material #${ms.medicalSupplyId}`,
                            type: ms.medicalSupply?.type || '',
                            quantity: ms.quantity || 1,
                            costPriceSnapshot:
                                ms.costPriceSnapshot ?? undefined,
                            recipientKind: ms.recipientKind ?? undefined,
                            recipientId: ms.recipientId ?? null,
                            unitPrice: ms.unitPrice || 0,
                            defaultSupplierId:
                                ms.medicalSupply?.supplierId ?? null,
                        }))
                    )
                }
                setOrderProducts(
                    ((p as any).products || []).map((pp: any) => ({
                        catalogId: pp.catalogId,
                        name: pp.name || pp.catalog?.name || 'Produto',
                        quantity: pp.quantity || 1,
                        supplierName: pp.supplier?.name ?? null,
                        unit: pp.catalog?.productDetails?.unit ?? null,
                    }))
                )
                setFromPackage(!!(p as any).packageCatalogId)
                setPackageSaleMode(
                    !!(p as any).packageCatalogId &&
                        (p as any).packageCatalog?.schedulable === false
                )
                // Itens atrelados (exames + consultas) — separados por catalog.category
                if (p.exams?.length) {
                    const mapLinked = (ex: any) => {
                        const itemPrice = Number(
                            ex.catalog?.sellingPrice ||
                                ex.catalog?.price ||
                                ex.unitPrice ||
                                0
                        )
                        return {
                            catalogId: ex.catalogId,
                            name: ex.catalog?.name || `Item #${ex.catalogId}`,
                            catalogTypeName:
                                ex.catalog?.catalogType?.name || '',
                            itemPrice,
                            unitPrice: ex.unitPrice || itemPrice,
                            quantity: ex.quantity || 1,
                            complimentary: ex.complimentary || false,
                            notes: ex.notes || undefined,
                            priceSource: 'item' as const,
                            availablePartners: [],
                            availableProfessionals: [],
                            // Sem reidratar o destinatário, editar qualquer coisa
                            // no procedimento reenviaria os componentes sem ele —
                            // e a edição reescreve a lista inteira.
                            recipientKind: ex.recipientKind ?? null,
                            recipientId: ex.recipientId ?? null,
                            recipientAmount: ex.recipientAmount ?? null,
                            // Valor que veio do banco é valor decidido: sem marcá-lo
                            // como editado, a sincronização da tela o substituiria pelo
                            // valor cheio e o repasse mudaria só por reabrir a tela.
                            recipientAmountEdited: ex.recipientAmount != null,
                            scheduledAt: ex.scheduledAt ?? null,
                            arrivalOrder: !!ex.arrivalOrder,
                            roomId: ex.roomId ?? null,
                        }
                    }
                    setOrderExams(
                        p.exams
                            .filter(
                                (ex: any) =>
                                    (ex.catalog?.category ?? 'EXAM') === 'EXAM'
                            )
                            .map(mapLinked)
                    )
                    setOrderConsultations(
                        p.exams
                            .filter(
                                (ex: any) =>
                                    ex.catalog?.category === 'CONSULTATION'
                            )
                            .map(mapLinked)
                    )
                }
                aplicarCobrancaDoRegistro((p as any).billing ?? null)
            } else if ((serviceType as string) === 'TELECONSULTATION') {
                const res = await getTeleconsultation(recordId)
                if (!res.success || !res.result) {
                    toast.error('Teleconsulta não encontrada')
                    navigate('/bookings/teleconsultations')
                    return
                }
                const t = res.result as any
                setClientId(t.clientId?.toString() || '')
                setProfessionalId(t.professionalId?.toString() || '')
                setPartnerId(t.partnerId || null)
                setHealthPlanId(t.healthPlanId || null)
                setScheduledAt(t.scheduledAt ? formatDate(t.scheduledAt) : '')
                setArrivalOrder(!!(t as any).arrivalOrder)
                // Guardado para detectar REMARCAÇÃO no submit: mudar a data de um atendimento
                // que já tinha data é remarcar, não editar.
                setOriginalScheduledAt(
                    t.scheduledAt ? formatDate(t.scheduledAt) : ''
                )
                setNotes(t.notes || '')
                setDiscount(
                    t.discount != null ? String(Number(t.discount)) : ''
                )
                setTeleTitle(t.title || '')
                setTeleIsReturn(t.isReturn || false)
                setMaxTeleDuration(t.maxDuration?.toString() || '3600')
                setRecordingEnabled(t.recordingEnabled || false)
                setEnableChat(t.enableChat !== false)
                setEnableScreenShare(t.enableScreenShare !== false)
                setSpecialtyId(t.specialtyId || null)
                if (t.price)
                    setTelePrice(currencyFormat(t.price.toString(), true))
                if (t.sellingPrice)
                    setTeleSellingPrice(
                        currencyFormat(t.sellingPrice.toString(), true)
                    )
                if (t.markupPercentage)
                    setTeleMarkup(t.markupPercentage.toString())
            }
        } catch {
            toast.error('Erro ao carregar dados')
        } finally {
            setLoadingData(false)
        }
    }

    // === Resetar ao mudar tipo ===
    const handleServiceTypeChange = (type: ServiceType) => {
        setServiceType(type)
        setSelectedCatalog(null)
        setCatalogSearch('')
        setExamItems([])
        setOrderItems([])
        setConsultationVariations([])
        setDiscount('')
        setProfessionalId('')
        if (!hidePartnerSelector) setPartnerId(null)
        setMedicalSupplies([])
        setAnesthetistFee('')
        setOrderExams([])
        setOrderExamTemplates([])
        setOrderConsultations([])
        setHospitalId(null)
        setHospitalIdTouched(false)
    }

    // Novo cadastro abre em Consulta; quem não tem Consultas no menu cai no primeiro
    // tipo liberado. Em edição o tipo é o do registro.
    useEffect(() => {
        if (isEditing || isCategoryAllowed(serviceType)) return
        const firstAllowed = availableServiceTypes[0]
        if (firstAllowed) handleServiceTypeChange(firstAllowed)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditing, serviceType, availableServiceTypes.join(',')])

    // === Seleção de catálogo ===
    /** A linha da pilha, montada a partir do item do catálogo. */
    const buildOrderItem = (catalog: Catalog): OrderSelectedItem => {
        const catCp = catalogCompanyPrice(catalog, user?.companyId)
        return {
            catalogId: catalog.id,
            name: catalog.name,
            catalogTypeName: catalog.catalogType?.name || '',
            unitPrice: catCp.sellingPrice || catCp.price || 0,
            quantity: 1,
            maxDiscount: catCp.maxDiscount
                ? Number(catCp.maxDiscount)
                : undefined,
        }
    }

    /** Substitui o único item da pilha pelo escolhido — a troca propriamente dita. */
    const replaceSingleOrderItem = (catalog: Catalog) => {
        setSelectedCatalog(catalog)
        setOrderItems([buildOrderItem(catalog)])
        setCatalogSearch('')
        setPendingCatalogChoice(null)
        void loadVariationsFor(catalog.id)
    }

    /** Empilha mais um procedimento, mantendo o que já estava na lista. */
    const appendOrderItem = (catalog: Catalog) => {
        setOrderItems(prev => [...prev, buildOrderItem(catalog)])
        setCatalogSearch('')
        setPendingCatalogChoice(null)
        void loadVariationsFor(catalog.id)
    }

    const handleCatalogSelect = async (catalog: Catalog) => {
        /**
         * Um item na pilha, numa edição: PERGUNTA antes de empilhar.
         *
         * Procedimento é empilhável como exame, então clicar no catálogo sempre
         * ACRESCENTAVA. Quem abre a edição para trocar "sem prótese" por "com
         * prótese" e esquece de remover o anterior sai com os dois na conta — e o
         * valor dobra sem que nada na tela grite. Com um item só dá para saber o
         * que ele quis dizer; com vários, não, e aí empilhar é a única leitura
         * possível.
         */
        if (
            isEditing &&
            serviceType === 'PROCEDURE' &&
            orderItems.length === 1 &&
            orderItems[0].catalogId !== catalog.id
        ) {
            setShowCatalogDropdown(false)
            setPendingCatalogChoice(catalog)
            return
        }

        // PROCEDURE empilhável: manter selectedCatalog no item primário (o
        // primeiro da pilha) para os memos de preço/parceiro continuarem
        // funcionando. Ao empilhar mais itens não sobrescrevemos.
        if (!(serviceType === 'PROCEDURE' && orderItems.length > 0)) {
            setSelectedCatalog(catalog)
        }
        setCatalogSearch(catalog.name)
        setShowCatalogDropdown(false)

        /**
         * EDIÇÃO não é montagem: trocar o item de um atendimento que já existe
         * mexe SÓ no item.
         *
         * Escolher um item do catálogo, na criação, monta o atendimento em volta
         * dele — executante em branco, taxa do anestesista, materiais e exames do
         * template. Fazer isso numa edição destrói o que já estava acertado: o caso
         * real é a paciente pagando a mastopexia em pagamento flexível e trocando
         * para a versão com prótese; o profissional era zerado, o valor recalculava
         * sem o honorário dele e os materiais voltavam para os do catálogo, tudo sem
         * ninguém ter pedido. Quem quiser mudar o executante ou os materiais muda à
         * mão, que é o gesto que ele já conhece.
         */
        const montandoDoZero = !isEditing

        if (montandoDoZero && catalog.defaultDuration) {
            setDuration(catalog.defaultDuration)
        }
        if (montandoDoZero) setProfessionalId('')
        // EXAM e PROCEDURE são multi-item (acúmulo): adicionar outro item NÃO
        // pode zerar o parceiro já escolhido — senão o valor "some" a cada
        // inclusão. Em CONSULTATION (item único) trocar o catálogo reseta o
        // parceiro.
        if (
            montandoDoZero &&
            !hidePartnerSelector &&
            serviceType !== 'EXAM' &&
            serviceType !== 'PROCEDURE'
        )
            setPartnerId(null)
        if (montandoDoZero) {
            setDiscount('')
            setPriceComposition('item')
        }

        // CONSULTA (item único): trocar de catálogo zera as variações da
        // seleção anterior; as do novo item são carregadas pelo effect.
        if (serviceType === 'CONSULTATION') {
            setConsultationVariations([])
        }

        // Exame: adicionar como item
        if (serviceType === 'EXAM') {
            const catCp = catalogCompanyPrice(catalog, user?.companyId)
            const baseCatalogPrice = catCp.sellingPrice || catCp.price || 0
            setExamItems(prev => [
                ...prev,
                {
                    catalogId: catalog.id,
                    name: catalog.name,
                    catalogTypeName: catalog.catalogType?.name || '',
                    unitPrice: baseCatalogPrice,
                    quantity: 1,
                    maxDiscount: catCp.maxDiscount
                        ? Number(catCp.maxDiscount)
                        : undefined,
                },
            ])
            setCatalogSearch('')
            // Carrega variações deste item (também coberto pelo effect).
            void loadVariationsFor(catalog.id)
            return
        }

        // Procedimento: empilhar como item (multi-item, igual ao exame). Os
        // campos compartilhados (materiais, anestesista, exames atrelados)
        // só são carregados a partir do PRIMEIRO procedimento adicionado,
        // para não sobrescreverem o que já foi configurado a cada inclusão.
        if (serviceType === 'PROCEDURE') {
            const isFirstOrderItem = orderItems.length === 0
            setOrderItems(prev => [...prev, buildOrderItem(catalog)])
            setCatalogSearch('')
            // Carrega variações deste item (também coberto pelo effect).
            void loadVariationsFor(catalog.id)

            if (!isFirstOrderItem || !montandoDoZero) {
                // selectedCatalog permanece apontando para o item primário
                // (o primeiro da pilha) — não sobrescrever ao empilhar mais.
                //
                // Na edição para aqui também: o que vem abaixo é a montagem a
                // partir do catálogo (anestesista, materiais, exames do template),
                // e ela sobrescreveria o que o operador já tem em tela.
                return
            }

            if ((catalog as any).anesthetistFee) {
                setAnesthetistFee(
                    currencyFormat(
                        (catalog as any).anesthetistFee.toString(),
                        true
                    )
                )
                // O destinatário padrão vem junto com a taxa: quem opera sempre com o
                // mesmo anestesista não deveria escolhê-lo a cada cirurgia.
                if ((catalog as any).anesthetistRecipientKind) {
                    setAnesthetistRecipientKind(
                        (catalog as any).anesthetistRecipientKind
                    )
                    setAnesthetistRecipientId(
                        (catalog as any).anesthetistRecipientId ?? null
                    )
                }
            }
            const msRes = await getCatalogMedicalSupplies(catalog.id)
            if (msRes.success && msRes.result.length > 0) {
                setMedicalSupplies(
                    msRes.result.map(cms => ({
                        medicalSupplyId: cms.medicalSupplyId,
                        name: cms.medicalSupply.name,
                        type: cms.medicalSupply.type,
                        quantity: cms.quantity,
                        unitPrice: cms.unitPrice,
                    }))
                )
            } else {
                setMedicalSupplies([])
            }

            // Carregar exames do template
            const examsRes = await getCatalogExams(catalog.id)
            if (examsRes.success && examsRes.result.length > 0) {
                const templateExams = examsRes.result.map(ce => {
                    const itemPrice =
                        ce.examCatalog.sellingPrice ?? ce.examCatalog.price ?? 0
                    return {
                        catalogId: ce.examCatalogId,
                        name: ce.examCatalog.name,
                        catalogTypeName: ce.examCatalog.catalogType?.name || '',
                        itemPrice,
                        unitPrice: itemPrice,
                        quantity: ce.quantity,
                        complimentary: ce.complimentary,
                        notes: ce.notes || undefined,
                        priceSource: 'item' as const,
                        availablePartners: [],
                        availableProfessionals: [],
                    }
                })
                setOrderExamTemplates(templateExams)
                setOrderExams(templateExams)
            } else {
                setOrderExamTemplates([])
                setOrderExams([])
            }
        }
    }

    const handleClearCatalog = () => {
        setSelectedCatalog(null)
        setCatalogSearch('')
        setOrderItems([])
        setConsultationVariations([])
        setDiscount('')
        setProfessionalId('')
        if (!hidePartnerSelector) setPartnerId(null)
        setMedicalSupplies([])
        setAnesthetistFee('')
        setHospitalFee('')
        setHospitalFeeNotes('')
        setOrderExams([])
        setOrderExamTemplates([])
        setOrderConsultations([])
        if (!hospitalIdTouched) setHospitalId(null)
    }

    // === Import via IA (exame: PDF/imagem) ===
    const handleExamDocImport = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0]
        // Limpa o input para permitir reupload do mesmo arquivo
        if (examImportInputRef.current) {
            examImportInputRef.current.value = ''
        }
        if (!file) return
        await importExamFile(file)
    }

    /** Importa a lista de exames de um pedido (foto ou PDF). Também usada pelo Modo IA. */
    const importExamFile = async (file: File) => {
        setImportingExamDoc(true)
        try {
            const response = await importExamListFromDocument(file)
            if (!response.success || !response.result) {
                toast.error(response.error || 'Falha ao processar documento')
                return
            }
            const { matched, unmatched, rawExtracted, notes } = response.result

            if (rawExtracted.length === 0) {
                toast.warning(
                    notes || 'Não foi possível identificar exames no documento'
                )
                return
            }

            if (matched.length === 0) {
                toast.warning(
                    `${rawExtracted.length} exame(s) detectado(s), mas nenhum encontrado no catálogo`
                )
                return
            }

            // Adiciona ao examItems, deduplicando por catalogId
            setExamItems(prev => {
                const existingIds = new Set(prev.map(e => e.catalogId))
                const additions: ExamSelectedItem[] = matched
                    .filter(m => !existingIds.has(m.catalogId))
                    .map(m => ({
                        catalogId: m.catalogId,
                        name: m.name,
                        catalogTypeName: '',
                        unitPrice: m.unitPrice,
                        quantity: m.quantity,
                    }))
                return [...prev, ...additions]
            })

            toast.success(
                `${matched.length} de ${rawExtracted.length} exame(s) importado(s) com sucesso`
            )

            if (unmatched.length > 0) {
                const names = unmatched.map(u => u.name)
                setUnmatchedImports(prev => {
                    const set = new Set(prev)
                    names.forEach(n => set.add(n))
                    return Array.from(set)
                })
            }
        } catch (err: any) {
            toast.error(
                err?.response?.data?.message ||
                    err?.message ||
                    'Erro ao processar documento'
            )
        } finally {
            setImportingExamDoc(false)
        }
    }

    // Hospitais sugeridos pelo item do catálogo (união quando EXAM)
    const suggestedHospitals = useMemo(() => {
        const map = new Map<number, { id: number; name: string }>()
        const addFromCatalog = (cat: Catalog | null | undefined) => {
            if (!cat) return
            const list = (cat as any).hospitals as
                | {
                      hospitalId: number
                      hospital?: { id: number; name: string }
                  }[]
                | undefined
            list?.forEach(ch => {
                const id = ch.hospitalId ?? ch.hospital?.id
                if (id != null && !map.has(id)) {
                    map.set(id, {
                        id,
                        name: ch.hospital?.name || `Hospital #${id}`,
                    })
                }
            })
        }
        if (serviceType === 'EXAM') {
            examItems.forEach(item => {
                const cat = catalogsById[item.catalogId]
                addFromCatalog(cat)
            })
        } else if (serviceType === 'PROCEDURE' && orderItems.length > 0) {
            orderItems.forEach(item => {
                const cat = catalogsById[item.catalogId]
                addFromCatalog(cat)
            })
        } else {
            addFromCatalog(selectedCatalog)
        }
        return Array.from(map.values())
    }, [selectedCatalog, serviceType, examItems, orderItems, catalogsById])

    // Auto-popular hospitalId com o primeiro sugerido, caso o usuário ainda não tenha interagido.
    //
    // Só na CRIAÇÃO: numa edição, trocar o item do catálogo faria o hospital ser
    // sugerido (ou limpo, quando o item novo não sugere nenhum) sem ninguém pedir —
    // e o local da cirurgia é decisão do operador, não do cadastro.
    useEffect(() => {
        if (hospitalIdTouched || isEditing) return
        if (suggestedHospitals.length === 0) {
            if (hospitalId != null) setHospitalId(null)
            return
        }
        if (hospitalId == null) {
            setHospitalId(suggestedHospitals[0].id)
        }
    }, [suggestedHospitals, hospitalIdTouched, isEditing])

    /**
     * Consultas marcadas como "apenas orçamento" não podem ser agendadas, então nem
     * aparecem na busca — o operador não escolhe algo que o formulário depois recusa.
     *
     * Só em consultas. Exames e procedimentos `budgetOnly` continuam selecionáveis:
     * neles o item existente força o modo orçamento (ver `forceBudgetMode`), que é
     * um fluxo legítimo. A consulta não tem esse caminho.
     */
    const filteredCatalogs = useMemo(
        () =>
            serviceType === 'CONSULTATION'
                ? catalogs.filter(c => c.budgetOnly !== true)
                : catalogs,
        [catalogs, serviceType]
    )

    /**
     * Intermediação: consulta e exame são prestados por um parceiro, e é dele o
     * preço — sem parceiro escolhido não há a quem atribuir o item cadastrado.
     * PROCEDURE é feito internamente e não depende de parceiro.
     */
    const catalogNeedsPartner = isIntermediation && serviceType !== 'PROCEDURE'

    const QUICK_CREATE_CATEGORIES = ['CONSULTATION', 'EXAM', 'PROCEDURE']

    /** Cadastro rápido disponível (o bloqueio por parceiro é avisado na UI). */
    const canQuickCreateCatalog =
        quickCreateAllowed &&
        !isEditing &&
        catalogSearch.trim().length > 0 &&
        QUICK_CREATE_CATEGORIES.includes(serviceType) &&
        (!catalogNeedsPartner || !!partnerId)

    // === Preço ===
    // Preço base do item/catálogo (sem considerar parceiro/profissional)
    /**
     * Hospital já em uso neste atendimento.
     *
     * Um atendimento acontece em UM hospital: se ele já foi escolhido — no campo do
     * atendimento, na taxa do anestesista, num material ou num componente —, todo repasse
     * de tipo HOSPITAL se refere a esse mesmo lugar. Sem isto, o operador busca o mesmo
     * hospital de novo a cada linha, e basta um deslize para o repasse sair para um
     * hospital que não atendeu ninguém.
     *
     * É só um PADRÃO: o campo continua editável, para o caso raro de duas casas.
     */
    const hospitalInUseId = useMemo<number | null>(() => {
        if (hospitalId) return hospitalId
        if (anesthetistRecipientKind === 'HOSPITAL' && anesthetistRecipientId)
            return anesthetistRecipientId
        const noMaterial = medicalSupplies.find(
            ms => ms.recipientKind === 'HOSPITAL' && ms.recipientId
        )
        if (noMaterial?.recipientId) return noMaterial.recipientId
        const noComponente = [
            ...orderExams,
            ...orderConsultations,
        ].find(ex => ex.recipientKind === 'HOSPITAL' && ex.recipientId)
        return noComponente?.recipientId ?? null
    }, [
        hospitalId,
        anesthetistRecipientKind,
        anesthetistRecipientId,
        medicalSupplies,
        orderExams,
        orderConsultations,
    ])

    const orderExamsTotal = useMemo(
        () =>
            orderExams.reduce(
                (sum, e) =>
                    sum + (e.complimentary ? 0 : e.unitPrice * e.quantity),
                0
            ),
        [orderExams]
    )

    const orderConsultationsTotal = useMemo(
        () =>
            orderConsultations.reduce(
                (sum, e) =>
                    sum + (e.complimentary ? 0 : e.unitPrice * e.quantity),
                0
            ),
        [orderConsultations]
    )

    const medicalSuppliesTotal = useMemo(
        () =>
            medicalSupplies.reduce(
                (sum, ms) => sum + (ms.unitPrice || 0) * (ms.quantity || 1),
                0
            ),
        [medicalSupplies]
    )

    const basePrice = useMemo(() => {
        if (serviceType === 'EXAM') {
            const examsSum = examItems.reduce(
                (sum, item) => sum + item.unitPrice * item.quantity,
                0
            )
            return examsSum + medicalSuppliesTotal
        }
        if (serviceType === 'PROCEDURE' && orderItems.length > 0) {
            const ordersSum = orderItems.reduce(
                (sum, item) => sum + item.unitPrice * item.quantity,
                0
            )
            return (
                ordersSum +
                orderExamsTotal +
                orderConsultationsTotal +
                medicalSuppliesTotal
            )
        }
        /*if (serviceType === 'TELECONSULTATION') {
            return teleSellingPrice
                ? parseCurrencyToCents(teleSellingPrice)
                : telePrice
                  ? parseCurrencyToCents(telePrice)
                  : 0
        }*/
        if (!selectedCatalog) return 0
        // CONSULTA com variações: a soma das variações substitui o preço base.
        const catalogPrice =
            serviceType === 'CONSULTATION' && consultationVariations.length > 0
                ? consultationVariations.reduce(
                      (sum, v) => sum + v.unitPrice * v.quantity,
                      0
                  )
                : catalogBasePrice(selectedCatalog, user?.companyId)
        return (
            catalogPrice +
            orderExamsTotal +
            orderConsultationsTotal +
            medicalSuppliesTotal
        )
    }, [
        selectedCatalog,
        serviceType,
        examItems,
        orderItems,
        consultationVariations,
        telePrice,
        teleSellingPrice,
        orderExamsTotal,
        orderConsultationsTotal,
        medicalSuppliesTotal,
    ])

    // PROCEDIMENTO sem preço próprio (sem sellingPrice em CatalogCompanyPrice):
    // exige informar o valor de venda/markup na hora. Se qualquer item de procedimento
    // não tiver sellingPrice próprio, considera obrigatório.
    const requireSellingPrice = useMemo(() => {
        if (serviceType !== 'PROCEDURE') return false
        const cats =
            orderItems.length > 0
                ? orderItems
                      .map(it => catalogsById[it.catalogId])
                      .filter(Boolean)
                : selectedCatalog
                  ? [selectedCatalog]
                  : []
        if (cats.length === 0) return false // sem catálogo escolhido ainda
        return !cats.every(
            cat =>
                catalogCompanyPrice(cat as any, user?.companyId).sellingPrice !=
                null
        )
    }, [
        serviceType,
        orderItems,
        catalogsById,
        selectedCatalog,
        user?.companyId,
    ])

    /**
     * Teto de desconto do atendimento — espelha `catalog-max-discount.util.ts` do
     * backend, que é quem de fato recusa.
     *
     * Item sem teto configurado NÃO aceita desconto (antes, ausência de
     * `maxDiscount` liberava até 100%). Com vários itens vale o MENOR teto, então
     * um único item sem configuração zera o desconto do atendimento inteiro.
     * O teto segue a cascata do preço: parceiro > profissional > filial.
     */
    const maxAllowedDiscount = useMemo(() => {
        const ctx = {
            companyId: user?.companyId,
            partnerId: partnerId ?? null,
            professionalId: professionalId
                ? parseInt(professionalId, 10)
                : null,
        }

        const tetoDoItem = (
            catalogId: number,
            catalogVariationId?: number | null
        ): number => {
            if (catalogVariationId != null) {
                const variation = (variationsByCatalog[catalogId] || []).find(
                    v => v.id === catalogVariationId
                )
                if (variation) return variationMaxDiscount(variation, ctx)
            }
            const catalog = catalogsById[catalogId]
            if (!catalog) return NO_DISCOUNT_ALLOWED
            return cascadeMaxDiscount(catalog, ctx)
        }

        if (serviceType === 'EXAM' && examItems.length > 0) {
            return combineMaxDiscounts(
                examItems.map(item =>
                    tetoDoItem(item.catalogId, item.catalogVariationId)
                )
            )
        }

        if (serviceType === 'PROCEDURE' && orderItems.length > 0) {
            return combineMaxDiscounts(
                orderItems.map(item =>
                    tetoDoItem(item.catalogId, item.catalogVariationId)
                )
            )
        }

        if (!selectedCatalog) return NO_DISCOUNT_ALLOWED

        // Consulta com variações escolhidas: são elas que são precificadas.
        if (consultationVariations.length > 0) {
            return combineMaxDiscounts(
                consultationVariations.map(v =>
                    tetoDoItem(selectedCatalog.id, v.catalogVariationId)
                )
            )
        }

        return cascadeMaxDiscount(selectedCatalog, ctx)
    }, [
        selectedCatalog,
        user?.companyId,
        partnerId,
        professionalId,
        serviceType,
        examItems,
        orderItems,
        consultationVariations,
        catalogsById,
        variationsByCatalog,
    ])

    /**
     * Editar o valor final à mão existe em PROCEDIMENTO, orçamento ou não.
     *
     * Era só no orçamento, o que deixava o procedimento já agendado sem como fechar
     * um valor negociado — e a negociação de cirurgia não acaba na aprovação do
     * orçamento (equipe maior, material que o item não previa, hospital mais caro).
     * Quem limita é o backend, que recusa descer abaixo do teto de desconto do item
     * (`assertPriceWithinDiscountLimit`) e aceita subir livre.
     *
     * Exame e consulta continuam valendo o preço do catálogo, com desconto dentro do
     * limite — ali o valor não se digita.
     */
    const canEditFinalPrice = serviceType === 'PROCEDURE'

    // === Profissionais e parceiros do catálogo ===
    // Parceiros/profissionais EFETIVOS de um item empilhável: quando ele tem uma
    // variação escolhida e a variação tem vínculos próprios, são os DELA — porque é a
    // variação que o backend precifica (`resolveVariationsForPricing` casa o parceiro
    // global com o CatalogPartner da variação). Sem variação (ou variação sem vínculo),
    // usa os do item pai. Assim o seletor global oferece o parceiro/profissional certo
    // para cobrar o preço da variação, e a escolha vira a do agendamento.
    const effectiveProfessionalsFor = (item: {
        catalogId: number
        catalogVariationId?: number
    }): any[] => {
        if (item.catalogVariationId != null) {
            const v = (variationsByCatalog[item.catalogId] || []).find(
                x => x.id === item.catalogVariationId
            )
            if (v?.professionals?.length) return v.professionals
        }
        return catalogsById[item.catalogId]?.professionals || []
    }

    const effectivePartnersFor = (item: {
        catalogId: number
        catalogVariationId?: number
    }): any[] => {
        if (item.catalogVariationId != null) {
            const v = (variationsByCatalog[item.catalogId] || []).find(
                x => x.id === item.catalogVariationId
            )
            if (v?.partners?.length) return v.partners
        }
        return catalogsById[item.catalogId]?.partners || []
    }

    const catalogActiveProfessionals = useMemo(() => {
        const dedupeProfs = (profs: any[]) =>
            profs.filter(
                (p: any, i, arr) =>
                    arr.findIndex(
                        (x: any) => x.professionalId === p.professionalId
                    ) === i
            )
        if (serviceType === 'EXAM') {
            const profs = examItems.flatMap(item =>
                effectiveProfessionalsFor(item).filter(
                    (p: any) => p.active !== false
                )
            )
            return dedupeProfs(profs)
        }
        if (serviceType === 'PROCEDURE' && orderItems.length > 0) {
            const profs = orderItems.flatMap(item =>
                effectiveProfessionalsFor(item).filter(
                    (p: any) => p.active !== false
                )
            )
            return dedupeProfs(profs)
        }
        // CONSULTA com variação escolhida: os profissionais efetivos vêm das
        // variações (Catalog filho com vínculos próprios), como em exame/procedimento.
        if (
            serviceType === 'CONSULTATION' &&
            selectedCatalog &&
            consultationVariations.length > 0
        ) {
            const vars = variationsByCatalog[selectedCatalog.id] || []
            const profs = consultationVariations.flatMap(cv => {
                const v = vars.find(x => x.id === cv.catalogVariationId)
                const own = v?.professionals || []
                return (
                    own.length ? own : selectedCatalog.professionals || []
                ).filter((p: any) => p.active !== false)
            })
            return dedupeProfs(profs)
        }
        return (selectedCatalog?.professionals || []).filter(
            (p: any) => p.active !== false
        )
    }, [
        selectedCatalog,
        serviceType,
        examItems,
        orderItems,
        consultationVariations,
        catalogsById,
        variationsByCatalog,
    ])

    const catalogActivePartners = useMemo(() => {
        // Filtra tanto a junção inativa (CatalogPartner.active) quanto o
        // parceiro globalmente inativo (Partner.active) — este ultimo vem em
        // p.partner.active do payload do catalogo.
        const isActive = (p: any) =>
            p.active !== false && p.partner?.active !== false
        const intersectPartners = (
            items: { catalogId: number; catalogVariationId?: number }[]
        ): any[] => {
            // Interseção: parceiro deve existir em TODOS os itens. Para um item com
            // variação escolhida, o conjunto vem da variação (ver effectivePartnersFor).
            const partnerSets = items.map(item =>
                effectivePartnersFor(item).filter(isActive)
            )
            if (partnerSets.length === 0) return []
            const firstSet = partnerSets[0]
            return firstSet.filter((p: any) =>
                partnerSets.every(set =>
                    set.some((sp: any) => sp.partnerId === p.partnerId)
                )
            )
        }
        if (serviceType === 'EXAM' && examItems.length > 0) {
            return intersectPartners(examItems)
        }
        if (serviceType === 'PROCEDURE' && orderItems.length > 0) {
            return intersectPartners(orderItems)
        }
        // CONSULTA com variação escolhida: o parceiro precisa existir nas variações
        // (interseção). Assim o seletor global só oferece os parceiros da variação —
        // escolher um vira o parceiro padrão do item; para usar um parceiro de fora,
        // o operador precisa remover a variação. Variação sem parceiro próprio cai no
        // parceiro do item pai.
        if (
            serviceType === 'CONSULTATION' &&
            selectedCatalog &&
            consultationVariations.length > 0
        ) {
            const vars = variationsByCatalog[selectedCatalog.id] || []
            const sets = consultationVariations.map(cv => {
                const v = vars.find(x => x.id === cv.catalogVariationId)
                const own = v?.partners || []
                return (
                    own.length ? own : selectedCatalog.partners || []
                ).filter(isActive)
            })
            if (sets.length === 0)
                return (selectedCatalog.partners || []).filter(isActive)
            const first = sets[0]
            return first.filter((p: any) =>
                sets.every(s =>
                    s.some((sp: any) => sp.partnerId === p.partnerId)
                )
            )
        }
        return (selectedCatalog?.partners || []).filter(isActive)
    }, [
        selectedCatalog,
        serviceType,
        examItems,
        orderItems,
        consultationVariations,
        catalogsById,
        variationsByCatalog,
    ])

    // Lista de parceiros para o PartnerSelector: a interseção do catálogo MAIS o
    // parceiro atualmente selecionado caso ele seja PARCIAL (atende só parte dos
    // itens, então não está na interseção). Sem isso o seletor não conseguiria
    // exibir o parceiro aplicado pelo modal de comparação. Busca o objeto do
    // parceiro nos próprios itens (onde ele existe, pois atende ao menos um).
    const catalogPartnersForSelector = useMemo(() => {
        if (
            !partnerId ||
            catalogActivePartners.some((cp: any) => cp.partnerId === partnerId)
        ) {
            return catalogActivePartners
        }
        // Procura o parceiro parcial nos vínculos EFETIVOS (variação quando houver),
        // não só no item pai — senão o parceiro que só existe na variação sumiria.
        const sourceSets: any[][] =
            serviceType === 'EXAM'
                ? examItems.map(item => effectivePartnersFor(item))
                : serviceType === 'PROCEDURE' && orderItems.length > 0
                  ? orderItems.map(item => effectivePartnersFor(item))
                  : serviceType === 'CONSULTATION' &&
                      selectedCatalog &&
                      consultationVariations.length > 0
                    ? // Consulta com variação: só considera parceiros DA variação —
                      // um parceiro só do item pai não é reofertado enquanto a
                      // variação estiver selecionada.
                      consultationVariations.map(cv => {
                          const v = (
                              variationsByCatalog[selectedCatalog.id] || []
                          ).find(x => x.id === cv.catalogVariationId)
                          return v?.partners?.length
                              ? v.partners
                              : selectedCatalog.partners || []
                      })
                    : [selectedCatalog?.partners || []]
        for (const set of sourceSets) {
            const cp = (set || []).find((p: any) => p.partnerId === partnerId)
            if (cp) return [...catalogActivePartners, cp]
        }
        return catalogActivePartners
    }, [
        catalogActivePartners,
        partnerId,
        serviceType,
        examItems,
        orderItems,
        consultationVariations,
        catalogsById,
        selectedCatalog,
        variationsByCatalog,
    ])

    // Modo IA: o usuário de IA monta exame, consulta ou procedimento não cirúrgico por
    // estes mesmos handlers, à vista do supervisor (docs/ai-user.md, fase 2). Só em criação.
    const priceOfVariation = (variation: CatalogVariation) =>
        variationDisplayPrice(
            variation,
            partnerId,
            professionalId ? Number(professionalId) : null
        )
    // A consulta é um item só, com a variação escolhida (a tela permite várias; a IA usa uma).
    const aiSchedulingLines: SchedulingAiLine[] =
        serviceType === 'EXAM'
            ? examItems
            : serviceType === 'PROCEDURE'
              ? orderItems
              : serviceType === 'CONSULTATION' && selectedCatalog
                ? [
                      {
                          catalogId: selectedCatalog.id,
                          name: selectedCatalog.name,
                          catalogTypeName: selectedCatalog.catalogType?.name || '',
                          quantity: 1,
                          unitPrice: consultationVariations[0]
                              ? consultationVariations[0].unitPrice
                              : catalogBasePrice(selectedCatalog, user?.companyId),
                          catalogVariationId: consultationVariations[0]?.catalogVariationId,
                          variationName: consultationVariations[0]?.name,
                      },
                  ]
                : []
    // Mesma busca do campo, sem o recorte de tipo: a IA acha a consulta mesmo com a tela
    // em exame e abre no tipo do item. Os resultados entram no mapa de catálogos.
    const searchAllCatalogsForAi = async (search: string): Promise<Catalog[]> => {
        if (!user?.companyId) return []
        const response = await getCatalogsByCompany(user.companyId, {
            // Só o que a IA orça: produto e pacote ocupariam os 30 resultados.
            categories: [...AI_SCHEDULING_SERVICE_TYPES],
            activeOnly: true,
            schedulableOnly: true,
            page: 1,
            limit: 30,
            search: search.trim() || undefined,
        })
        if (!response.success) return []
        const list = response.result || []
        mergeCatalogs(list)
        return list
    }
    /**
     * "Gerar cobrança depois": nenhuma cobrança nasce agora.
     *
     * ⚠️ NÃO marca `paymentChoiceTouchedRef`, ao contrário de todas as outras escolhas
     * do bloco de pagamento: aquele ref é o que AUTORIZA a edição a emitir a cobrança que
     * falta, e marcá-lo faria "cobrar depois" emitir na hora — exatamente o oposto do que
     * o operador pediu.
     */
    /** O que o último envio criou (ver `finishCreate`), para a ponte do Modo IA. */
    const aiLastCreatedRef = useRef<{ billingId: number | null; detailPath: string | null } | null>(null)

    /**
     * "Outra forma ou dividir o pagamento": escolha deliberada de decidir na tela de
     * pagamento. Conta como mexer no pagamento e autoriza a edição a criar a cobrança —
     * que nasce sem forma definida e leva o operador à tela de cobrança, como sempre.
     */
    const handlePaymentCustom = () => {
        paymentChoiceTouchedRef.current = true
        // "Decidir depois" também sai do flex.
        setFlexRequested(false)
        setDeferBilling(false)
        setPaymentMethod('')
        setInstallmentCount(null)
    }

    const handlePaymentDefer = () => {
        setDeferBilling(true)
        setFlexRequested(false)
        setPaymentMethod('')
        setInstallmentCount(null)
    }

    /** Forma de pagamento escolhida no bloco de pagamento (o mesmo gesto serve à IA). */
    const handlePaymentChoice = (method: string, installments: number) => {
        paymentChoiceTouchedRef.current = true
        // Escolher uma forma é querer a cobrança AGORA: desfaz o "cobrar depois", como já
        // desfaz o flex logo abaixo.
        setDeferBilling(false)
        // Escolher uma forma DESFAZ o flex: sem isto a escolha dele era irreversível na
        // tela — o método clicado ficava marcado, o flex continuava selecionado por baixo,
        // e o salvamento descartava a forma em silêncio.
        setFlexRequested(false)
        setPaymentMethod(method)
        // Só o cartão parcela; nas demais formas o número guardado seria ruído no checkout.
        setInstallmentCount(method === 'CREDIT_CARD' && installments > 1 ? installments : null)
    }

    useSchedulingAiBridge({
        enabled:
            !isEditing &&
            !isBudgetMode &&
            (AI_SCHEDULING_SERVICE_TYPES as readonly string[]).includes(serviceType),
        serviceType,
        setServiceType: handleServiceTypeChange,
        clientId,
        clientName,
        setClientId,
        lines: aiSchedulingLines,
        // Os mesmos gestos da tabela de itens (`SelectedItemsTable`, `VariationRowControls`)
        // e, na consulta, do "limpar" e do seletor de variações.
        removeLine: line => {
            if (serviceType === 'EXAM') {
                setExamItems(prev => prev.filter((_, index) => index !== line))
            } else if (serviceType === 'PROCEDURE') {
                setOrderItems(prev => prev.filter((_, index) => index !== line))
            } else {
                handleClearCatalog()
            }
        },
        chooseLineVariation: (line, variation) => {
            const patch = {
                catalogVariationId: variation.id,
                variationName: variation.name,
                unitPrice: priceOfVariation(variation),
            }
            if (serviceType === 'EXAM') {
                setExamItems(prev => prev.map((entry, index) => (index === line ? { ...entry, ...patch } : entry)))
            } else if (serviceType === 'PROCEDURE') {
                setOrderItems(prev => prev.map((entry, index) => (index === line ? { ...entry, ...patch } : entry)))
            } else {
                setConsultationVariations([
                    { catalogVariationId: variation.id, quantity: 1, name: variation.name, unitPrice: patch.unitPrice },
                ])
            }
        },
        variationsByCatalog,
        catalogsById,
        searchAllCatalogs: searchAllCatalogsForAi,
        selectCatalog: handleCatalogSelect,
        importExamFile,
        importingExamDoc,
        unmatchedImports,
        variationPrice: priceOfVariation,
        activePartners: catalogActivePartners,
        effectivePartnersFor,
        // A linha com variação guarda o preço do executante ESCOLHIDO (antes de escolher,
        // o da filial, que muitas vezes não existe). O total de cada parceiro tem de usar
        // o preço da variação COM AQUELE parceiro, pela mesma cascata da tela.
        partnerTotalFor: (id, items) =>
            getPartnerTotalForItems(
                id,
                items.map(item => {
                    if (!item.catalogVariationId) return item
                    const variation = (
                        variationsByCatalog[item.catalogId] || []
                    ).find(entry => entry.id === item.catalogVariationId)
                    return variation
                        ? {
                              ...item,
                              unitPrice: variationDisplayPrice(
                                  variation,
                                  id,
                                  professionalId ? Number(professionalId) : null
                              ),
                          }
                        : item
                }),
                Object.values(catalogsById)
            ),
        partnerId,
        setPartnerId,
        professionals: catalogActiveProfessionals,
        professionalId: professionalId ? Number(professionalId) : null,
        setProfessionalId: id => setProfessionalId(id ? String(id) : ''),
        pricingReady: !loadingData && !loadingCatalogs && !catalogsHydrating,
        chargedTotal: chargedSummaryPrice,
        appliedCoupon,
        applyCoupon: handleApplyCoupon,
        applyingCoupon,
        grouping: agrupando && billingTargetId != null
            ? { billingId: billingTargetId, count: groupedCount, amount: groupedAmount }
            : null,
        chargeSeparately: () => setAgrupamentoDispensado(true),
        addressConfirmed,
        missingPrices: itemsMissingExecutantPrice.map(
            entry => `${entry.name}: falta ${entry.missing} para ${entry.executant}`
        ),
        serviceAddress,
        setServiceAddress,
        paymentMethod,
        installmentCount,
        deferBilling,
        choosePayment: handlePaymentChoice,
        payLater: handlePaymentDefer,
        payOnBillingScreen: handlePaymentCustom,
        setPipelineId,
        submitting: loading,
        // O mesmo envio do botão "Agendar" ou, com `addAnother`, do "Adicionar outro" (que
        // marca o ref antes do submit, como o botão). Devolve o que o `finishCreate` registrou.
        submit: async addAnother => {
            aiLastCreatedRef.current = null
            addAnotherRef.current = addAnother
            await handleSubmit({ preventDefault: () => {} } as React.FormEvent)
            // Envio recusado pela validação deixaria o ref ligado, e o próximo "Agendar"
            // de uma pessoa cairia no fluxo do "Adicionar outro".
            addAnotherRef.current = false
            return aiLastCreatedRef.current
        },
    })

    const hasCatalogSelected =
        serviceType === 'EXAM'
            ? examItems.length > 0
            : serviceType === 'PROCEDURE'
              ? orderItems.length > 0
              : !!selectedCatalog

    /**
     * Linha da comparação de parceiros, respeitando a VARIAÇÃO escolhida.
     *
     * ⚠️ Sem isto o modal comparava sempre o catálogo **pai**, e quem tem preço numa
     * variação-como-item é o vínculo da variação (`docs/catalog-variation-as-item-redesign.md`)
     * — o operador via a cotação dos parceiros do item principal, que não é a que ele
     * vai pagar, ou coluna vazia quando só a variação tem vínculo.
     *
     * A variação **é** um `Catalog` (`isVariation`), então o `id` dela serve de chave e
     * o backend do PDF a resolve igual. Usar o id da variação também evita a colisão em
     * `matrix[catalogId]` do modal quando duas linhas saem do mesmo item pai com
     * variações diferentes — antes, uma sobrescrevia a outra.
     *
     * O formato de preço próprio difere: o item tem `sellingPrice`/`price` no topo, a
     * variação tem `companyPrices[]`. Aqui é achatado para o que o modal espera.
     */
    const comparisonItemFor = (item: {
        catalogId: number
        catalogVariationId?: number
        name: string
        quantity?: number
    }) => {
        const parent = catalogsById[item.catalogId]
        const variation =
            item.catalogVariationId != null
                ? (variationsByCatalog[item.catalogId] || []).find(
                      v => v.id === item.catalogVariationId
                  )
                : undefined
        if (!variation && !parent) return null

        if (variation) {
            const cp = (variation.companyPrices || []).find(
                p => p.companyId === user?.companyId
            )
            return {
                catalogId: variation.id,
                name: item.name,
                quantity: item.quantity || 1,
                complimentary: false,
                catalog: {
                    id: variation.id,
                    sellingPrice: cp?.sellingPrice ?? null,
                    price: cp?.price ?? null,
                    partners: variation.partners || [],
                } as any,
            }
        }

        return {
            catalogId: item.catalogId,
            name: item.name,
            quantity: item.quantity || 1,
            complimentary: false,
            catalog: parent as any,
        }
    }

    const comparisonItems = useMemo(() => {
        if (serviceType === 'EXAM') {
            return examItems.map(comparisonItemFor).filter(Boolean) as any[]
        }
        if (serviceType === 'CONSULTATION' && selectedCatalog) {
            return [
                {
                    catalogId: selectedCatalog.id,
                    name: selectedCatalog.name,
                    quantity: 1,
                    complimentary: false,
                    catalog: selectedCatalog as any,
                },
            ]
        }
        return []
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        serviceType,
        examItems,
        selectedCatalog,
        catalogsById,
        variationsByCatalog,
        user?.companyId,
    ])

    const canCompareCatalog =
        (serviceType === 'EXAM' || serviceType === 'CONSULTATION') &&
        comparisonItems.length > 0
    //const isTeleconsultation = serviceType === 'TELECONSULTATION'

    const isTeleconsultation = false

    /**
     * Consulta de RETORNO que não deve gerar cobrança.
     *
     * O retorno é a continuação da consulta já paga — o paciente volta para
     * mostrar o exame ou ouvir o resultado —, e a configuração financeira
     * ("Cobrar consulta de retorno", desligada por padrão) diz se a casa cobra
     * por ele. Sem isto, marcar "retorno" era só um rótulo clínico: a cobrança
     * nascia igual à da primeira consulta e alguém precisava cancelá-la à mão.
     *
     * O caminho usado é o mesmo do "Gerar cobrança depois" (`deferBilling`): o
     * valor continua gravado no atendimento e a emissão fica disponível pelo
     * "Gerar cobrança" da tela do atendimento — nada impede cobrar um retorno
     * excepcional.
     *
     * Orçamento fica de fora porque ali nada é cobrado até alguém aprovar, e a
     * edição de um atendimento cuja cobrança JÁ existe também: ali não há emissão
     * a suprimir, e limpar a forma escondia da tela o que o paciente combinou.
     */
    const returnWithoutBilling =
        serviceType === 'CONSULTATION' &&
        isReturn &&
        !chargeReturnConsultation &&
        !isBudgetMode &&
        (!isEditing || hasNoEffectiveBilling(existingBilling))

    /**
     * O "cobrar depois" ligado foi decisão do RETORNO, não do operador.
     *
     * Guarda quem ligou para poder desligar ao desmarcar o retorno, sem apagar a
     * escolha que o operador tenha feito por conta própria no bloco de pagamento.
     */
    const deferByReturnRef = useRef(false)

    useEffect(() => {
        if (returnWithoutBilling) {
            deferByReturnRef.current = true
            setDeferBilling(true)
            // Retorno sem cobrança não tem forma de pagamento nem parcela: mandar
            // o que ficou no estado gravaria um método numa cobrança que não nasce.
            setPaymentMethod('')
            setInstallmentCount(null)
            setFlexRequested(false)
            return
        }
        if (deferByReturnRef.current) {
            deferByReturnRef.current = false
            setDeferBilling(false)
        }
    }, [returnWithoutBilling])

    // === Submit ===

    /**
     * O que aconteceu com o dinheiro quando o valor do atendimento mudou numa edição.
     *
     * Editar valor de atendimento com cobrança PAGA era proibido — não dava para
     * acrescentar um exame nem corrigir um preço. Agora a diferença é liquidada: para mais,
     * o backend cria uma cobrança COMPLEMENTAR e o operador é levado ao pagamento dela
     * (mesmo destino de quando o agendamento é criado); para menos, abre uma solicitação de
     * ESTORNO parcial, que um operador financeiro aprova.
     *
     * Devolve `true` quando já navegou (cobrança a pagar), para o caller não navegar de novo.
     */
    const handleBillingAdjustment = (
        result: any,
        fallbackPath: string
    ): boolean => {
        const adj = result?.billingAdjustment
        if (!adj || adj.action === 'NONE' || adj.action === 'UPDATED')
            return false

        // Atendimento que estava sem cobrança e ganhou uma nesta edição. O destino é o
        // mesmo da criação: com a forma de pagamento já fechada, a cobrança nasce
        // completa e o operador segue para o detalhe; sem forma definida, ele vai à tela
        // de cobrança — que é onde existem dinheiro, maquininha e o split.
        if (adj.action === 'CREATED' && adj.billingId) {
            if (sentPaymentMethodRef.current) {
                toast.success('Cobrança gerada para este atendimento.')
                navigate(fallbackPath)
                return true
            }
            navigate(`/financial/billings/${adj.billingId}/edit?from=schedule`)
            return true
        }

        if (adj.action === 'EXTRA_CHARGE' && adj.billingId) {
            toast.info(
                'O valor aumentou. Foi gerada uma cobrança complementar da diferença.'
            )
            navigate(`/financial/billings/${adj.billingId}/edit?from=schedule`)
            return true
        }

        if (adj.action === 'REFUND_REQUESTED') {
            toast.success(
                'O valor diminuiu. A solicitação de estorno da diferença foi enviada para aprovação de um operador financeiro.'
            )
            navigate(fallbackPath)
            return true
        }

        return false
    }

    /**
     * "Adicionar outro" foi o botão clicado? Marcado antes do submit e lido no fim.
     * É `ref` e não estado: precisa valer JÁ neste envio, sem esperar re-render.
     */
    const addAnotherRef = useRef(false)

    /**
     * Forma de pagamento a gravar na cobrança deste atendimento.
     *
     * Devolve **vazio** enquanto o carrinho está aberto — e é isso que faz "Adicionar
     * outro" voltar a funcionar. O `attachAppointmentToBilling` recusa somar num
     * destino que já tem `paymentMethod` ("o operador já levou esta cobrança ao
     * caixa"), e quem chama trata a recusa criando **cobrança própria**: em silêncio, o
     * paciente saía com uma cobrança por atendimento. Medido em 2026-08-02: um paciente
     * com três cobranças separadas no mesmo dia, todas com método definido.
     *
     * Dois casos param o envio:
     *  - `addAnotherRef` — o operador clicou em "Adicionar outro"; o total ainda vai
     *    crescer, então não há o que fechar;
     *  - `agrupando` na CRIAÇÃO — este atendimento soma numa cobrança já aberta, cujo
     *    pagamento é decidido no fim, na tela da cobrança.
     *
     * Na EDIÇÃO continua indo: ali `agrupando` significa a cobrança do próprio
     * atendimento, e é assim que se troca o parcelamento sem sair da tela.
     */
    /**
     * A forma que ESTA submissão realmente mandou, para o `finishCreate` decidir o
     * desvio sem reavaliar as condições (que dependem de `addAnotherRef` e de
     * `agrupando`, ambos sujeitos a mudar entre o envio e a resposta).
     */
    const sentPaymentMethodRef = useRef<string | undefined>(undefined)

    /**
     * O operador mexeu no bloco de pagamento NESTA edição?
     *
     * É o que autoriza a edição a criar a cobrança que faltava (`generateBilling`).
     * Precisa ser um sinal explícito: a mesma tela edita um atendimento só para
     * corrigir a data, e ali nada de dinheiro pode acontecer. Também não serve olhar o
     * `paymentMethod`: escolher "decidir na tela de pagamento" o deixa VAZIO de
     * propósito, o mesmo valor de quem não escolheu nada.
     *
     * `ref` e não estado: vale já neste envio, sem esperar re-render.
     */
    const paymentChoiceTouchedRef = useRef(false)

    /**
     * O operador escolheu (ou retirou) o CONVÊNIO nesta edição?
     *
     * Vale como gesto financeiro pelo mesmo motivo que a forma de pagamento: pôr o
     * convênio num atendimento sem cobrança é dizer quem paga, e a cobrança do
     * convênio nasce sem link, sem taxa e fora da régua do paciente. Sem este
     * gatilho o operador escolheria o convênio, salvaria, e nada seria faturado —
     * o bloco de pagamento nem aparece quando quem paga é a operadora.
     *
     * `ref` e não estado: vale já neste envio, sem esperar re-render.
     */
    const healthPlanChoiceTouchedRef = useRef(false)

    /**
     * O convênio está TRAVADO nesta edição.
     *
     * Convênio não é rótulo: ele decide o PAGADOR da cobrança (sem link, sem
     * gateway, sem régua de inadimplência em cima do paciente). Essa decisão é
     * tomada quando a cobrança nasce e não é reescrita depois — o backend recusa
     * trocar o pagador de uma cobrança existente. Deixar o campo editável aqui
     * mostraria "Convênio X" na tela enquanto o paciente continuaria recebendo a
     * cobrança: mudança cosmética, e ilusória.
     *
     * O critério é o mesmo do backend (`hasNoEffectiveBilling`): cobrança que
     * morreu sem deixar dinheiro retido não trava — é justamente por aí que se
     * corrige o pagador (cancelar a cobrança e gerá-la de novo).
     */
    const healthPlanLocked = isEditing && !hasNoEffectiveBilling(existingBilling)

    /**
     * Autoriza a criação da cobrança neste envio.
     *
     * Só na EDIÇÃO: na criação a cobrança nasce junto com o atendimento, sem gatilho.
     * Orçamento fica de fora — nada é cobrado até alguém aprovar. E com o carrinho
     * aberto ("Adicionar outro") também não: o total ainda vai crescer.
     */
    const billingCreationFieldForSubmit = (): { generateBilling?: boolean } =>
        isEditing &&
        !isBudgetMode &&
        // "Cobrar depois" é o pedido explícito de NÃO emitir. Sem este recorte, escolher
        // o convênio na mesma edição (que também autoriza a emissão) faria a cobrança
        // nascer assim mesmo.
        !deferBilling &&
        !addAnotherRef.current &&
        (paymentChoiceTouchedRef.current || healthPlanChoiceTouchedRef.current)
            ? { generateBilling: true }
            : {}

    /**
     * Manda o "cobrar depois" ao backend — só na CRIAÇÃO.
     *
     * Na edição não existe cobrança a suprimir: ali a emissão já é opcional e depende do
     * `generateBilling`, que o helper acima não envia quando esta opção está marcada.
     *
     * Orçamento fica de fora porque nada é cobrado até alguém aprovar, e o carrinho
     * ("Adicionar outro") também: ali a cobrança já espera o total dos próximos.
     */
    const deferBillingFieldForSubmit = (): { deferBilling?: boolean } =>
        !isEditing && deferBilling && !isBudgetMode && !addAnotherRef.current
            ? { deferBilling: true }
            : {}

    const paymentFieldsForSubmit = (): {
        paymentMethod?: string
        installmentCount?: number
    } => {
        const fields = ((): {
            paymentMethod?: string
            installmentCount?: number
        } => {
            // Orçamento não tem forma de pagamento: nada é cobrado até alguém aprovar, e
            // é a aprovação que decide como se paga (fluxo normal ou flex). A tela já não
            // oferece a escolha, mas o estado pode ter ficado preenchido de antes de o
            // operador ligar o modo orçamento.
            if (isBudgetMode) return {}
            // Flex: a forma é escolhida pelo paciente em cada pagamento avulso do
            // portal. A tela não oferece a escolha; mandar o que ficou no estado
            // gravaria um método que ninguém acertou.
            if (flexPayment) return {}
            // Acabou de escolher o flex nesta edição: mesma razão do de cima, com o
            // registro ainda gravado como normal.
            if (flexRequested) return {}
            // Cobrar depois: não há cobrança nascendo, então não há forma a gravar. Mandar
            // o método aqui faria o `finishCreate` acreditar que o pagamento foi resolvido.
            if (deferBilling) return {}
            if (addAnotherRef.current || (!isEditing && agrupando)) return {}
            return {
                paymentMethod: paymentMethod || undefined,
                installmentCount: installmentCount ?? undefined,
            }
        })()
        sentPaymentMethodRef.current = fields.paymentMethod
        return fields
    }

    /**
     * Deixa o formulário pronto para o PRÓXIMO atendimento do mesmo paciente.
     *
     * Mantém paciente e data — é a mesma visita. Limpa o que é do atendimento
     * (item do catálogo, executante, valores, local) e já aponta a cobrança que
     * acabou de nascer como destino, para o próximo ser somado nela em vez de
     * gerar outro link de pagamento.
     */
    const prepareNextAppointment = (billingId?: number) => {
        handleClearCatalog()
        setExamItems([])
        setConsultTitle('')
        setNotes('')
        setDiagnosis('')
        setTreatmentPlan('')
        setPreInstructions('')
        setPostInstructions('')
        setTriageId('')
        setIsReturn(false)
        setOverridePrice(null)
        setOrderCostPrice(null)
        setOrderMarkup(null)
        setDuration(null)
        setRoomId(null)
        setArrivalOrder(false)
        setHealthPlanId(null)
        // O próximo atendimento pode ser em outro lugar: o local volta a exigir
        // confirmação, em vez de herdar o endereço do parceiro anterior.
        setServiceAddress(null)

        // O conteúdo rola dentro do container do layout, não na janela — por isso
        // `scrollIntoView` (que sobe até o ancestral rolável certo) e não
        // `window.scrollTo`, que aqui não faria nada.
        document
            .getElementById('secao-atendimento')
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' })

        // A dispensa valia para o atendimento que acabou de ser salvo ("este aqui
        // cobra à parte"). A partir do próximo, a cobrança mais recente volta a ser
        // o destino natural.
        setAgrupamentoDispensado(false)
        void confirmarDestinoDaSoma(billingId)
    }

    /**
     * Depois de salvar, pergunta ao servidor em que pé está o grupo.
     *
     * Reusa a MESMA detecção da seleção do paciente, de propósito: um caminho só
     * para descobrir o agrupamento, seja ao escolher o paciente, ao voltar do
     * almoço ou logo após salvar. Se a cobrança recém-criada não puder receber
     * soma, isso aparece aqui — antes de o operador agendar mais três.
     */
    const confirmarDestinoDaSoma = async (billingId?: number) => {
        const alvo = await detectarAgrupamento(clientId)
        if (!alvo) {
            toast.info(`Agende o próximo atendimento deste ${clientLabel}.`)
            return
        }
        if (billingId && alvo.id !== billingId) {
            toast.warning(
                'A cobrança recém-criada não aceita soma. O próximo atendimento vai para a cobrança #' +
                    alvo.id +
                    '.'
            )
            return
        }
        toast.info('Agende o próximo — vai somar na mesma cobrança.')
    }

    /**
     * O que fazer depois de criar o atendimento: seguir para o pagamento (o fluxo
     * de sempre) ou ficar no formulário para agendar mais um.
     *
     * **Com a forma de pagamento já fechada na tela, não há desvio para a cobrança.**
     * O motivo do desvio era um só: a cobrança nascia sem método, e alguém precisava
     * defini-lo antes de o link poder ser enviado. Escolhida a forma aqui, ela nasce
     * completa — PIX com QR, cartão com o parcelamento combinado — e o operador vai
     * direto ao detalhe do atendimento, que é onde ele quer estar.
     *
     * ⚠️ Quem decide isso é o que **foi enviado** (`sentPaymentMethodRef`), não o
     * `paymentMethod` do estado. No carrinho ("Adicionar outro") e no orçamento a tela
     * pode ter o método preenchido e **não** enviá-lo de propósito — o total ainda vai
     * crescer. Olhando o estado, o último atendimento do carrinho ia para o detalhe
     * como se estivesse tudo resolvido, e a cobrança ficava sem forma, sem link e sem
     * ninguém sabendo: o operador achava que havia terminado.
     */
    const finishCreate = (
        billingId: number | null | undefined,
        fallbackPath: string,
        detailPath?: string
    ) => {
        // O Modo IA lê daqui o que acabou de ser criado: a tela navega e sai do ar logo abaixo.
        aiLastCreatedRef.current = { billingId: billingId ?? null, detailPath: detailPath ?? null }
        if (addAnotherRef.current) {
            addAnotherRef.current = false
            prepareNextAppointment(billingId ?? undefined)
            return
        }
        if (billingId && sentPaymentMethodRef.current) {
            navigate(detailPath || fallbackPath)
            return
        }
        if (billingId) {
            navigate(`/financial/billings/${billingId}/edit?from=schedule`)
            return
        }
        /**
         * Cobrança adiada: não há cobrança para onde desviar, e a listagem esconderia o
         * que o operador precisa ver — é no DETALHE que fica o "Gerar cobrança", que é
         * como esta venda vai ser cobrada no dia. Terminar na lista faria parecer que
         * algo se perdeu.
         */
        if (deferBilling && detailPath) {
            // Dito uma vez, aqui, e não nos cinco pontos de envio: sem o aviso o operador
            // chega ao detalhe procurando a cobrança que ele mesmo pediu para adiar.
            //
            // O retorno tem aviso próprio: ali ninguém pediu para adiar nada — foi a
            // configuração financeira que decidiu não cobrar —, e mandar "quando for
            // cobrar, use Gerar cobrança" soa como tarefa pendente numa consulta que a
            // casa não cobra.
            toast.info(
                returnWithoutBilling
                    ? 'Consulta de retorno agendada sem cobrança, conforme a configuração financeira.'
                    : 'Agendado sem cobrança. Quando for cobrar, use "Gerar cobrança" na tela do atendimento.'
            )
            navigate(detailPath)
            return
        }
        navigate(fallbackPath)
    }

    /**
     * O que fazer depois de ATUALIZAR: ir para o detalhe (o fluxo de sempre) ou sair
     * do modo edição para agendar mais um na mesma cobrança.
     *
     * A saída é por navegação, não por limpeza de estado: `isEditing` vem do `:id` da
     * rota, então enquanto ele estiver na URL o formulário continua editando o mesmo
     * registro. Paciente e cobrança-alvo viajam na query — quem monta a tela de novo
     * lê os dois (ver o efeito de sementes da URL).
     *
     * O ajuste de valor tem precedência: se a edição gerou cobrança complementar ou
     * estorno, o operador vai para lá. Na prática as duas coisas não se cruzam — uma
     * cobrança que aceitou complemento já foi paga, e cobrança paga não é alvo de soma.
     */
    const finishUpdate = (result: any, detailPath: string) => {
        const wantsAnother = addAnotherRef.current
        addAnotherRef.current = false

        if (handleBillingAdjustment(result, detailPath)) return

        if (wantsAnother && clientId) {
            const params = new URLSearchParams({
                type: serviceType,
                clientId,
            })
            // Sem alvo (cobrança deste atendimento já fechada), o próximo segue o
            // caminho normal da criação: a detecção no servidor procura uma cobrança
            // de hoje do paciente e, não achando, ele nasce com a sua.
            if (billingTargetId != null) {
                params.set('billingTarget', String(billingTargetId))
            }
            toast.info(
                billingTargetId != null
                    ? 'Agende o próximo — vai somar na mesma cobrança.'
                    : `Agende o próximo atendimento deste ${clientLabel}.`
            )
            navigate(`/atendimento/scheduling?${params.toString()}`)
            return
        }

        navigate(detailPath)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!isEditing && !isBudgetMode && !serviceAddress) {
            toast.error('Confirme o local de atendimento antes de agendar')
            return
        }

        if (!user?.companyId) {
            toast.error('Usuário não autenticado')
            return
        }
        if (!isEditing && !clientId) {
            toast.error(`Selecione um ${clientLabel}`)
            return
        }
        /**
         * Procedimento sem preço próprio de filial: exige margem — mas não exige que ela
         * tenha sido DIGITADA.
         *
         * A condição pedia `overridePrice != null`, ou seja, um valor digitado à mão.
         * Com o custo lido do vínculo (`CatalogPartner.price`) e a venda vinda do
         * mesmo vínculo, a margem já existe assim que o parceiro é escolhido — e o
         * operador era obrigado a redigitar um número que o cadastro já tinha. O que
         * importa é haver markup positivo, venha ele do cadastro ou do teclado.
         */
        if (
            requireSellingPrice &&
            (orderMarkup == null || orderMarkup <= 0)
        ) {
            toast.error(
                'Este procedimento não tem markup próprio. Informe o markup (margem) antes de salvar.'
            )
            return
        }
        /*if (!isTeleconsultation && !hasCatalogSelected) {
            toast.error('Selecione um item do catálogo')
            return
        }
        if (isTeleconsultation && !scheduledAt && !isEditing) {
            toast.error('Informe a data e hora')
            return
        }*/
        // Intermediação só opera com parceiro definido (paciente vai a um único
        // local). Bloqueia salvar/agendar/criar orçamento sem parceiro — exceto
        // PROCEDURE, que é feito internamente e não depende de parceiro.
        if (!partnerId && !professionalId) {
            toast.error('Selecione um parceiro ou profissional para prosseguir')
            return
        }

        // Item sem preço no vínculo do executante: o repasse sai zero e a conta a pagar
        // nasce a menor, sem nada em tela dizer isso.
        if (itemsMissingExecutantPrice.length > 0) {
            const primeiro = itemsMissingExecutantPrice[0]
            const resto =
                itemsMissingExecutantPrice.length > 1
                    ? ` (e mais ${itemsMissingExecutantPrice.length - 1})`
                    : ''
            toast.error(
                `Cadastre o ${primeiro.missing} de "${primeiro.name}" para ${primeiro.executant} no catálogo antes de salvar${resto}`
            )
            return
        }

        // Taxa de anestesista gera conta a pagar na baixa. Sem credor não há para quem
        // transferir — a conta nasceria impagável. O backend recusa igual; o bloqueio aqui
        // é para o operador não perder o formulário inteiro. Ver docs/billing-system.md §7.
        if (
            anesthetistFee &&
            parseCurrencyToCents(anesthetistFee) > 0 &&
            (!anesthetistRecipientKind || !anesthetistRecipientId)
        ) {
            toast.error(
                'Informe quem recebe a taxa do anestesista (hospital, profissional ou parceiro)'
            )
            return
        }

        // Material sem custo gera repasse ZERO — foi o que aconteceu com 25.572 linhas de
        // OPME até 2026-07-30, R$ 56,11 mi cobrados sem um centavo de custo lançado.
        const semCusto = medicalSupplies.find(
            ms => !ms.costPriceSnapshot || ms.costPriceSnapshot <= 0
        )
        if (semCusto) {
            toast.error(
                `Informe o custo de "${semCusto.name}" — é ele que gera o repasse a quem forneceu`
            )
            return
        }
        // Só o fornecedor tem padrão de onde herdar (o cadastro do material). Hospital,
        // profissional e parceiro exigem o destinatário na linha.
        const semDestinatario = medicalSupplies.find(
            ms =>
                !!ms.recipientKind &&
                ms.recipientKind !== 'SUPPLIER' &&
                !ms.recipientId
        )
        if (semDestinatario) {
            toast.error(
                `Informe quem recebe pelo material "${semDestinatario.name}"`
            )
            return
        }
        // Fornecedor em branco herda o do cadastro do material — quando existe. Quando
        // não existe, a fatia é descartada na geração do repasse e o custo é cobrado sem
        // conta a pagar. O backend também barra; aqui é para o operador ver antes.
        const semFornecedor = medicalSupplies.find(
            ms =>
                (ms.recipientKind ?? 'SUPPLIER') === 'SUPPLIER' &&
                !ms.recipientId &&
                !ms.defaultSupplierId
        )
        if (semFornecedor) {
            toast.error(
                `Escolha o fornecedor de "${semFornecedor.name}": ele não tem fornecedor no cadastro`
            )
            return
        }
        // Todo componente atrelado tem de dizer quem recebe: um exame ou uma consulta
        // sem destinatário é custo cobrado do paciente que nunca vira conta a pagar.
        const componentesSemDestinatario = fromPackage
            ? undefined
            : [...orderExams, ...orderConsultations].find(
                  ex => !ex.recipientKind || !ex.recipientId
              )
        if (componentesSemDestinatario) {
            toast.error(
                `Informe quem recebe por "${componentesSemDestinatario.name}"`
            )
            return
        }
        // Parceiro herda o custo do vínculo; profissional e hospital não têm de onde.
        const componenteSemValor = fromPackage
            ? undefined
            : [...orderExams, ...orderConsultations].find(
                  ex =>
                      ex.recipientKind !== 'PARTNER' &&
                      (!ex.recipientAmount || ex.recipientAmount <= 0)
              )
        if (componenteSemValor) {
            toast.error(
                `Informe o valor do repasse de "${componenteSemValor.name}"`
            )
            return
        }

        setLoading(true)
        try {
            // Orçamento novo não leva data: ela nem aparece na tela nesse modo, e
            // gravá-la cria o atendimento com data e sem evento de agenda (ver a
            // limpeza do estado em `forceBudgetMode`). Segunda trava, para o caso de
            // o estado escapar por outro caminho.
            //
            // Na EDIÇÃO a data segue normalmente: ali ela é a do registro, e um
            // orçamento com data gravada de antes continua sendo salvo como está.
            const scheduledAtUTC =
                scheduledAt && !(isBudgetMode && !isEditing)
                    ? convertLocalToUTC(scheduledAt)
                    : undefined

            /**
             * Mudar a data de um atendimento que JÁ tinha data é REMARCAÇÃO, não edição.
             *
             * O update comum só sobrescreve o campo: não valida conflito de horário e não
             * avisa o paciente, que aparece no horário antigo. O endpoint de reagendamento
             * faz as duas coisas e ainda grava o histórico.
             *
             * Roda ANTES do update: se o horário conflitar, o backend recusa e nada é
             * salvo — em vez de gravar a data e só depois descobrir o choque de agenda.
             */
            const isReschedule =
                isEditing &&
                !!originalScheduledAt &&
                !!scheduledAtUTC &&
                scheduledAt !== originalScheduledAt

            if (isReschedule && id) {
                const payload = {
                    scheduledAt: scheduledAtUTC as string,
                    arrivalOrder,
                    reason: rescheduleReason.trim() || undefined,
                }
                const recordId = parseInt(id, 10)
                const res =
                    serviceType === 'CONSULTATION'
                        ? await rescheduleConsultation(recordId, payload)
                        : serviceType === 'PROCEDURE'
                          ? await rescheduleOrder(recordId, payload)
                          : await rescheduleExam(recordId, payload)

                if (!res.success) {
                    toast.error(
                        res.error || 'Não foi possível remarcar o atendimento'
                    )
                    setLoading(false)
                    return
                }
                toast.success(
                    `Atendimento remarcado. O ${clientLabel} foi avisado da nova data.`
                )
            }
            const profId = professionalId
                ? parseInt(professionalId, 10)
                : undefined
            // ⚠️ Campo VAZIO significa "sem desconto", e precisa ir como 0.
            //
            // Enviar `undefined` faz o backend entender "não mexer neste campo" — o
            // operador apagava os 9%, salvava, e o desconto continuava lá. O sintoma era
            // "a edição não salva"; na verdade o campo nunca chegava.
            //
            // Só é `undefined` quando o formulário não tem desconto nenhum a dizer (o
            // caso de criação sem o campo preenchido é 0 do mesmo jeito).
            const parsedDiscount = parseFloat(discount)
            const discountVal = Number.isFinite(parsedDiscount)
                ? parsedDiscount
                : 0
            const recordId = id ? parseInt(id, 10) : 0

            /**
             * Preço digitado à mão só vai ao backend onde ele é aceito: orçamento de
             * procedimento (livre) ou item com desconto máximo configurado (limitado
             * ao piso). Sem margem nenhuma o campo nem aparece, e mandar o valor
             * seria 400 na cara do operador — a edição de um registro antigo carrega
             * `overridePrice` do que está gravado e o reenviaria sem querer.
             */
            const overridePriceForSubmit =
                canEditFinalPrice || maxAllowedDiscount > 0
                    ? overridePrice || undefined
                    : undefined

            /**
             * Desconto segue a mesma regra do valor digitado: só vai ao backend
             * quando o campo existe na tela.
             *
             * Sem teto no catálogo o campo de desconto não é renderizado
             * (`pricing-section`), mas o estado continua hidratado com o
             * percentual gravado — e reenviá-lo fazia o backend recusar o save
             * com "nenhum desconto máximo está configurado", numa tela onde o
             * operador não via desconto nenhum para tirar. Foi o que travou os
             * orçamentos importados do legado.
             *
             * `undefined` (e não 0) porque zerar aqui mudaria o valor negociado
             * do atendimento sem ninguém ter pedido; o backend preserva o que
             * está gravado quando o campo não vem.
             */
            const discountForSubmit =
                maxAllowedDiscount > 0 ? discountVal : undefined

            // Cupom novo vai pelo código; o já gravado não é reenviado (o backend o
            // mantém e não aceita trocar).
            const couponFieldForSubmit = () =>
                appliedCoupon && !appliedCoupon.persisted
                    ? { couponCode: appliedCoupon.code }
                    : {}

            if (serviceType === 'CONSULTATION') {
                if (isEditing) {
                    const response = await updateConsultation(recordId, {
                        title: consultTitle || undefined,
                        scheduledAt: scheduledAtUTC,
                        arrivalOrder,
                        notes: notes || undefined,
                        diagnosis: diagnosis || undefined,
                        treatmentPlan: treatmentPlan || undefined,
                        isReturn,
                        triageId: triageId ? parseInt(triageId, 10) : undefined,
                        discount: discountForSubmit,
                        ...couponFieldForSubmit(),
                        overridePrice: overridePriceForSubmit,
                        roomId: roomId || undefined,
                        hospitalId: hospitalId || null,
                        // `null`, não `undefined`: o convênio escolhido (ou retirado)
                        // na edição precisa CHEGAR ao backend. Omitido, ele lê "não
                        // informado" e mantém o que estava — o operador selecionava o
                        // convênio, salvava, e o campo voltava vazio.
                        healthPlanId: healthPlanLocked
                            ? undefined
                            : (healthPlanId ?? null),
                        // Trocar o parcelamento na edição, sem ida à cobrança.
                        ...paymentFieldsForSubmit(),
                        // Cria a cobrança quando o atendimento ficou sem nenhuma.
                        ...billingCreationFieldForSubmit(),
                        // O item do catálogo e quem executa definem o preço — sem enviá-los,
                        // trocar a consulta ou o parceiro na edição não mudava nada.
                        catalogId: selectedCatalog?.id || undefined,
                        // ⚠️ `null`, não `undefined`: campo vazio aqui significa "sem
                        // executante", e o backend entende ausência como "não mexer neste
                        // campo" — o operador retirava o profissional, salvava, e ele
                        // continuava lá. Mesma armadilha do desconto, logo acima.
                        professionalId: profId ?? null,
                        partnerId: partnerId ?? null,
                        variations:
                            consultationVariations.length > 0
                                ? consultationVariations.map(v => ({
                                      catalogId: v.catalogVariationId,
                                      quantity: v.quantity,
                                  }))
                                : undefined,
                    } as any)
                    if (response.success) {
                        toast.success('Consulta atualizada com sucesso!')
                        finishUpdate(
                            response.result,
                            `/bookings/consultations/${recordId}`
                        )
                    } else {
                        toast.error(
                            response.error || 'Erro ao atualizar consulta'
                        )
                    }
                } else {
                    const data: CreateConsultationData = {
                        // Local de atendimento confirmado (snapshot de auditoria).
                        ...(serviceAddress || {}),
                        title: consultTitle || undefined,
                        status: isBudgetMode
                            ? ('BUDGET' as any)
                            : ((loadedStatus && loadedStatus !== 'BUDGET'
                                  ? loadedStatus
                                  : 'SCHEDULED') as any),
                        scheduledAt: scheduledAtUTC,
                        arrivalOrder,
                        notes: notes || undefined,
                        diagnosis: diagnosis || undefined,
                        treatmentPlan: treatmentPlan || undefined,
                        isReturn,
                        clientId: parseInt(clientId, 10),
                        professionalId: profId,
                        partnerId: partnerId || undefined,
                        healthPlanId: healthPlanId || undefined,
                        hospitalId: hospitalId || undefined,
                        companyId: user.companyId,
                        triageId: triageId ? parseInt(triageId, 10) : undefined,
                        duration: duration || undefined,
                        discount: discountForSubmit,
                        ...couponFieldForSubmit(),
                        overridePrice: overridePriceForSubmit,
                        catalogId: selectedCatalog?.id || undefined,
                        variations:
                            consultationVariations.length > 0
                                ? consultationVariations.map(v => ({
                                      catalogId: v.catalogVariationId,
                                      quantity: v.quantity,
                                  }))
                                : undefined,
                        pipelineId: pipelineId || undefined,
                        roomId: roomId || undefined,
                        billingTargetId: destinoDaSoma,
                        // Forma fechada com o pagador na própria tela: a cobrança
                        // nasce com o método e o parcelamento, e o link já pode ir.
                        // Antes disto ela nascia sem método e o operador tinha de
                        // abrir a cobrança para definir e só então enviar.
                        ...paymentFieldsForSubmit(),
                        // ...ou cobrança nenhuma, quando o paciente paga no dia.
                        ...deferBillingFieldForSubmit(),
                    }
                    const response = await createConsultation(data)
                    if (response.success && response.result) {
                        toast.success('Consulta agendada com sucesso!')
                        const billingId = (response.result as any)?.billingId
                        finishCreate(
                            billingId,
                            '/bookings/consultations',
                            (response.result as any)?.id
                                ? `/bookings/consultations/${(response.result as any).id}`
                                : undefined
                        )
                    } else {
                        toast.error(response.error || 'Erro ao criar consulta')
                    }
                }
            } else if (serviceType === 'EXAM') {
                if (isEditing) {
                    const response = await updateExam(recordId, {
                        description: notes || undefined,
                        notes: notes || undefined,
                        scheduledAt: scheduledAtUTC,
                        arrivalOrder,
                        discount: discountForSubmit,
                        ...couponFieldForSubmit(),
                        duration: duration || undefined,
                        roomId: roomId || undefined,
                        // Trocar o parcelamento na edição, sem ida à cobrança.
                        ...paymentFieldsForSubmit(),
                        // Cria a cobrança quando o atendimento ficou sem nenhuma.
                        ...billingCreationFieldForSubmit(),
                        // Os itens (os exames em si) e quem executa definem o valor. Antes
                        // não eram enviados na edição: acrescentar um exame não salvava nada
                        // e o valor não mudava — logo, nenhuma cobrança complementar nascia.
                        items: examItems.map(item => ({
                            catalogId: item.catalogId,
                            quantity: item.quantity,
                            variationCatalogIds: item.catalogVariationId
                                ? [item.catalogVariationId]
                                : [],
                        })),
                        // ⚠️ `null`, não `undefined` — ver a nota na edição da consulta.
                        professionalId: profId ?? null,
                        partnerId: partnerId ?? null,
                        // `null`, não `undefined`: campo apagado tem de CHEGAR ao
                        // backend. Omitido, ele lê "não informado" e mantém (ou
                        // herda do catálogo) a taxa que o operador acabou de tirar.
                        anesthetistFee: anesthetistFee
                            ? parseCurrencyToCents(anesthetistFee)
                            : null,
                        anesthetistRecipientKind:
                            anesthetistRecipientKind ?? undefined,
                        anesthetistRecipientId:
                            anesthetistRecipientId ?? undefined,
                        medicalSupplies:
                            medicalSupplies.length > 0
                                ? medicalSupplies.map(ms => ({
                                      medicalSupplyId: ms.medicalSupplyId,
                                      quantity: ms.quantity,
                                      unitPrice: ms.unitPrice,
                                      // Custo e destinatário vão POR LINHA: o preço do OPME
                                      // muda a cada negociação e o fornecedor muda com ele.
                                      costPriceSnapshot: ms.costPriceSnapshot,
                                      recipientKind: ms.recipientKind,
                                      recipientId: ms.recipientId ?? undefined,
                                  }))
                                : undefined,
                        overridePrice: overridePriceForSubmit,
                        hospitalId: hospitalId || null,
                        // `null`, não `undefined`: o convênio escolhido (ou retirado)
                        // na edição precisa CHEGAR ao backend. Omitido, ele lê "não
                        // informado" e mantém o que estava — o operador selecionava o
                        // convênio, salvava, e o campo voltava vazio.
                        healthPlanId: healthPlanLocked
                            ? undefined
                            : (healthPlanId ?? null),
                    } as any)
                    if (response.success) {
                        toast.success('Exame atualizado com sucesso!')
                        finishUpdate(
                            response.result,
                            `/bookings/exams/${recordId}`
                        )
                    } else {
                        toast.error(response.error || 'Erro ao atualizar exame')
                    }
                } else {
                    const data: CreateExamData = {
                        // Local de atendimento confirmado (snapshot de auditoria).
                        ...(serviceAddress || {}),
                        description: notes || undefined,
                        notes: notes || undefined,
                        status: isBudgetMode
                            ? ('BUDGET' as any)
                            : ((loadedStatus && loadedStatus !== 'BUDGET'
                                  ? loadedStatus
                                  : 'REQUESTED') as any),
                        scheduledAt: scheduledAtUTC,
                        arrivalOrder,
                        validUntil:
                            isBudgetMode && validUntil ? validUntil : undefined,
                        clientId: parseInt(clientId, 10),
                        professionalId: profId,
                        companyId: user.companyId,
                        partnerId: partnerId || undefined,
                        healthPlanId: healthPlanId || undefined,
                        hospitalId: hospitalId || undefined,
                        duration: duration || undefined,
                        items: examItems.map(item => ({
                            catalogId: item.catalogId,
                            quantity: item.quantity,
                            variationCatalogIds: item.catalogVariationId
                                ? [item.catalogVariationId]
                                : [],
                        })),
                        discount: discountForSubmit,
                        ...couponFieldForSubmit(),
                        pipelineId: pipelineId || undefined,
                        billingTargetId: destinoDaSoma,
                        // Forma fechada com o pagador na própria tela: a cobrança
                        // nasce com o método e o parcelamento, e o link já pode ir.
                        // Antes disto ela nascia sem método e o operador tinha de
                        // abrir a cobrança para definir e só então enviar.
                        ...paymentFieldsForSubmit(),
                        // ...ou cobrança nenhuma, quando o paciente paga no dia.
                        ...deferBillingFieldForSubmit(),
                        roomId: roomId || undefined,
                        // `null`, não `undefined`: campo apagado tem de CHEGAR ao
                        // backend. Omitido, ele lê "não informado" e mantém (ou
                        // herda do catálogo) a taxa que o operador acabou de tirar.
                        anesthetistFee: anesthetistFee
                            ? parseCurrencyToCents(anesthetistFee)
                            : null,
                        anesthetistRecipientKind:
                            anesthetistRecipientKind ?? undefined,
                        anesthetistRecipientId:
                            anesthetistRecipientId ?? undefined,
                        medicalSupplies:
                            medicalSupplies.length > 0
                                ? medicalSupplies.map(ms => ({
                                      medicalSupplyId: ms.medicalSupplyId,
                                      quantity: ms.quantity,
                                      unitPrice: ms.unitPrice,
                                      // Custo e destinatário vão POR LINHA: o preço do OPME
                                      // muda a cada negociação e o fornecedor muda com ele.
                                      costPriceSnapshot: ms.costPriceSnapshot,
                                      recipientKind: ms.recipientKind,
                                      recipientId: ms.recipientId ?? undefined,
                                  }))
                                : undefined,
                        overridePrice: overridePriceForSubmit,
                    } as any
                    const response = await createExam(data)
                    if (response.success && response.result) {
                        toast.success(
                            isBudgetMode
                                ? 'Orçamento de exame criado!'
                                : 'Exame agendado com sucesso!'
                        )
                        const result = response.result as any
                        // Orçamento não gera cobrança: vai direto para os detalhes do exame.
                        if (isBudgetMode && result?.id)
                            navigate(`/bookings/exams/${result.id}`)
                        else
                            finishCreate(
                                result?.billingId,
                                '/bookings/exams',
                                result?.id
                                    ? `/bookings/exams/${result.id}`
                                    : undefined
                            )
                    } else {
                        toast.error(response.error || 'Erro ao criar exame')
                    }
                }
            } else if (serviceType === 'PROCEDURE') {
                if (isEditing) {
                    const response = await updateOrder(recordId, {
                        // Itens empilhados: sem isto, trocar o procedimento do
                        // catálogo na tela não saía daqui — o operador trocava
                        // "Mastopexia sem prótese" por "com prótese", salvava, e
                        // nada acontecia. A troca vale em qualquer status vivo; o
                        // backend reprecifica e liquida a diferença (complemento ou
                        // estorno), e recusa apenas quando o repasse já foi pago.
                        items: orderItems.map(item => ({
                            catalogId: item.catalogId,
                            quantity: item.quantity,
                            variationCatalogIds: item.catalogVariationId
                                ? [item.catalogVariationId]
                                : [],
                        })),
                        description: notes || undefined,
                        notes: notes || undefined,
                        scheduledAt: scheduledAtUTC,
                        arrivalOrder,
                        duration: duration || undefined,
                        roomId: roomId || undefined,
                        preInstructions: preInstructions || undefined,
                        postInstructions: postInstructions || undefined,
                        discount: discountForSubmit,
                        ...couponFieldForSubmit(),
                        // `null`, não `undefined`: campo apagado tem de CHEGAR ao
                        // backend. Omitido, ele lê "não informado" e mantém (ou
                        // herda do catálogo) a taxa que o operador acabou de tirar.
                        anesthetistFee: anesthetistFee
                            ? parseCurrencyToCents(anesthetistFee)
                            : null,
                        anesthetistRecipientKind:
                            anesthetistRecipientKind ?? undefined,
                        anesthetistRecipientId:
                            anesthetistRecipientId ?? undefined,
                        hospitalFee: hospitalFee
                            ? parseCurrencyToCents(hospitalFee)
                            : undefined,
                        hospitalFeeNotes: hospitalFeeNotes || undefined,
                        // Sempre enviar a lista (mesmo vazia): com `undefined` o
                        // backend ignorava a edição e voltava a herdar os materiais do
                        // catálogo, ressuscitando valores que o operador removeu.
                        medicalSupplies: medicalSupplies.map(ms => ({
                            medicalSupplyId: ms.medicalSupplyId,
                            quantity: ms.quantity,
                            unitPrice: ms.unitPrice,
                            costPriceSnapshot: ms.costPriceSnapshot,
                            recipientKind: ms.recipientKind,
                            recipientId: ms.recipientId ?? undefined,
                        })),
                        exams:
                            orderExams.length > 0
                                ? orderExams.map(ex => ({
                                      catalogId: ex.catalogId,
                                      quantity: ex.quantity,
                                      unitPrice: ex.unitPrice,
                                      complimentary: ex.complimentary,
                                      notes: ex.notes || undefined,
                                      catalogVariationId:
                                          ex.catalogVariationId ?? undefined,
                                      variationName:
                                          ex.variationName || undefined,
                                      // Quem recebe por este componente. Vazio
                                      // mantém o comportamento antigo.
                                      recipientKind:
                                          ex.recipientKind ?? undefined,
                                      recipientId: ex.recipientId ?? undefined,
                                      recipientAmount:
                                          ex.recipientAmount ?? undefined,
                                      // Agenda do componente: só a venda de pacote a
                                      // preenche. `undefined` preserva o que está
                                      // gravado (o backend não sobrescreve o que não
                                      // foi enviado).
                                      scheduledAt: ex.scheduledAt ?? undefined,
                                      arrivalOrder: ex.scheduledAt
                                          ? !!ex.arrivalOrder
                                          : undefined,
                                      roomId: ex.roomId ?? undefined,
                                  }))
                                : [],
                        consultations:
                            orderConsultations.length > 0
                                ? orderConsultations.map(ex => ({
                                      catalogId: ex.catalogId,
                                      quantity: ex.quantity,
                                      unitPrice: ex.unitPrice,
                                      complimentary: ex.complimentary,
                                      notes: ex.notes || undefined,
                                      catalogVariationId:
                                          ex.catalogVariationId ?? undefined,
                                      variationName:
                                          ex.variationName || undefined,
                                      // Quem recebe por este componente. Vazio
                                      // mantém o comportamento antigo.
                                      recipientKind:
                                          ex.recipientKind ?? undefined,
                                      recipientId: ex.recipientId ?? undefined,
                                      recipientAmount:
                                          ex.recipientAmount ?? undefined,
                                      // Agenda do componente: só a venda de pacote a
                                      // preenche. `undefined` preserva o que está
                                      // gravado (o backend não sobrescreve o que não
                                      // foi enviado).
                                      scheduledAt: ex.scheduledAt ?? undefined,
                                      arrivalOrder: ex.scheduledAt
                                          ? !!ex.arrivalOrder
                                          : undefined,
                                      roomId: ex.roomId ?? undefined,
                                  }))
                                : [],
                        // ⚠️ `null`, não `undefined` — mesma armadilha do desconto e da
                        // consulta: ausência é "não mexer neste campo". Sem estes dois,
                        // trocar quem executa um procedimento na tela não saía daqui —
                        // o operador escolhia outro parceiro, salvava, e o antigo
                        // continuava lá, recebendo o repasse.
                        professionalId: profId ?? null,
                        partnerId: partnerId ?? null,
                        overridePrice: overridePriceForSubmit,
                        costPrice: orderCostPrice ?? undefined,
                        markupPercentage: orderMarkup ?? undefined,
                        professionalAmount: professionalAmountOverride
                            ? parseCurrencyToCents(professionalAmountOverride)
                            : null,
                        hospitalId: hospitalId || null,
                        // `null`, não `undefined`: o convênio escolhido (ou retirado)
                        // na edição precisa CHEGAR ao backend. Omitido, ele lê "não
                        // informado" e mantém o que estava — o operador selecionava o
                        // convênio, salvava, e o campo voltava vazio.
                        healthPlanId: healthPlanLocked
                            ? undefined
                            : (healthPlanId ?? null),
                        // Trocar o parcelamento na edição: o paciente que desistiu de
                        // 3x e quer 4x é resolvido aqui, e a cobrança acompanha.
                        ...paymentFieldsForSubmit(),
                        // Produtos: SÓ na venda de pacote. Enviar a lista reescreve a
                        // do atendimento, e a tela comum de procedimento não os carrega
                        // — mandá-la de lá apagaria os produtos congelados na venda.
                        products: packageSaleMode
                            ? orderProducts.map(p => ({
                                  catalogId: p.catalogId,
                                  quantity: p.quantity,
                              }))
                            : undefined,
                    } as any)
                    if (response.success) {
                        /**
                         * Flex escolhido entre as formas: liga DEPOIS de a edição
                         * estar gravada.
                         *
                         * Antes seria pior: a correção de valor desta mesma
                         * submissão passaria pela regra do flex (sem complemento
                         * nem estorno) enquanto a cobrança que existe ainda é a
                         * normal.
                         *
                         * Falhar aqui não desfaz o que já foi salvo — o
                         * procedimento continua válido, só não virou flex —, então
                         * o erro é dito e a navegação segue.
                         */
                        if (flexRequested) {
                            const flexRes =
                                await enableOrderFlexPayment(recordId)
                            if (flexRes.success) {
                                toast.success(
                                    `${serviceLabels.Order} atualizado e pagamento flexível ativado. O link foi enviado ao ${clientLabel}.`
                                )
                            } else {
                                toast.error(
                                    flexRes.error ||
                                        `O ${serviceLabels.order} foi salvo, mas não foi possível ativar o pagamento flexível.`
                                )
                            }
                        } else {
                            toast.success(
                                `${serviceLabels.Order} atualizado com sucesso!`
                            )
                        }
                        finishUpdate(
                            response.result,
                            `/bookings/orders/${recordId}`
                        )
                    } else {
                        toast.error(
                            response.error || 'Erro ao atualizar procedimento'
                        )
                    }
                } else {
                    const data: CreateOrderData = {
                        // Local de atendimento confirmado (snapshot de auditoria).
                        ...(serviceAddress || {}),
                        // O item do catálogo agora sempre existe antes do
                        // salvamento — quem cria é o cadastro rápido
                        // (`CatalogQuickCreate`), não o endpoint do procedimento.
                        ...(orderItems.length > 0
                            ? {
                                  items: orderItems.map(item => ({
                                      catalogId: item.catalogId,
                                      quantity: item.quantity,
                                      variationCatalogIds:
                                          item.catalogVariationId
                                              ? [item.catalogVariationId]
                                              : [],
                                  })),
                              }
                            : { catalogId: selectedCatalog!.id }),
                        status: isBudgetMode
                            ? ('BUDGET' as any)
                            : ((loadedStatus && loadedStatus !== 'BUDGET'
                                  ? loadedStatus
                                  : 'SCHEDULED') as any),
                        description: notes || undefined,
                        notes: notes || undefined,
                        scheduledAt: scheduledAtUTC,
                        arrivalOrder,
                        validUntil:
                            isBudgetMode && validUntil ? validUntil : undefined,
                        duration: duration ? duration : undefined,
                        roomId: roomId || undefined,
                        preInstructions: preInstructions || undefined,
                        postInstructions: postInstructions || undefined,
                        clientId: parseInt(clientId, 10),
                        professionalId: profId,
                        partnerId: partnerId || undefined,
                        healthPlanId: healthPlanId || undefined,
                        hospitalId: hospitalId || undefined,
                        companyId: user.companyId,
                        discount: discountForSubmit,
                        ...couponFieldForSubmit(),
                        pipelineId: pipelineId || undefined,
                        billingTargetId: destinoDaSoma,
                        // Forma fechada com o pagador na própria tela: a cobrança
                        // nasce com o método e o parcelamento, e o link já pode ir.
                        // Antes disto ela nascia sem método e o operador tinha de
                        // abrir a cobrança para definir e só então enviar.
                        ...paymentFieldsForSubmit(),
                        // ...ou cobrança nenhuma, quando o paciente paga no dia.
                        ...deferBillingFieldForSubmit(),
                        // `null`, não `undefined`: campo apagado tem de CHEGAR ao
                        // backend. Omitido, ele lê "não informado" e mantém (ou
                        // herda do catálogo) a taxa que o operador acabou de tirar.
                        anesthetistFee: anesthetistFee
                            ? parseCurrencyToCents(anesthetistFee)
                            : null,
                        anesthetistRecipientKind:
                            anesthetistRecipientKind ?? undefined,
                        anesthetistRecipientId:
                            anesthetistRecipientId ?? undefined,
                        hospitalFee: hospitalFee
                            ? parseCurrencyToCents(hospitalFee)
                            : undefined,
                        hospitalFeeNotes: hospitalFeeNotes || undefined,
                        // Sempre enviar a lista (mesmo vazia): com `undefined` o
                        // backend ignorava a edição e voltava a herdar os materiais do
                        // catálogo, ressuscitando valores que o operador removeu.
                        medicalSupplies: medicalSupplies.map(ms => ({
                            medicalSupplyId: ms.medicalSupplyId,
                            quantity: ms.quantity,
                            unitPrice: ms.unitPrice,
                            costPriceSnapshot: ms.costPriceSnapshot,
                            recipientKind: ms.recipientKind,
                            recipientId: ms.recipientId ?? undefined,
                        })),
                        exams:
                            orderExams.length > 0
                                ? orderExams.map(ex => ({
                                      catalogId: ex.catalogId,
                                      quantity: ex.quantity,
                                      unitPrice: ex.unitPrice,
                                      complimentary: ex.complimentary,
                                      notes: ex.notes || undefined,
                                      catalogVariationId:
                                          ex.catalogVariationId ?? undefined,
                                      variationName:
                                          ex.variationName || undefined,
                                      // Quem recebe por este componente. Vazio
                                      // mantém o comportamento antigo.
                                      recipientKind:
                                          ex.recipientKind ?? undefined,
                                      recipientId: ex.recipientId ?? undefined,
                                      recipientAmount:
                                          ex.recipientAmount ?? undefined,
                                      // Agenda do componente: só a venda de pacote a
                                      // preenche. `undefined` preserva o que está
                                      // gravado (o backend não sobrescreve o que não
                                      // foi enviado).
                                      scheduledAt: ex.scheduledAt ?? undefined,
                                      arrivalOrder: ex.scheduledAt
                                          ? !!ex.arrivalOrder
                                          : undefined,
                                      roomId: ex.roomId ?? undefined,
                                  }))
                                : undefined,
                        consultations:
                            orderConsultations.length > 0
                                ? orderConsultations.map(ex => ({
                                      catalogId: ex.catalogId,
                                      quantity: ex.quantity,
                                      unitPrice: ex.unitPrice,
                                      complimentary: ex.complimentary,
                                      notes: ex.notes || undefined,
                                      catalogVariationId:
                                          ex.catalogVariationId ?? undefined,
                                      variationName:
                                          ex.variationName || undefined,
                                      // Quem recebe por este componente. Vazio
                                      // mantém o comportamento antigo.
                                      recipientKind:
                                          ex.recipientKind ?? undefined,
                                      recipientId: ex.recipientId ?? undefined,
                                      recipientAmount:
                                          ex.recipientAmount ?? undefined,
                                      // Agenda do componente: só a venda de pacote a
                                      // preenche. `undefined` preserva o que está
                                      // gravado (o backend não sobrescreve o que não
                                      // foi enviado).
                                      scheduledAt: ex.scheduledAt ?? undefined,
                                      arrivalOrder: ex.scheduledAt
                                          ? !!ex.arrivalOrder
                                          : undefined,
                                      roomId: ex.roomId ?? undefined,
                                  }))
                                : undefined,
                        overridePrice: overridePriceForSubmit,
                        costPrice: orderCostPrice ?? undefined,
                        markupPercentage: orderMarkup ?? undefined,
                        professionalAmount: professionalAmountOverride
                            ? parseCurrencyToCents(professionalAmountOverride)
                            : null,
                    } as any
                    const response = await createOrder(data)
                    if (response.success && response.result) {
                        toast.success(
                            isBudgetMode
                                ? `Orçamento de ${serviceLabels.order} criado!`
                                : `${serviceLabels.Order} agendado com sucesso!`
                        )
                        const result = response.result as any
                        // Orçamento não gera cobrança: vai direto para os detalhes do procedimento.
                        if (isBudgetMode && result?.id)
                            navigate(`/bookings/orders/${result.id}`)
                        else
                            finishCreate(
                                result?.billingId,
                                '/bookings/orders',
                                result?.id
                                    ? `/bookings/orders/${result.id}`
                                    : undefined
                            )
                    } else {
                        toast.error(
                            response.error || 'Erro ao criar procedimento'
                        )
                    }
                }
            } else if (serviceType === 'PACKAGE') {
                // Pacote: roteia por composição. Só exames -> Exame (multi-item);
                // com procedimento/consulta -> Procedimento. O backend expande o
                // pacote no create e cobra o preço único. Ver docs/catalog-packages.md §3.
                const pkg = selectedCatalog
                if (!pkg) {
                    toast.error('Selecione um pacote')
                    setLoading(false)
                    return
                }
                const components = pkg.packageItems || []
                // O roteamento (exame x procedimento) olha só os componentes CLÍNICOS:
                // produto acompanha o pacote, não decide por qual fluxo ele é agendado.
                // Um pacote sem componente clínico não existe (o backend recusa).
                //
                // ⚠️ `!!pi.item` NÃO basta: mercadoria também preenche `item` desde a
                // unificação. Sem filtrar por `kind`, um pacote de exames com um produto
                // dentro deixava de ser "só exame" e ia parar no fluxo de procedimento.
                const clinicalComponents = components.filter(
                    pi => (pi.kind ?? 'ITEM') === 'ITEM' && !!pi.item
                )
                const isExamOnly =
                    clinicalComponents.length > 0 &&
                    clinicalComponents.every(pi => pi.item!.category === 'EXAM')

                if (isExamOnly) {
                    const data: CreateExamData = {
                        // Local de atendimento confirmado (snapshot de auditoria).
                        ...(serviceAddress || {}),
                        description: notes || undefined,
                        notes: notes || undefined,
                        status: isBudgetMode
                            ? ('BUDGET' as any)
                            : ((loadedStatus && loadedStatus !== 'BUDGET'
                                  ? loadedStatus
                                  : 'REQUESTED') as any),
                        scheduledAt: scheduledAtUTC,
                        arrivalOrder,
                        validUntil:
                            isBudgetMode && validUntil ? validUntil : undefined,
                        clientId: parseInt(clientId, 10),
                        professionalId: profId,
                        companyId: user.companyId,
                        partnerId: partnerId || undefined,
                        healthPlanId: healthPlanId || undefined,
                        hospitalId: hospitalId || undefined,
                        duration: duration || undefined,
                        // O preço do pacote é fixado server-side (overridePrice).
                        items: [{ catalogId: pkg.id, quantity: 1 }],
                        discount: discountForSubmit,
                        ...couponFieldForSubmit(),
                        pipelineId: pipelineId || undefined,
                        roomId: roomId || undefined,
                        billingTargetId: destinoDaSoma,
                        // Forma fechada com o pagador na própria tela: a cobrança
                        // nasce com o método e o parcelamento, e o link já pode ir.
                        // Antes disto ela nascia sem método e o operador tinha de
                        // abrir a cobrança para definir e só então enviar.
                        ...paymentFieldsForSubmit(),
                        // ...ou cobrança nenhuma, quando o paciente paga no dia.
                        ...deferBillingFieldForSubmit(),
                    } as any
                    const response = await createExam(data)
                    if (response.success && response.result) {
                        toast.success(
                            isBudgetMode
                                ? 'Orçamento de pacote criado!'
                                : 'Pacote agendado com sucesso!'
                        )
                        const result = response.result as any
                        // Orçamento não gera cobrança: vai direto para os detalhes.
                        if (isBudgetMode && result?.id)
                            navigate(`/bookings/exams/${result.id}`)
                        else
                            finishCreate(
                                result?.billingId,
                                '/bookings/exams',
                                result?.id
                                    ? `/bookings/exams/${result.id}`
                                    : undefined
                            )
                    } else {
                        toast.error(response.error || 'Erro ao agendar pacote')
                    }
                } else {
                    const data: CreateOrderData = {
                        // Local de atendimento confirmado (snapshot de auditoria).
                        ...(serviceAddress || {}),
                        catalogId: pkg.id,
                        status: isBudgetMode
                            ? ('BUDGET' as any)
                            : ((loadedStatus && loadedStatus !== 'BUDGET'
                                  ? loadedStatus
                                  : 'SCHEDULED') as any),
                        description: notes || undefined,
                        notes: notes || undefined,
                        scheduledAt: scheduledAtUTC,
                        arrivalOrder,
                        validUntil:
                            isBudgetMode && validUntil ? validUntil : undefined,
                        duration: duration ? duration : undefined,
                        roomId: roomId || undefined,
                        clientId: parseInt(clientId, 10),
                        professionalId: profId,
                        partnerId: partnerId || undefined,
                        healthPlanId: healthPlanId || undefined,
                        hospitalId: hospitalId || undefined,
                        companyId: user.companyId,
                        discount: discountForSubmit,
                        ...couponFieldForSubmit(),
                        pipelineId: pipelineId || undefined,
                        billingTargetId: destinoDaSoma,
                        // Forma fechada com o pagador na própria tela: a cobrança
                        // nasce com o método e o parcelamento, e o link já pode ir.
                        // Antes disto ela nascia sem método e o operador tinha de
                        // abrir a cobrança para definir e só então enviar.
                        ...paymentFieldsForSubmit(),
                        // ...ou cobrança nenhuma, quando o paciente paga no dia.
                        ...deferBillingFieldForSubmit(),
                    } as any
                    const response = await createOrder(data)
                    if (response.success && response.result) {
                        toast.success(
                            isBudgetMode
                                ? 'Orçamento de pacote criado!'
                                : 'Pacote agendado com sucesso!'
                        )
                        const result = response.result as any
                        // Orçamento não gera cobrança: vai direto para os detalhes.
                        if (isBudgetMode && result?.id)
                            navigate(`/bookings/orders/${result.id}`)
                        else
                            finishCreate(
                                result?.billingId,
                                '/bookings/orders',
                                result?.id
                                    ? `/bookings/orders/${result.id}`
                                    : undefined
                            )
                    } else {
                        toast.error(response.error || 'Erro ao agendar pacote')
                    }
                }
            } else if (serviceType === 'TELECONSULTATION') {
                if (isEditing) {
                    const response = await updateTeleconsultation(recordId, {
                        scheduledAt: scheduledAtUTC
                            ? new Date(scheduledAt).toISOString()
                            : undefined,
                        maxDuration: parseInt(maxTeleDuration, 10),
                        recordingEnabled,
                        enableChat,
                        enableScreenShare,
                        notes: notes || undefined,
                        specialtyId: specialtyId || undefined,
                        price: telePrice
                            ? parseCurrencyToCents(telePrice)
                            : undefined,
                        sellingPrice: teleSellingPrice
                            ? parseCurrencyToCents(teleSellingPrice)
                            : undefined,
                        markupPercentage: teleMarkup
                            ? parseFloat(teleMarkup)
                            : undefined,
                        discount: discountForSubmit,
                        ...paymentFieldsForSubmit(),
                    })
                    if (response.success) {
                        toast.success('Teleconsulta atualizada com sucesso!')
                        navigate(`/bookings/teleconsultations/${recordId}`)
                    } else {
                        toast.error(
                            response.error || 'Erro ao atualizar teleconsulta'
                        )
                    }
                } else {
                    const scheduledAtTele = scheduledAt
                        ? new Date(scheduledAt).toISOString()
                        : ''
                    const response = await createTeleconsultation({
                        clientId: parseInt(clientId, 10),
                        professionalId: profId!,
                        companyId: user.companyId,
                        scheduledAt: scheduledAtTele,
                        maxDuration: parseInt(maxTeleDuration, 10),
                        recordingEnabled,
                        enableChat,
                        enableScreenShare,
                        notes: notes || undefined,
                        title: teleTitle || undefined,
                        isReturn: teleIsReturn,
                        specialtyId: specialtyId || undefined,
                        price: telePrice
                            ? parseCurrencyToCents(telePrice)
                            : undefined,
                        sellingPrice: teleSellingPrice
                            ? parseCurrencyToCents(teleSellingPrice)
                            : undefined,
                        markupPercentage: teleMarkup
                            ? parseFloat(teleMarkup)
                            : undefined,
                        discount: discountForSubmit || undefined,
                        ...paymentFieldsForSubmit(),
                        partnerId: partnerId || undefined,
                        healthPlanId: healthPlanId || undefined,
                        pipelineId: pipelineId || undefined,
                    })
                    if (response.success && response.result) {
                        toast.success('Teleconsulta criada com sucesso!')
                        navigate(
                            `/bookings/teleconsultations/${response.result.id}`
                        )
                    } else {
                        toast.error(
                            response.error || 'Erro ao criar teleconsulta'
                        )
                    }
                }
            }
        } catch {
            toast.error('Erro ao salvar')
        } finally {
            setLoading(false)
        }
    }

    const backRoute =
        serviceType === 'EXAM'
            ? '/bookings/exams'
            : serviceType === 'PROCEDURE'
              ? '/bookings/orders'
              : /*: serviceType === 'TELECONSULTATION'
                ? '/bookings/teleconsultations'*/
                '/bookings/consultations'

    /* Índice lateral. É âncora, não aba: o conteúdo continua inteiro na página
       porque o operador precisa ver item e valor ao mesmo tempo, e a validação
       desta tela é por toast (nada trava se um campo estiver fora de vista). */
    const hasDetailsSection =
        serviceType === 'CONSULTATION' ||
        serviceType === 'PROCEDURE' ||
        (serviceType === 'PACKAGE' && !!selectedCatalog)

    const sections = useMemo(
        () =>
            [
                {
                    id: 'secao-atendimento',
                    label: `${ClientLabel} e data`,
                    icon: <MdPerson className="text-lg" />,
                    visible: true,
                },
                {
                    id: 'secao-servico',
                    label: 'Serviço e itens',
                    icon: <MdInventory2 className="text-lg" />,
                    visible: !isTeleconsultation,
                },
                {
                    id: 'secao-valores',
                    label: 'Valores e local',
                    icon: <MdPayments className="text-lg" />,
                    visible: !isTeleconsultation,
                },
                {
                    id: 'secao-detalhes',
                    label: 'Detalhes',
                    icon: <MdTune className="text-lg" />,
                    visible: hasDetailsSection,
                },
                {
                    id: 'secao-observacoes',
                    label: 'Observações',
                    icon: <MdNotes className="text-lg" />,
                    visible: true,
                },
            ].filter(section => section.visible),
        [isTeleconsultation, hasDetailsSection]
    )

    const sectionKey = sections.map(section => section.id).join('|')

    useEffect(() => {
        if (typeof IntersectionObserver === 'undefined') return
        const observer = new IntersectionObserver(
            entries => {
                // A primeira seção que cruza a faixa de leitura manda; sem isso,
                // duas seções visíveis brigam pelo destaque a cada pixel rolado.
                const first = entries
                    .filter(entry => entry.isIntersecting)
                    .sort(
                        (a, b) =>
                            a.boundingClientRect.top - b.boundingClientRect.top
                    )[0]
                if (first) setActiveSection(first.target.id)
            },
            { rootMargin: '-120px 0px -55% 0px', threshold: 0 }
        )
        sectionKey.split('|').forEach(id => {
            const element = document.getElementById(id)
            if (element) observer.observe(element)
        })
        return () => observer.disconnect()
    }, [sectionKey])

    if (loadingData) {
        return (
            <div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-sm text-base-content/60">
                    Carregando dados...
                </p>
            </div>
        )
    }

    return (
        <div
            className={`mx-auto flex max-w-6xl flex-col gap-5 ${floatingBarVisible ? 'pb-32' : ''}`}
        >
            <SchedulingHeader
                isEditing={isEditing}
                isBudgetMode={isBudgetMode}
                serviceType={serviceType}
                serviceTypeLabels={serviceTypeLabels}
                availableServiceTypes={availableServiceTypes}
                onBack={() => navigate(backRoute)}
                onServiceTypeChange={handleServiceTypeChange}
            />

            <SchedulingSummaryBar
                clientName={clientName}
                clientLabel={ClientLabel}
                serviceTypeLabel={serviceTypeLabels[serviceType]}
                isBudgetMode={isBudgetMode}
                price={chargedSummaryPrice}
                quoteTotal={summaryQuote ? summaryQuote.total : null}
                quoteLabel={summaryQuoteLabel}
            />

            <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[212px_minmax(0,1fr)]">
                <SchedulingSectionNav
                    sections={sections}
                    activeSection={activeSection}
                />

                <form
                    onSubmit={handleSubmit}
                    onPointerDownCapture={markPricingTouched}
                    onChangeCapture={markPricingTouched}
                    onKeyDownCapture={markPricingTouched}
                    className="flex min-w-0 flex-col gap-5"
                >
                    {/* Paciente antes do serviço: escolher o exame antes de
                        saber para quem era a ordem invertida da tela antiga. */}
                    <FormSection
                        id="secao-atendimento"
                        icon={<MdPerson className="text-lg" />}
                        title={
                            isBudgetMode
                                ? `${ClientLabel} e orçamento`
                                : `${ClientLabel} e agendamento`
                        }
                        subtitle="Para quem é e quando acontece"
                        actions={
                            !isEditing &&
                            !urlPresetBudget &&
                            canToggleBudgetMode ? (
                                <label
                                    className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 ${
                                        isBudgetMode
                                            ? 'border-warning/40 bg-warning/10'
                                            : 'border-base-300 bg-base-100'
                                    } ${forceBudgetMode ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
                                    title={
                                        forceBudgetMode
                                            ? 'Este item do catálogo é exclusivo para orçamento'
                                            : undefined
                                    }
                                >
                                    <span className="text-xs font-medium text-base-content/70">
                                        Orçamento
                                    </span>
                                    <input
                                        type="checkbox"
                                        className="toggle toggle-warning toggle-xs"
                                        checked={isBudgetMode}
                                        disabled={forceBudgetMode}
                                        onChange={e =>
                                            setIsBudgetMode(e.target.checked)
                                        }
                                    />
                                </label>
                            ) : isBudgetMode ? (
                                <span className="badge badge-warning badge-sm">
                                    Orçamento
                                </span>
                            ) : null
                        }
                    >
                        {/* Duas pilhas independentes, não um grid de linhas: com
                            grid, a linha toda herdava a altura de "Data e hora" (o
                            campo mais alto) e abria um vão embaixo do paciente. */}
                        <div className="grid grid-cols-1 items-start gap-5 md:grid-cols-2">
                            {/* Quem é atendido e sob qual acordo comercial. */}
                            <div className="flex flex-col gap-5">
                                <ClientSelector
                                    value={
                                        clientId ? parseInt(clientId, 10) : null
                                    }
                                    onChange={id =>
                                        setClientId(id ? id.toString() : '')
                                    }
                                    label={`${ClientLabel} *`}
                                    placeholder={`Buscar ${clientLabel}...`}
                                />

                                {/* Profissional para teleconsulta (que não tem catálogo) */}
                                {isTeleconsultation && canSeeProfessionals && (
                                    <ProfessionalSearchSelector
                                        value={
                                            professionalId
                                                ? parseInt(professionalId, 10)
                                                : null
                                        }
                                        onChange={pid =>
                                            setProfessionalId(
                                                pid ? pid.toString() : ''
                                            )
                                        }
                                        label="Profissional"
                                        placeholder="Buscar profissional..."
                                    />
                                )}

                                {isHealth && (
                                    <HealthPlanSelector
                                        value={healthPlanId}
                                        onChange={id => {
                                            // Na edição o auto-preenchimento está
                                            // desligado: chegar aqui é gesto do
                                            // operador, e é ele que autoriza a
                                            // cobrança do convênio a nascer.
                                            if (isEditing)
                                                healthPlanChoiceTouchedRef.current =
                                                    true
                                            setHealthPlanId(id)
                                        }}
                                        clientId={
                                            clientId
                                                ? parseInt(clientId, 10)
                                                : null
                                        }
                                        label="Convênio (opcional)"
                                        disabled={healthPlanLocked}
                                        // Na edição o convênio vem do registro:
                                        // preencher sozinho pelo cadastro do
                                        // paciente trocaria o pagador sem pedido.
                                        autoSelectFromClient={!isEditing}
                                        hint={
                                            healthPlanLocked
                                                ? 'O atendimento já tem cobrança: o convênio define quem paga e não muda depois que ela existe. Cancele a cobrança e gere-a novamente para trocar.'
                                                : undefined
                                        }
                                    />
                                )}

                                <PipelineSelector
                                    value={pipelineId}
                                    onChange={setPipelineId}
                                />

                                {/* Parceiro para teleconsulta (que não tem catálogo) */}
                                {isTeleconsultation && !hidePartnerSelector && (
                                    <PartnerSelector
                                        value={partnerId}
                                        onChange={pid => setPartnerId(pid)}
                                        label="Parceiro (opcional)"
                                    />
                                )}
                            </div>

                            {/* Quando e onde acontece. */}
                            <ScheduleWhenFields
                                isBudgetMode={isBudgetMode}
                                isTeleconsultation={isTeleconsultation}
                                serviceType={serviceType}
                                scheduledDate={scheduledDate}
                                onScheduledDateChange={setScheduledDate}
                                scheduledTime={scheduledTime}
                                onScheduledTimeChange={setScheduledTime}
                                arrivalOrder={arrivalOrder}
                                onArrivalOrderChange={checked => {
                                    setArrivalOrder(checked)
                                    if (checked) setScheduledTime('')
                                }}
                                validUntil={validUntil}
                                onValidUntilChange={setValidUntil}
                                rooms={rooms}
                                roomId={roomId}
                                onRoomChange={setRoomId}
                            />
                        </div>

                        {isEditing &&
                            !isBudgetMode &&
                            !!originalScheduledAt &&
                            !!scheduledAt &&
                            scheduledAt !== originalScheduledAt && (
                                <RescheduleReasonField
                                    clientLabel={clientLabel}
                                    reason={rescheduleReason}
                                    onReasonChange={setRescheduleReason}
                                />
                            )}
                    </FormSection>

                    {!isTeleconsultation && (
                        <FormSection
                            id="secao-servico"
                            icon={<MdInventory2 className="text-lg" />}
                            title={`${serviceTypeLabels[serviceType]} do catálogo`}
                            subtitle="O que será realizado"
                            actions={
                                serviceType === 'EXAM' ? (
                                    <div className="flex flex-wrap items-center gap-2">
                                        <input
                                            ref={examImportInputRef}
                                            type="file"
                                            accept="image/*,application/pdf"
                                            className="hidden"
                                            onChange={handleExamDocImport}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline btn-secondary gap-2"
                                            title="Enviar PDF ou foto do pedido de exames — a IA extrai e adiciona automaticamente"
                                            onClick={() =>
                                                examImportInputRef.current?.click()
                                            }
                                            disabled={importingExamDoc}
                                        >
                                            {importingExamDoc ? (
                                                <>
                                                    <span className="loading loading-spinner loading-xs" />
                                                    Processando...
                                                </>
                                            ) : (
                                                <>
                                                    <MdAutoAwesome className="text-lg" />
                                                    Importar lista
                                                </>
                                            )}
                                        </button>

                                        {canCompareCatalog && (
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline btn-secondary gap-2"
                                                onClick={() =>
                                                    setShowPartnerComparison(
                                                        true
                                                    )
                                                }
                                            >
                                                Comparar parceiros
                                            </button>
                                        )}
                                    </div>
                                ) : null
                            }
                        >
                            <div className="relative" ref={catalogDropdownRef}>
                                {selectedCatalog &&
                                serviceType !== 'EXAM' &&
                                serviceType !== 'PROCEDURE' ? (
                                    <FormField
                                        label="Item selecionado"
                                        required
                                    >
                                        <div className="flex w-full items-center justify-between rounded-lg border border-base-300 bg-base-200 px-4 py-2.5">
                                            <span className="truncate font-medium">
                                                {selectedCatalog.name}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={handleClearCatalog}
                                                className="btn btn-xs btn-ghost shrink-0 text-error"
                                                title="Trocar item"
                                            >
                                                <MdClose className="text-sm" />
                                            </button>
                                        </div>
                                    </FormField>
                                ) : (
                                    <>
                                        <FormField
                                            label={`Buscar no catálogo`}
                                            htmlFor="catalogo-busca"
                                            required
                                        >
                                            <input
                                                id="catalogo-busca"
                                                type="text"
                                                className="input input-bordered w-full"
                                                placeholder={`Buscar ${serviceTypeLabels[
                                                    serviceType
                                                ].toLowerCase()} no catálogo...`}
                                                value={catalogSearch}
                                                onChange={e => {
                                                    setCatalogSearch(
                                                        e.target.value
                                                    )
                                                    setShowCatalogDropdown(true)
                                                }}
                                                onFocus={() =>
                                                    setShowCatalogDropdown(true)
                                                }
                                            />
                                            <label className="flex cursor-pointer items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    className="checkbox checkbox-sm"
                                                    checked={searchAllCatalog}
                                                    onChange={e => {
                                                        setSearchAllCatalog(
                                                            e.target.checked
                                                        )
                                                        setShowCatalogDropdown(
                                                            true
                                                        )
                                                    }}
                                                />
                                                <span className="text-xs text-base-content/60">
                                                    Buscar em todo o catálogo
                                                </span>
                                            </label>
                                        </FormField>
                                        {showCatalogDropdown && (
                                            <div className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-base-300 bg-base-100 shadow-xl">
                                                {loadingCatalogs ? (
                                                    <div className="p-4 text-center">
                                                        <span className="loading loading-spinner loading-sm"></span>
                                                    </div>
                                                ) : filteredCatalogs.length ===
                                                  0 ? (
                                                    <div className="p-3">
                                                        <p className="py-2 text-center text-sm text-base-content/60">
                                                            {catalogSearch
                                                                ? 'Nenhum item encontrado'
                                                                : 'Nenhum item no catálogo'}
                                                        </p>
                                                        {/* Cadastro rápido. Com parceiro escolhido a
                                                            busca é funilada por ele, então "não achei"
                                                            pode significar só "não é deste parceiro" —
                                                            quem resolve isso é o CatalogQuickCreate,
                                                            que refaz a busca sem o filtro. */}
                                                        {canQuickCreateCatalog && (
                                                            <button
                                                                type="button"
                                                                className="flex w-full items-center gap-2 rounded-lg p-2 text-left text-sm font-medium text-secondary hover:bg-base-200"
                                                                onClick={() => {
                                                                    setShowCatalogDropdown(
                                                                        false
                                                                    )
                                                                    setShowQuickCreate(
                                                                        true
                                                                    )
                                                                }}
                                                            >
                                                                <MdAdd className="shrink-0 text-lg" />
                                                                <span className="truncate">
                                                                    Adicionar "
                                                                    {catalogSearch.trim()}
                                                                    "
                                                                </span>
                                                            </button>
                                                        )}
                                                        {quickCreateAllowed &&
                                                            !canQuickCreateCatalog &&
                                                            catalogNeedsPartner &&
                                                            !partnerId &&
                                                            !isEditing &&
                                                            catalogSearch.trim() && (
                                                                <p className="px-2 pb-1 text-xs text-base-content/60">
                                                                    Selecione o
                                                                    parceiro
                                                                    para
                                                                    cadastrar um
                                                                    item novo —
                                                                    o preço é
                                                                    dele.
                                                                </p>
                                                            )}
                                                    </div>
                                                ) : (
                                                    filteredCatalogs
                                                        .filter(c =>
                                                            serviceType ===
                                                            'PROCEDURE'
                                                                ? !orderItems.some(
                                                                      i =>
                                                                          i.catalogId ===
                                                                          c.id
                                                                  )
                                                                : serviceType !==
                                                                      'EXAM' ||
                                                                  !examItems.some(
                                                                      i =>
                                                                          i.catalogId ===
                                                                          c.id
                                                                  )
                                                        )
                                                        .map(catalog => (
                                                            <button
                                                                key={catalog.id}
                                                                type="button"
                                                                className="w-full border-b border-base-300/50 p-3 text-left last:border-b-0 hover:bg-base-200"
                                                                onClick={() =>
                                                                    handleCatalogSelect(
                                                                        catalog
                                                                    )
                                                                }
                                                            >
                                                                <div className="flex items-center justify-between">
                                                                    <span className="font-medium">
                                                                        {
                                                                            catalog.name
                                                                        }
                                                                    </span>
                                                                    {catalogBasePrice(
                                                                        catalog,
                                                                        user?.companyId
                                                                    ) > 0 ? (
                                                                        <span className="ml-2 text-sm font-medium text-secondary">
                                                                            {formatCentsLabel(
                                                                                catalogBasePrice(
                                                                                    catalog,
                                                                                    user?.companyId
                                                                                )
                                                                            )}
                                                                        </span>
                                                                    ) : null}
                                                                </div>
                                                            </button>
                                                        ))
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            {serviceType === 'EXAM' && (
                                <UnmatchedImportsPanel
                                    names={unmatchedImports}
                                    onClear={() => setUnmatchedImports([])}
                                    onPick={name => {
                                        setCatalogSearch(name)
                                        setShowCatalogDropdown(true)
                                        setUnmatchedImports(prev =>
                                            prev.filter(n => n !== name)
                                        )
                                    }}
                                />
                            )}

                            {serviceType === 'EXAM' && (
                                <SelectedItemsTable
                                    items={examItems}
                                    setItems={setExamItems}
                                    itemColumnLabel="Item"
                                    removeTitle="Remover item"
                                    renderVariationControls={
                                        renderVariationControls
                                    }
                                />
                            )}

                            {serviceType === 'PROCEDURE' && (
                                <SelectedItemsTable
                                    items={orderItems}
                                    setItems={setOrderItems}
                                    itemColumnLabel={serviceLabels.Order}
                                    showTypeColumn
                                    removeTitle={`Remover ${serviceLabels.order}`}
                                    renderVariationControls={
                                        renderVariationControls
                                    }
                                />
                            )}

                            {/*
                              * Item anexado sem preço no cadastro do executante. Aparece
                              * assim que o item entra na pilha — e não só ao tentar salvar —
                              * porque o operador ainda está com o cadastro à mão nesse
                              * momento. O submit é recusado enquanto isso não for resolvido.
                              */}
                            {itemsMissingExecutantPrice.length > 0 && (
                                <div className="mt-4 flex items-start gap-3 rounded-xl border border-warning/40 bg-warning/10 p-4">
                                    <MdInfoOutline className="mt-0.5 shrink-0 text-lg text-warning" />
                                    <div className="text-sm leading-relaxed text-base-content/80">
                                        <p className="font-medium">
                                            Cadastre o preço no catálogo antes
                                            de salvar
                                        </p>
                                        <ul className="mt-2 list-disc space-y-1 pl-5">
                                            {itemsMissingExecutantPrice.map(
                                                issue => (
                                                    <li key={issue.key}>
                                                        <span className="font-medium">
                                                            {issue.name}
                                                        </span>
                                                        : falta {issue.missing}{' '}
                                                        para {issue.executant}
                                                    </li>
                                                )
                                            )}
                                        </ul>
                                    </div>
                                </div>
                            )}

                            {/* Variações da consulta (multi-seleção) */}
                            {serviceType === 'CONSULTATION' &&
                                selectedCatalog && (
                                    <ConsultationVariationsPicker
                                        variations={
                                            variationsByCatalog[
                                                selectedCatalog.id
                                            ] || []
                                        }
                                        selected={consultationVariations}
                                        priceOf={variation =>
                                            variationDisplayPrice(
                                                variation,
                                                partnerId,
                                                professionalId
                                                    ? Number(professionalId)
                                                    : null
                                            )
                                        }
                                        priceFromOf={variationPriceFrom}
                                        onToggle={(variation, checked) =>
                                            setConsultationVariations(prev =>
                                                checked
                                                    ? [
                                                          ...prev,
                                                          {
                                                              catalogVariationId:
                                                                  variation.id,
                                                              quantity: 1,
                                                              name: variation.name,
                                                              unitPrice:
                                                                  variationDisplayPrice(
                                                                      variation,
                                                                      partnerId,
                                                                      professionalId
                                                                          ? Number(
                                                                                professionalId
                                                                            )
                                                                          : null
                                                                  ),
                                                          },
                                                      ]
                                                    : prev.filter(
                                                          item =>
                                                              item.catalogVariationId !==
                                                              variation.id
                                                      )
                                            )
                                        }
                                    />
                                )}
                        </FormSection>
                    )}

                    {!isTeleconsultation && (
                        <FormSection
                            id="secao-valores"
                            icon={<MdPayments className="text-lg" />}
                            title="Valores e responsáveis"
                            subtitle="Quem executa, quanto custa e onde é feito"
                        >
                            {/* Retorno antes de tudo: quando a configuração
                                financeira não cobra pelo retorno, marcar aqui
                                esconde a forma de pagamento logo abaixo — a
                                escolha precisa vir ANTES do pagamento, não no
                                fim do formulário. */}
                            {serviceType === 'CONSULTATION' && (
                                <div className="flex flex-col gap-2 rounded-xl border border-base-300 bg-base-200/40 p-4">
                                    <label className="flex cursor-pointer items-center gap-3">
                                        <input
                                            type="checkbox"
                                            className="toggle toggle-secondary"
                                            checked={isReturn}
                                            onChange={e =>
                                                setIsReturn(e.target.checked)
                                            }
                                        />
                                        <span className="text-sm font-medium">
                                            Consulta de retorno
                                        </span>
                                    </label>
                                    <p className="text-xs leading-relaxed text-base-content/60">
                                        {chargeReturnConsultation
                                            ? 'A configuração financeira cobra pelo retorno: a cobrança é gerada normalmente.'
                                            : 'A configuração financeira não cobra pelo retorno: ao marcar, o agendamento é feito sem cobrança.'}
                                    </p>
                                </div>
                            )}

                            {/* Profissional, Parceiro e Preços */}
                            <PricingSection
                                paymentMethod={paymentMethod || null}
                                installmentCount={installmentCount}
                                groupingIntoBilling={agrupando}
                                // Fluxo antigo: cobrança nasce sem forma e o
                                // operador é levado à tela de pagamento, onde
                                // existem dinheiro, maquininha e o split.
                                onPaymentCustom={handlePaymentCustom}
                                deferBillingSelected={deferBilling}
                                onPaymentDefer={handlePaymentDefer}
                                onPaymentChoice={handlePaymentChoice}
                                basePrice={basePrice}
                                catalogPricingMode={catalogPricingMode}
                                catalogProfessionals={
                                    catalogActiveProfessionals
                                }
                                catalogPartners={catalogPartnersForSelector}
                                professionalId={professionalId}
                                onProfessionalIdChange={setProfessionalId}
                                partnerId={partnerId}
                                onPartnerIdChange={setPartnerId}
                                partnerDocument={partnerDocument}
                                discount={discount}
                                onDiscountChange={setDiscount}
                                maxDiscount={maxAllowedDiscount}
                                canEditFinalPrice={canEditFinalPrice}
                                isEditing={isEditing}
                                // Flex fica de fora do aviso de ajuste: não há
                                // complemento nem estorno a anunciar — o backend nem
                                // chama `adjustAppointmentTotal` nesse caso, e o saldo
                                // do portal acompanha o novo valor sozinho. Prometer
                                // "será criada uma cobrança de X" seria falso.
                                existingBilling={
                                    flexPayment ? null : existingBilling
                                }
                                // Cobrança compartilhada: sem saber qual atendimento
                                // é o nosso, o aviso compararia o valor deste contra
                                // o total do grupo e anunciaria uma queda que não
                                // existe. Mesma informação que o backend exige em
                                // `adjustAppointmentTotal`.
                                adjustmentSource={
                                    isEditing && id && !flexPayment
                                        ? {
                                              sourceType: serviceType,
                                              sourceId: parseInt(id, 10),
                                          }
                                        : null
                                }
                                hidePartnerSelector={hidePartnerSelector}
                                hideProfessionalSelector={
                                    hideProfessionalSelector
                                }
                                hasCatalogSelected={hasCatalogSelected}
                                priceComposition={priceComposition}
                                onPriceCompositionChange={setPriceComposition}
                                examItems={
                                    serviceType === 'EXAM'
                                        ? examItems
                                        : undefined
                                }
                                // Procedimento também é multi-item: sem isto, o
                                // honorário do parceiro/profissional não
                                // multiplicava pela quantidade na tela, embora o
                                // backend multiplique ao gravar.
                                orderItems={
                                    serviceType === 'PROCEDURE'
                                        ? orderItems
                                        : undefined
                                }
                                hasConsultationVariations={
                                    serviceType === 'CONSULTATION' &&
                                    consultationVariations.length > 0
                                }
                                catalogs={
                                    serviceType === 'EXAM' ||
                                    serviceType === 'PROCEDURE'
                                        ? Object.values(catalogsById)
                                        : undefined
                                }
                                serviceType={serviceType}
                                extraTotal={
                                    serviceType === 'PROCEDURE'
                                        ? orderExamsTotal +
                                          orderConsultationsTotal +
                                          medicalSuppliesTotal
                                        : serviceType === 'EXAM'
                                          ? medicalSuppliesTotal
                                          : 0
                                }
                                onFloatingBarChange={setFloatingBarVisible}
                                onSubmittedPriceChange={setSummaryPrice}
                                onPaymentQuoteChange={setPaymentQuote}
                                isBudgetMode={isBudgetMode}
                                overridePrice={overridePrice}
                                couponAmount={appliedCoupon?.amount ?? 0}
                                onOverridePriceChange={setOverridePrice}
                                // Também espera os catálogos referenciados: ver a nota
                                // em `catalogsHydrating` — é o que impede o valor final
                                // salvo de subir sozinho quando um vínculo chega tarde.
                                pricingReady={
                                    !loadingData &&
                                    !loadingCatalogs &&
                                    !catalogsHydrating
                                }
                                pricingTouched={pricingTouched}
                                // Flex: o valor é o acordo com quem já está
                                // pagando. Trocar o procedimento, a taxa do
                                // hospital ou um material não redefine sozinho
                                // quanto o paciente deve — quem decide o novo
                                // valor é o operador, digitando.
                                autoAdjustFinalPrice={!flexPayment}
                                savedCostPrice={savedCostPrice}
                                savedMarkup={savedMarkup}
                                hidePaymentOptions={flexPayment}
                                returnWithoutBilling={returnWithoutBilling}
                                /**
                                 * Flex só onde ele existe e ainda cabe: PROCEDIMENTO,
                                 * numa edição, fora do orçamento (é a aprovação que
                                 * decide o regime) e que ainda não seja flex — as
                                 * mesmas condições que `enableFlexPayment` valida no
                                 * backend. Na criação não há procedimento para ligar.
                                 */
                                onEnableFlex={
                                    serviceType === 'PROCEDURE' &&
                                    isEditing &&
                                    !isBudgetMode &&
                                    !flexPayment
                                        ? () => {
                                              setFlexRequested(true)
                                              // No flex ninguém acerta forma nem
                                              // parcela na venda: quem escolhe é o
                                              // paciente, a cada pagamento avulso.
                                              setPaymentMethod('')
                                              setInstallmentCount(null)
                                          }
                                        : undefined
                                }
                                flexSelected={flexRequested}
                                requireSellingPrice={requireSellingPrice}
                                onCostMarkupChange={handleCostMarkupChange}
                                hospitalFee={
                                    serviceType === 'PROCEDURE' && hospitalId
                                        ? parseCurrencyToCents(hospitalFee)
                                        : 0
                                }
                                anesthetistFee={
                                    serviceType === 'PROCEDURE' &&
                                    anesthetistFee
                                        ? parseCurrencyToCents(anesthetistFee)
                                        : 0
                                }
                                professionalAmountOverride={
                                    professionalAmountOverride
                                }
                                professionalAmountFromCatalog={
                                    professionalAmountFromCatalog
                                }
                                onProfessionalAmountOverrideChange={(
                                    value,
                                    fromCatalog
                                ) => {
                                    setProfessionalAmountOverride(value)
                                    setProfessionalAmountFromCatalog(
                                        !!fromCatalog
                                    )
                                }}
                            />

                            {canUseCoupons &&
                                !isBudgetMode &&
                                (serviceType === 'EXAM' ||
                                    serviceType === 'CONSULTATION' ||
                                    serviceType === 'PROCEDURE') && (
                                    <CouponField
                                        applied={appliedCoupon}
                                        manualPrice={summaryPrice}
                                        applying={applyingCoupon}
                                        onApply={handleApplyCoupon}
                                        onRemove={() => setAppliedCoupon(null)}
                                    />
                                )}

                            <PayoutRedirectNotice preview={payoutRedirect} />

                            {/* Hospital depois de quem executa: o endereço proposto no
                                local de atendimento vem dele, e a taxa dele entra na
                                conta — por isso fica antes do local, e não no fim.
                                Só na saúde: fora dela `hospitalId` fica null e o
                                endereço proposto cai no fallback (parceiro/profissional). */}
                            {isHealth && (
                            <div className="flex flex-col gap-3">
                                <HospitalSearchSelector
                                    value={hospitalId}
                                    onChange={id => {
                                        setHospitalId(id)
                                        setHospitalIdTouched(true)
                                    }}
                                    label={
                                        suggestedHospitals.length > 0
                                            ? 'Hospital (sugerido pelo item do catálogo)'
                                            : 'Hospital (opcional)'
                                    }
                                    placeholder="Buscar hospital..."
                                />
                                {suggestedHospitals.length > 0 && (
                                    <div>
                                        <p className="mb-1.5 text-xs text-base-content/50">
                                            Sugestões do catálogo:
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {suggestedHospitals.map(h => (
                                                <button
                                                    key={h.id}
                                                    type="button"
                                                    className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                                                        hospitalId === h.id
                                                            ? 'border-secondary bg-secondary/10 text-secondary'
                                                            : 'border-base-300 text-base-content/60 hover:bg-base-200'
                                                    }`}
                                                    onClick={() => {
                                                        setHospitalId(h.id)
                                                        setHospitalIdTouched(
                                                            true
                                                        )
                                                    }}
                                                >
                                                    {h.name}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {serviceType === 'PROCEDURE' &&
                                    hospitalId != null && (
                                        <div className="flex flex-col gap-4 rounded-xl border border-base-300 bg-base-200/40 p-4">
                                            <p className="text-xs font-medium text-base-content/60">
                                                Valores cobrados pelo hospital
                                                (diárias, taxas)
                                            </p>
                                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                                <FormField
                                                    label="Valor do hospital"
                                                    htmlFor="hospital-valor"
                                                >
                                                    <input
                                                        id="hospital-valor"
                                                        type="text"
                                                        className="input input-bordered input-sm"
                                                        placeholder="R$ 0,00"
                                                        value={hospitalFee}
                                                        onChange={e =>
                                                            setHospitalFee(
                                                                currencyFormat(
                                                                    e.target
                                                                        .value,
                                                                    true
                                                                )
                                                            )
                                                        }
                                                    />
                                                </FormField>
                                                <FormField
                                                    label="Descrição"
                                                    htmlFor="hospital-descricao"
                                                    optional
                                                >
                                                    <input
                                                        id="hospital-descricao"
                                                        type="text"
                                                        className="input input-bordered input-sm"
                                                        placeholder="Ex: 3 diárias UTI"
                                                        value={hospitalFeeNotes}
                                                        onChange={e =>
                                                            setHospitalFeeNotes(
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </FormField>
                                            </div>
                                        </div>
                                    )}
                            </div>
                            )}

                            {/* Local de atendimento (confirmação obrigatória ao agendar).
                                Fica DEPOIS de parceiro/profissional de propósito: o
                                endereço proposto vem deles, e confirmar antes de
                                escolher significaria confirmar o endereço errado. */}
                            {!isEditing && !isBudgetMode && (
                                <div className="flex flex-col gap-3 border-t border-base-300 pt-5">
                                    <div className="flex items-center gap-2">
                                        <MdLocationOn className="text-xl text-secondary" />
                                        <h3 className="text-sm font-semibold text-base-content">
                                            Local de atendimento
                                        </h3>
                                    </div>
                                    <ServiceAddressSection
                                        partnerId={partnerId}
                                        professionalId={professionalId}
                                        hospitalId={hospitalId}
                                        value={serviceAddress}
                                        onChange={setServiceAddress}
                                    />
                                </div>
                            )}
                        </FormSection>
                    )}

                    {/* Campos específicos por tipo */}
                    {serviceType === 'PACKAGE' && selectedCatalog && (
                        <FormSection
                            id="secao-detalhes"
                            icon={<MdTune className="text-lg" />}
                            title="Itens inclusos no pacote"
                            subtitle="Agendados juntos e cobrados como um único pacote"
                            actions={
                                <span className="badge badge-sm badge-success">
                                    {selectedCatalog.packageItems?.length || 0}
                                </span>
                            }
                        >
                            {isHealth && (
                                <p className="text-xs leading-relaxed text-base-content/50">
                                    Cada componente entra na guia com o seu
                                    próprio código TUSS.
                                </p>
                            )}
                            {selectedCatalog.packageItems &&
                            selectedCatalog.packageItems.length > 0 ? (
                                <div className="overflow-x-auto rounded-xl border border-base-300">
                                    <table className="table table-sm">
                                        <thead>
                                            <tr className="text-xs text-base-content/50">
                                                <th>Componente</th>
                                                <th>Categoria</th>
                                                {isHealth && <th>TUSS</th>}
                                                <th>Qtd</th>
                                                <th>Cortesia</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedCatalog.packageItems.map(
                                                pi => (
                                                    <tr
                                                        key={pi.id}
                                                        className={
                                                            pi.complimentary
                                                                ? 'opacity-60'
                                                                : ''
                                                        }
                                                    >
                                                        <td className="font-medium">
                                                            {pi.item?.name ??
                                                                '-'}
                                                        </td>
                                                        <td>
                                                            {/* Serviço e mercadoria são
                                                                ambos item de catálogo: o
                                                                selo vem da categoria. */}
                                                            <span
                                                                className={`badge badge-xs ${pi.kind === 'PRODUCT' ? 'badge-info' : 'badge-ghost'}`}
                                                            >
                                                                {pi.item
                                                                    ? categoryLabels[
                                                                          pi
                                                                              .item
                                                                              .category
                                                                      ]
                                                                    : 'Componente'}
                                                            </span>
                                                        </td>
                                                        {isHealth && (
                                                            <td className="font-mono text-xs">
                                                                {pi.item
                                                                    ?.tussCode ||
                                                                    '-'}
                                                            </td>
                                                        )}
                                                        <td>{pi.quantity}</td>
                                                        <td>
                                                            {pi.complimentary ? (
                                                                <span className="badge badge-xs badge-success">
                                                                    Cortesia
                                                                </span>
                                                            ) : (
                                                                '-'
                                                            )}
                                                        </td>
                                                    </tr>
                                                )
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="flex items-start gap-3 rounded-xl border border-warning/30 bg-warning/5 p-4">
                                    <MdInfoOutline className="mt-0.5 shrink-0 text-lg text-warning" />
                                    <p className="text-sm leading-relaxed text-base-content/70">
                                        Este pacote não possui componentes
                                        cadastrados. Edite o pacote no catálogo
                                        antes de agendar.
                                    </p>
                                </div>
                            )}
                        </FormSection>
                    )}

                    {serviceType === 'CONSULTATION' && (
                        <FormSection
                            id="secao-detalhes"
                            icon={<MdTune className="text-lg" />}
                            title="Detalhes da consulta"
                            subtitle="Informações clínicas do atendimento"
                        >
                            <ConsultationFields
                                title={consultTitle}
                                onTitleChange={setConsultTitle}
                                triageId={triageId}
                                onTriageIdChange={setTriageId}
                                clientId={clientId}
                                companyId={user?.companyId || 0}
                                diagnosis={diagnosis}
                                onDiagnosisChange={setDiagnosis}
                                treatmentPlan={treatmentPlan}
                                onTreatmentPlanChange={setTreatmentPlan}
                            />
                        </FormSection>
                    )}

                    {serviceType === 'PROCEDURE' && (
                        <FormSection
                            id="secao-detalhes"
                            icon={<MdTune className="text-lg" />}
                            title="Detalhes do procedimento"
                            subtitle="Orientações de preparo e pós-atendimento"
                        >
                            <OrderFields
                                preInstructions={preInstructions}
                                onPreInstructionsChange={setPreInstructions}
                                postInstructions={postInstructions}
                                onPostInstructionsChange={setPostInstructions}
                            />
                        </FormSection>
                    )}

                    {serviceType === 'PROCEDURE' && selectedCatalog && (
                        <>
                            <OrderExamsFields
                                exams={orderExams}
                                onExamsChange={setOrderExams}
                                companyId={user?.companyId || 0}
                                defaultHospitalId={hospitalInUseId}
                                showComponentSchedule={packageSaleMode}
                                rooms={rooms}
                                kind="EXAM"
                            />
                            <OrderExamsFields
                                exams={orderConsultations}
                                onExamsChange={setOrderConsultations}
                                companyId={user?.companyId || 0}
                                defaultHospitalId={hospitalInUseId}
                                showComponentSchedule={packageSaleMode}
                                rooms={rooms}
                                kind="CONSULTATION"
                            />
                        </>
                    )}

                    {/* Materiais médicos — só PROCEDIMENTO.
                        Exame não tem material: o que aparecia vinha herdado do item
                        de catálogo, sem passar por aqui e sem entrar no preço. A
                        taxa de anestesista mora nesta mesma seção e também é de
                        procedimento (0 dos 71 mil exames em produção usam; 10.611
                        dos 10.815 procedimentos usam). */}
                    {serviceType === 'PROCEDURE' && (
                        <MedicalSuppliesFields
                            supplies={medicalSupplies}
                            onSuppliesChange={setMedicalSupplies}
                            anesthetistFee={anesthetistFee}
                            onAnesthetistFeeChange={setAnesthetistFee}
                            anesthetistRecipientKind={anesthetistRecipientKind}
                            anesthetistRecipientId={anesthetistRecipientId}
                            onAnesthetistRecipientChange={(kind, entityId) => {
                                setAnesthetistRecipientKind(kind)
                                setAnesthetistRecipientId(entityId)
                            }}
                            organizationId={user?.organizationId || 0}
                            companyId={user?.companyId ?? undefined}
                            defaultHospitalId={hospitalInUseId}
                            isHealth={isHealth}
                        />
                    )}

                    {/* Produtos: só na edição de VENDA DE PACOTE. No agendamento
                        comum o produto não faz parte do atendimento — quem vende
                        produto é a loja. */}
                    {packageSaleMode && (
                        <OrderProductsFields
                            products={orderProducts}
                            onProductsChange={setOrderProducts}
                            companyId={user?.companyId || 0}
                        />
                    )}

                    <FormSection
                        id="secao-observacoes"
                        icon={<MdNotes className="text-lg" />}
                        title="Observações"
                        subtitle="Anotações internas sobre este atendimento"
                    >
                        <FormField
                            label="Anotações"
                            htmlFor="agendamento-observacoes"
                            optional
                        >
                            <textarea
                                id="agendamento-observacoes"
                                className="textarea textarea-bordered w-full leading-relaxed"
                                placeholder="Anotações sobre o agendamento..."
                                value={notes}
                                onChange={e => setNotes(e.target.value)}
                                rows={4}
                            />
                        </FormField>
                    </FormSection>

                    <SchedulingActions
                        isEditing={isEditing}
                        isBudgetMode={isBudgetMode}
                        isTeleconsultation={isTeleconsultation}
                        loading={loading}
                        addressConfirmed={addressConfirmed}
                        grouping={agrupando}
                        billingTargetId={billingTargetId}
                        groupedCount={groupedCount}
                        groupedAmount={groupedAmount}
                        summaryPrice={chargedSummaryPrice}
                        clientLabel={clientLabel}
                        onCancel={() => navigate(backRoute)}
                        onAddAnotherToggle={addAnother => {
                            addAnotherRef.current = addAnother
                        }}
                        onDismissGrouping={() => setAgrupamentoDispensado(true)}
                    />
                </form>
            </div>

            {/* Substituir ou acrescentar: ver a nota em `handleCatalogSelect`. */}
            {pendingCatalogChoice && (
                <ReplaceOrAppendDialog
                    currentItemName={orderItems[0]?.name}
                    incomingItemName={pendingCatalogChoice.name}
                    onCancel={() => setPendingCatalogChoice(null)}
                    onAppend={() => appendOrderItem(pendingCatalogChoice)}
                    onReplace={() =>
                        replaceSingleOrderItem(pendingCatalogChoice)
                    }
                />
            )}

            <PartnerComparisonModal
                isOpen={showPartnerComparison}
                onClose={() => setShowPartnerComparison(false)}
                items={comparisonItems}
                serviceTypeLabel={serviceTypeLabels[serviceType]}
                clientId={clientId ? parseInt(clientId, 10) : undefined}
                onApplyPartner={pid => {
                    if (!hidePartnerSelector) {
                        setPartnerId(pid)
                    }
                }}
            />

            {/* Cadastro rápido: item novo (ou item existente atrelado ao parceiro)
                já selecionado no agendamento, sem sair da tela. */}
            {showQuickCreate && user?.companyId && (
                <CatalogQuickCreate
                    open
                    onClose={() => setShowQuickCreate(false)}
                    initialName={catalogSearch}
                    category={serviceType as CatalogCategory}
                    companyId={user.companyId}
                    partnerId={catalogNeedsPartner ? partnerId : null}
                    requireTuss={serviceType === 'PROCEDURE'}
                    onReady={catalog => {
                        // Item novo não está em `catalogs` (a lista veio do
                        // backend antes dele existir); `handleCatalogSelect`
                        // trabalha com o objeto, então basta passá-lo.
                        mergeCatalogs([catalog])
                        void handleCatalogSelect(catalog)
                    }}
                />
            )}
        </div>
    )
}
